import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  TrendingUp,
  DollarSign,
  Clock,
  Settings,
  Save,
  Star,
  Activity,
  Users,
  ThumbsUp,
  Layers,
  RefreshCw,
  Menu,
  Download,
  Upload,
  X,
  Power,
  CheckCircle,
  LocateFixed,
  Sun,
} from "lucide-react";
import { showToast } from "../utils/toast";
import ExtrasManagement from "./ExtrasManagement";
import MediaModal from "./MediaModal";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";
import { apiFetch } from "../utils/apiClient";
import { getProductionDestinationLabel } from "../utils/productionDestination";
import {
  readLastProductTrackingKind,
  rememberProductTrackingKind,
} from "../utils/productTrackingChoice";
import {
  isValidCatalogPrice,
  normalizeCatalogPrice,
  normalizeOptionalCatalogPrice,
} from "../utils/catalogPrice";
import {
  buildProductCatalogExport,
  buildProductCatalogImportPreview,
  isProductCatalogExport,
} from "../utils/productCatalogTransfer";

const hasExplicitCatalogPrice = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const formatExtraPayload = (extra) => ({
  id: extra.id,
  type: extra.type,
  price: extra.price || extra.base_price || 0,
  max_quantity: extra.max_quantity || 1,
  is_default: extra.is_default || false,
});

const buildProductPayloadForUpdate = (product, overrides = {}) => {
  if (!product) return { ...overrides };

  const basePayload = {
    name: product.name,
    description: product.description || "",
    category_id: product.category_id,
    destination: product.destination || "cuisine",
    preparation_time: product.preparation_time || 0,
    is_available: product.is_available,
    track_stock: product.track_stock || false,
    unit_type: product.unit_type || "Unité",
    product_type: product.product_type || "simple",
    price: normalizeCatalogPrice(product.price) ?? 0,
    cost_price: parseFloat(product.cost_price) || 0,
    yango_price:
      product.yango_price === null || product.yango_price === undefined
        ? null
        : normalizeOptionalCatalogPrice(product.yango_price),
    glovo_price:
      product.glovo_price === null || product.glovo_price === undefined
        ? null
        : normalizeOptionalCatalogPrice(product.glovo_price),
    has_accompaniments: product.has_accompaniments || false,
    has_supplements: product.has_supplements || false,
    free_accompaniment_quota: product.free_accompaniment_quota ?? 1,
    selectedExtras: (product.extras || []).map(formatExtraPayload),
  };

  if (product.product_type === "variable") {
    const attributeMap = new Map();
    (product.variations || []).forEach((variation) => {
      Object.entries(variation.attributes || {}).forEach(([name, value]) => {
        if (!attributeMap.has(name)) {
          attributeMap.set(name, new Set());
        }
        attributeMap.get(name).add(value);
      });
    });
    basePayload.attributes =
      product.attributes ||
      Array.from(attributeMap.entries()).map(([name, values]) => ({
        name,
        values: Array.from(values),
      }));

    basePayload.variations = (product.variations || []).map((variation) => ({
      ...variation,
      price: normalizeCatalogPrice(variation.price) ?? 0,
      cost_price: parseFloat(variation.cost_price) || 0,
      yango_price:
        variation.yango_price === null || variation.yango_price === undefined
          ? null
          : normalizeOptionalCatalogPrice(variation.yango_price),
      glovo_price:
        variation.glovo_price === null || variation.glovo_price === undefined
          ? null
          : normalizeOptionalCatalogPrice(variation.glovo_price),
      stock_quantity:
        parseInt(
          variation.stock_quantity ?? variation.stock ?? variation.quantity ?? 0,
          10,
        ) || 0,
    }));
  }

  return { ...basePayload, ...overrides };
};

const PRODUCT_STEP_GUIDE = [
  {
    id: 1,
    title: "Profil produit",
    description: "Nom, description, destination",
    icon: Package,
  },
  {
    id: 2,
    title: "Tarifs & variations",
    description: "Prix de vente, attributs, déclinaisons",
    icon: DollarSign,
  },
  {
    id: 3,
    title: "Extras & suppléments",
    description: "Accompagnements, options additionnelles",
    icon: Layers,
  },
  {
    id: 4,
    title: "Récapitulatif",
    description: "Validation finale avant création",
    icon: CheckCircle,
  },
];

const getProductStepGuide = (isBoutique) => {
  const baseGuide = isBoutique
    ? PRODUCT_STEP_GUIDE.filter((step) => step.id !== 3)
    : PRODUCT_STEP_GUIDE;

  if (!isBoutique) return baseGuide;

  return baseGuide.map((step) => {
    if (step.id === 1) {
      return { ...step, description: "Nom, description, SKU" };
    }
    if (step.id === 2) {
      return { ...step, description: "Prix de vente, variations, SKU" };
    }
    return step;
  });
};

const useBodyScrollLock = (isLocked) => {
  useEffect(() => {
    if (
      !isLocked ||
      typeof document === "undefined" ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const originalPadding = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;

    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPadding;
    };
  }, [isLocked]);
};

const ProductManagement = () => {
  // État pour les onglets
  const [activeTab, setActiveTab] = useState("products"); // products, extras
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const productStepGuide = getProductStepGuide(isBoutique);
  const editTotalSteps = productStepGuide.length;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productionDestinations, setProductionDestinations] = useState([]);
  const safeCategories = Array.isArray(categories) ? categories : [];
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewMode, setViewMode] = useState(() =>
    getStoredBusinessType() === "boutique" ? "list" : "grid",
  ); // grid, list, analytics
  const [sortBy, setSortBy] = useState("name");
  const [filterBy, setFilterBy] = useState("all");
  const [productAnalytics, setProductAnalytics] = useState({});
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [aggregatorPricingSettings, setAggregatorPricingSettings] = useState({
    yango: false,
    glovo: false,
  });

  const [availableExtras, setAvailableExtras] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [catalogImportPreview, setCatalogImportPreview] = useState(null);
  const importInputRef = useRef(null);
  const approvedImportFileRef = useRef(null);
  const [imageUploadMode, setImageUploadMode] = useState("none"); // 'none', 'upload', 'url'
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mediaImages, setMediaImages] = useState([]);
  const [selectedMediaImage, setSelectedMediaImage] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditQuickCategoryModal, setShowEditQuickCategoryModal] =
    useState(false);
  const [editQuickCategoryName, setEditQuickCategoryName] = useState("");
  const [isCreatingEditQuickCategory, setIsCreatingEditQuickCategory] =
    useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingAttributes, setEditingAttributes] = useState([]);
  const [editingVariations, setEditingVariations] = useState([]);
  const [editingSelectedExtras, setEditingSelectedExtras] = useState([]);
  const [editingImagePreview, setEditingImagePreview] = useState(null);
  const [editingImageUploadMode, setEditingImageUploadMode] = useState("none");
  const [editingSelectedImageFile, setEditingSelectedImageFile] =
    useState(null);
  const [showEditingMediaModal, setShowEditingMediaModal] = useState(false);
  const [editingSelectedMediaImage, setEditingSelectedMediaImage] =
    useState(null);
  const [hasLoadedMediaImages, setHasLoadedMediaImages] = useState(false);
  const [editCurrentStep, setEditCurrentStep] = useState(1);
  const [editProductType, setEditProductType] = useState("simple");
  const [editingExtraScope, setEditingExtraScope] = useState("global");
  const [editingVariationExtras, setEditingVariationExtras] = useState({});
  const [editingUseGlobalPrice, setEditingUseGlobalPrice] = useState(true);
  const [editingUseGlobalStock, setEditingUseGlobalStock] = useState(true);
  const [editingUseVariationStock, setEditingUseVariationStock] =
    useState(false);
  const [showEditingAggregatorPrices, setShowEditingAggregatorPrices] =
    useState(false);
  const [showDailyMenuModal, setShowDailyMenuModal] = useState(false);
  const [dailyMenuProduct, setDailyMenuProduct] = useState(null);
  const [dailyMenuPrice, setDailyMenuPrice] = useState("");
  const [isSavingDailyMenu, setIsSavingDailyMenu] = useState(false);
  const [dailyMenuMode, setDailyMenuMode] = useState("global"); // global | per_variation
  const [dailyMenuVariationPrices, setDailyMenuVariationPrices] = useState([]);
  const editModalRef = useRef(null);
  const lastEditFocusRef = useRef(null);
  const hasLoadedProductsRef = useRef(false);
  const isEditModalVisible = showEditModal && Boolean(editingProduct);

  // Prompt minimal pour choisir la nature lors de l'activation du suivi (liste).
  const [trackingKindPrompt, setTrackingKindPrompt] = useState(null); // { product, nextTracking }
  const [trackingKindChoice, setTrackingKindChoice] = useState(readLastProductTrackingKind);
  const [trackingScopeChoice, setTrackingScopeChoice] = useState(null);

  useBodyScrollLock(isEditModalVisible);

  useEffect(() => {
    if (
      !isEditModalVisible ||
      typeof document === "undefined" ||
      !editModalRef.current
    ) {
      return;
    }

    const activeElement = document.activeElement;
    if (
      activeElement &&
      activeElement !== document.body &&
      editModalRef.current.contains(activeElement)
    ) {
      return;
    }

    const fallbackTarget =
      (lastEditFocusRef.current &&
        editModalRef.current.contains(lastEditFocusRef.current) &&
        lastEditFocusRef.current) ||
      editModalRef.current.querySelector(
        "input, textarea, select, button:not([disabled])",
      );

    fallbackTarget?.focus({ preventScroll: true });
  }, [
    isEditModalVisible,
    editCurrentStep,
    editingProduct,
    editingAttributes,
    editingVariations,
  ]);

  const broadcastProductCatalogChange = (event) => {
    if (typeof BroadcastChannel === "undefined" || !event) return;
    try {
      const channel = new BroadcastChannel("moo-products-sync");
      channel.postMessage({
        ...event,
        source: "admin-products",
        timestamp: Date.now(),
      });
      channel.close();
    } catch (error) {
      console.warn(
        "⚠️ Impossible d'envoyer la mise à jour catalogue:",
        error?.message || error,
      );
    }
  };

  const getEditingVariationKey = (variation, index) =>
    variation.uid || variation.id || `editing-variation-${index}`;

  const syncEditingVariationExtrasState = (nextVariations) => {
    setEditingVariationExtras((prev) => {
      const next = {};
      (nextVariations || []).forEach((variation, index) => {
        const key = getEditingVariationKey(variation, index);
        next[key] = prev[key] || [];
      });
      return next;
    });
  };

  const handleEditingExtraScopeChange = (scope) => {
    setEditingExtraScope(scope);
    if (scope === "global") {
      setEditingVariationExtras({});
    } else {
      setEditingSelectedExtras([]);
      syncEditingVariationExtrasState(editingVariations);
    }
  };

  const addEditingVariationExtra = (variationKey, extra, type) => {
    setEditingVariationExtras((prev) => {
      const current = prev[variationKey] || [];
      if (current.some((item) => item.id === extra.id)) {
      showToast("info", `"${extra.name}" est déjà associé à cette variation.`);
      return prev;
      }
      const payload = {
        ...extra,
        type:
          type ||
          (extra.category === "Accompagnements"
            ? "accompaniment"
            : "supplement"),
        price: extra.base_price || 0,
        max_quantity: 1,
      };
      return { ...prev, [variationKey]: [...current, payload] };
    });
  };

  const updateEditingVariationExtra = (variationKey, extraId, field, value) => {
    setEditingVariationExtras((prev) => {
      const list = prev[variationKey] || [];
      return {
        ...prev,
        [variationKey]: list.map((extra) =>
          extra.id === extraId ? { ...extra, [field]: value } : extra,
        ),
      };
    });
  };

  const removeEditingVariationExtra = (variationKey, extraId) => {
    setEditingVariationExtras((prev) => {
      const list = prev[variationKey] || [];
      return {
        ...prev,
        [variationKey]: list.filter((extra) => extra.id !== extraId),
      };
    });
  };

  const addEditingAttribute = () => {
    setEditingAttributes((prev) => [
      ...prev,
      { name: "", values: [], newValue: "" },
    ]);
  };

  const removeEditingAttribute = (index) => {
    setEditingAttributes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditingAttributeChange = (index, field, value) => {
    setEditingAttributes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addEditingAttributeValue = (index) => {
    setEditingAttributes((prev) => {
      const next = [...prev];
      const attr = next[index] || { values: [], newValue: "" };
      const newValue = (attr.newValue || "").trim();
      if (!newValue) {
        return prev;
      }
      const existingValues = Array.isArray(attr.values) ? attr.values : [];
      if (existingValues.includes(newValue)) {
        return prev;
      }
      next[index] = {
        ...attr,
        values: [...existingValues, newValue],
        newValue: "",
      };
      return next;
    });
  };

  const removeEditingAttributeValue = (attrIndex, valueIndex) => {
    setEditingAttributes((prev) => {
      const next = [...prev];
      const attr = next[attrIndex];
      if (!attr) return prev;
      const values = Array.isArray(attr.values) ? [...attr.values] : [];
      values.splice(valueIndex, 1);
      next[attrIndex] = { ...attr, values };
      return next;
    });
  };

  const generateEditingVariations = () => {
    if (
      editingAttributes.length === 0 ||
      editingAttributes.some(
        (attr) =>
          !attr.name?.trim() ||
          !Array.isArray(attr.values) ||
          attr.values.length === 0,
      )
    ) {
      showToast(
        "info",
        "Définissez au moins un attribut avec des valeurs pour générer des déclinaisons.",
      );
      return;
    }

    const combinations = editingAttributes.reduce((acc, attr) => {
      const validValues = attr.values || [];
      if (acc.length === 0) {
        return validValues.map((value) => ({ [attr.name]: value }));
      }
      return acc.flatMap((combo) =>
        validValues.map((value) => ({ ...combo, [attr.name]: value })),
      );
    }, []);

    if (combinations.length === 0) {
      showToast("info", "Aucune combinaison générée. Vérifiez vos attributs.");
      return;
    }

    const timestamp = Date.now();
    const newVariations = combinations.map((attributes, index) => ({
      uid: `edit-variation-${timestamp}-${index}`,
      attributes,
      price: "",
      cost_price: "",
      stock_quantity: 0,
      track_stock: false,
      is_active: true,
      extras: [],
    }));

    setEditingVariations(newVariations);
    syncEditingVariationExtrasState(newVariations);
    showToast("success", `${newVariations.length} variation(s) générée(s).`);
  };

  const handleEditProductTypeChange = (type) => {
    setEditProductType(type);
    setEditingProduct((prev) => (prev ? { ...prev, product_type: type } : prev));
    if (type === "variable" && editingAttributes.length === 0) {
      setEditingAttributes([{ name: "", values: [], newValue: "" }]);
    }
    if (type === "simple") {
      setEditingExtraScope("global");
    }
  };
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setBusinessType(extractBusinessType(data));
          try {
            localStorage.setItem("restaurant-info", JSON.stringify(data || {}));
          } catch (_e) {}
          return;
        }
      } catch (_e) {
        // ignore
      }
      try {
        const stored = localStorage.getItem("restaurant-info");
        if (stored) {
          setBusinessType(extractBusinessType(JSON.parse(stored)));
        }
      } catch (_e) {
        // ignore
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    if (isBoutique && activeTab === "extras") {
      setActiveTab("products");
    }
  }, [activeTab, isBoutique]);

  useEffect(() => {
    if (editCurrentStep > editTotalSteps) {
      setEditCurrentStep(editTotalSteps);
    }
  }, [editCurrentStep, editTotalSteps]);

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () =>
      setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Référence pour les formulaires modaux
  const selectMediaImage = (image) => {
    // Cette fonction est utilisée pour le modal d'édition
    handleEditingSelectFromMedia(image);
  };

  // Analytics produits basés sur les ventes réelles (avec quelques heuristiques)
  const generateProductAnalytics = (product, stats = {}) => {
    const ordersCount = product.total_items_sold || 0;
    const totalSales = product.total_revenue_amount || 0;
    const popularity = stats.maxOrders
      ? Math.round((ordersCount / stats.maxOrders) * 100)
      : 0;
    const avgOrders = stats.averageOrders || 0;
    const trendDelta = avgOrders
      ? ((ordersCount - avgOrders) / avgOrders) * 100
      : 0;

    return {
      totalSales,
      ordersCount,
      popularity: Math.min(100, Math.max(0, popularity)),
      monthlyTrend: trendDelta >= 0 ? "up" : "down",
      trendPercentage: Math.abs(trendDelta).toFixed(1),
      averageRating: (Math.random() * 0.4 + 4.3).toFixed(1),
      profitMargin: (Math.random() * 15 + 25).toFixed(1),
      stockLevel: Math.floor(Math.random() * 100) + 10,
      customerFavorite: popularity >= 70,
      quickSeller:
        ordersCount >=
        (stats.quickSellerThreshold || Math.max(5, Math.ceil(avgOrders))),
      seasonalItem: popularity >= 50 && Math.random() > 0.6,
    };
  };

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadProductionDestinations();
    loadAggregatorPricingSettings();
  }, []);

  const loadProductionDestinations = async () => {
    try {
      const response = await apiFetch("/api/production-destinations", {
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Destinations indisponibles");
      const data = await response.json();
      setProductionDestinations(
        (Array.isArray(data) ? data : []).filter((destination) =>
          Boolean(Number(destination?.is_active ?? 1)),
        ),
      );
    } catch (error) {
      console.warn("Destinations KDS:", error?.message || error);
      setProductionDestinations([
        { code: "cuisine", name: "Cuisine", icon: "🍳" },
        { code: "bar", name: "Bar", icon: "🍹" },
        { code: "none", name: "Aucune", icon: "❌" },
      ]);
    }
  };

  const loadAggregatorPricingSettings = async () => {
    try {
      const [yangoRes, glovoRes] = await Promise.all([
        fetch("/api/settings/aggregator-yango"),
        fetch("/api/settings/aggregator-glovo"),
      ]);
      const [yangoData, glovoData] = await Promise.all([
        yangoRes.ok ? yangoRes.json() : Promise.resolve({ enabled: false }),
        glovoRes.ok ? glovoRes.json() : Promise.resolve({ enabled: false }),
      ]);
      setAggregatorPricingSettings({
        yango: Boolean(yangoData?.enabled),
        glovo: Boolean(glovoData?.enabled),
      });
    } catch (_error) {
      setAggregatorPricingSettings({ yango: false, glovo: false });
    }
  };

  const loadProducts = async (options = {}) => {
    // Le plein écran de chargement ne sert qu'au premier accès. Une action
    // sur une carte ou un formulaire rafraîchit la liste sans démonter la page.
    const silent = hasLoadedProductsRef.current || Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await fetch("/api/products/admin");
      if (!response.ok) {
        throw new Error(`Catalogue indisponible (${response.status})`);
      }
      const data = await response.json();
      // L'API admin renvoie déjà les déclinaisons et extras groupés. Cela
      // remplace l'ancien N+1 (jusqu'à deux requêtes HTTP par produit).
      const productsWithDetails = Array.isArray(data) ? data : [];

      setProducts(productsWithDetails);
      hasLoadedProductsRef.current = true;
      // Ne pas attendre l'agrégation de tout l'historique des ventes pour
      // rendre le catalogue. Les cartes apparaissent d'abord, puis leurs
      // indicateurs se complètent silencieusement.
      void loadProductAnalytics(productsWithDetails);
    } catch (error) {
      console.error("❌ Erreur chargement produits:", error);
      showToast("error", hasLoadedProductsRef.current
        ? "La liste des produits n'a pas pu être actualisée. Réessayez."
        : "Impossible de charger les produits. Réessayez.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadProductAnalytics = async (catalogProducts = []) => {
    try {
      const response = await apiFetch("/api/products/admin/analytics", {
        cache: "no-store",
      });
      if (!response.ok) return;
      const rows = await response.json();
      const analyticsById = new Map(
        (Array.isArray(rows) ? rows : []).map((row) => [String(row.id), row]),
      );
      const enrichedProducts = catalogProducts.map((product) => ({
        ...product,
        ...(analyticsById.get(String(product.id)) || {}),
      }));
      const maxOrders = enrichedProducts.reduce(
        (max, product) =>
          Math.max(max, parseInt(product.total_items_sold || 0, 10)),
        0,
      );
      const totalOrders = enrichedProducts.reduce(
        (sum, product) =>
          sum + (parseInt(product.total_items_sold || 0, 10) || 0),
        0,
      );
      const averageOrders =
        enrichedProducts.length > 0 ? totalOrders / enrichedProducts.length : 0;
      const thresholds = {
        maxOrders,
        averageOrders,
        quickSellerThreshold: Math.max(5, Math.ceil(averageOrders * 1.1)),
      };
      const analytics = {};
      enrichedProducts.forEach((product) => {
        analytics[product.id] = generateProductAnalytics(product, thresholds);
      });
      setProductAnalytics(analytics);
    } catch (error) {
      console.warn("Statistiques produits différées:", error?.message || error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiFetch("/api/categories", { cache: "no-store" });
      const data = await response.json();
      const normalized = Array.isArray(data)
        ? data
        : Array.isArray(data?.categories)
          ? data.categories
          : [];
      setCategories(normalized);
      return normalized;
    } catch (error) {
      console.error("Erreur lors du chargement des catégories:", error);
      showToast("error", "Erreur lors du chargement des catégories");
    }
    return [];
  };

  const createCategoryQuick = async (name) => {
    const cleanName = String(name || "").trim();
    if (!cleanName) {
      throw new Error("Le nom de catégorie est requis");
    }

    const existing = safeCategories.find(
      (cat) =>
        String(cat?.name || "").trim().toLowerCase() === cleanName.toLowerCase(),
    );
    if (existing?.id) {
      return { id: existing.id, name: existing.name };
    }

    const response = await apiFetch("/api/categories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: cleanName,
        color: "#FFB800",
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Impossible de créer la catégorie");
    }

    const created = payload?.data || payload?.category || payload;
    const createdId = Number(created?.id || 0);
    const createdName = String(created?.name || cleanName).trim();
    if (!createdId) {
      const refreshed = await loadCategories();
      const fallback = (refreshed || []).find(
        (cat) =>
          String(cat?.name || "").trim().toLowerCase() === cleanName.toLowerCase(),
      );
      if (fallback?.id) {
        return { id: fallback.id, name: fallback.name };
      }
      throw new Error("Catégorie créée mais introuvable après rafraîchissement");
    }

    setCategories((prev) => {
      const base = Array.isArray(prev) ? prev : [];
      if (base.some((cat) => Number(cat?.id) === createdId)) return base;
      const merged = [...base, { id: createdId, name: createdName }];
      return merged.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "fr", {
          sensitivity: "base",
        }),
      );
    });

    broadcastProductCatalogChange({
      type: "CATEGORY_CREATED",
      categoryId: createdId,
    });

    return { id: createdId, name: createdName };
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
      return;
    }

    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(
          payload?.error || "Erreur lors de la suppression du produit",
        );
      }

      showToast("success", "Produit supprimé avec succès");
      broadcastProductCatalogChange({
        type: "PRODUCT_DELETED",
        productId,
      });
      void loadProducts({ silent: true });
    } catch (error) {
      console.error("Erreur lors de la suppression du produit:", error);
      showToast(
        "error",
        error.message || "Erreur lors de la suppression du produit",
      );
    }
  };

  const handleToggleProductAvailability = async (product) => {
    if (!product) return;
    const previousAvailability = Boolean(product.is_available);
    const nextAvailability = !previousAvailability;

    setProducts((prev) =>
      prev.map((entry) =>
        Number(entry.id) === Number(product.id)
          ? { ...entry, is_available: nextAvailability }
          : entry,
      ),
    );

    try {
      const payload = buildProductPayloadForUpdate(product, {
        is_available: nextAvailability,
      });
      const response = await fetch(`/api/products/${product.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            errorData.message ||
            "Impossible de mettre à jour la disponibilité",
        );
      }
      showToast(
        "success",
        nextAvailability
          ? "Produit activé avec succès"
          : "Produit désactivé avec succès",
      );
      broadcastProductCatalogChange({
        type: "PRODUCT_AVAILABILITY_CHANGED",
        productId: product.id,
        is_available: nextAvailability,
      });
    } catch (error) {
      setProducts((prev) =>
        prev.map((entry) =>
          Number(entry.id) === Number(product.id)
            ? { ...entry, is_available: previousAvailability }
            : entry,
        ),
      );
      console.error("Erreur lors du changement de disponibilité:", error);
      showToast(
        "error",
        error.message ||
          "Erreur lors du changement de disponibilité du produit",
      );
    }
  };

  const handleToggleProductTracking = async (product) => {
    if (!product) return;
    try {
      const nextTracking = !product.track_stock;

      if (nextTracking) {
        setTrackingKindChoice(readLastProductTrackingKind(product.inventory_kind));
        setTrackingScopeChoice(null);
        setTrackingKindPrompt({ product, nextTracking: true });
        return;
      }

      setProducts((prev) =>
        prev.map((entry) => {
          if (Number(entry.id) !== Number(product.id)) return entry;
          const updated = { ...entry, track_stock: nextTracking };
          if (entry.product_type === "variable" && Array.isArray(entry.variations)) {
            updated.variations = entry.variations.map((variation) => ({
              ...variation,
              track_stock: false,
            }));
          }
          return updated;
        }),
      );

      const response = await fetch(`/api/products/${product.id}/track-stock`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          track_stock: nextTracking,
          inventory_kind: product.inventory_kind || undefined,
          applyToVariations: false,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            errorData.message ||
            "Impossible de mettre à jour le suivi de stock",
        );
      }
      const result = await response.json().catch(() => ({}));
      showToast(
        "success",
        nextTracking
          ? "Suivi de stock activé pour ce produit."
          : "Suivi de stock désactivé pour ce produit.",
      );
      broadcastProductCatalogChange({
        type: "PRODUCT_STOCK_TRACKING_CHANGED",
        productId: product.id,
        track_stock: nextTracking,
      });
      if (result?.inventory_kind) {
        setProducts((prev) =>
          prev.map((entry) =>
            Number(entry.id) === Number(product.id)
              ? { ...entry, inventory_kind: result.inventory_kind }
              : entry,
          ),
        );
      }
    } catch (error) {
      setProducts((prev) =>
        prev.map((entry) => {
          if (Number(entry.id) !== Number(product.id)) return entry;
          const reverted = {
            ...entry,
            track_stock: Boolean(product.track_stock),
            inventory_kind: product.inventory_kind || null,
          };
          if (entry.product_type === "variable" && Array.isArray(entry.variations)) {
            reverted.variations = product.variations || entry.variations;
          }
          return reverted;
        }),
      );
      console.error("Erreur lors du changement de suivi stock:", error);
      showToast(
        "error",
        error.message || "Erreur lors du changement de suivi stock",
      );
    }
  };

  const confirmTrackingKindPrompt = async () => {
    const target = trackingKindPrompt?.product;
    if (!target) return;
    if (target.product_type === "variable" && !trackingScopeChoice) return;
    const chosenKind = trackingKindChoice;
    const trackVariations = target.product_type === "variable" && trackingScopeChoice === "variation";

    setProducts((prev) =>
      prev.map((entry) => {
        if (Number(entry.id) !== Number(target.id)) return entry;
        const updated = {
          ...entry,
          track_stock: true,
          inventory_kind: chosenKind,
        };
        if (entry.product_type === "variable" && Array.isArray(entry.variations)) {
          updated.variations = entry.variations.map((variation) => ({
            ...variation,
            track_stock: trackVariations,
          }));
        }
        return updated;
      }),
    );

    try {
      const response = await fetch(`/api/products/${target.id}/track-stock`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          track_stock: true,
          inventory_kind: chosenKind,
          ...(target.product_type === "variable" ? { stock_scope: trackingScopeChoice } : {}),
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            errorData.message ||
            "Impossible de mettre à jour le suivi de stock",
        );
      }
      const result = await response.json().catch(() => ({}));
      rememberProductTrackingKind(chosenKind);
      showToast("success", "Suivi de stock activé pour ce produit.");
      broadcastProductCatalogChange({
        type: "PRODUCT_STOCK_TRACKING_CHANGED",
        productId: target.id,
        track_stock: true,
      });
      if (result?.inventory_kind) {
        setProducts((prev) =>
          prev.map((entry) =>
            Number(entry.id) === Number(target.id)
              ? { ...entry, inventory_kind: result.inventory_kind }
              : entry,
          ),
        );
      }
      setTrackingKindPrompt(null);
    } catch (error) {
      setProducts((prev) =>
        prev.map((entry) => {
          if (Number(entry.id) !== Number(target.id)) return entry;
          const reverted = {
            ...entry,
            track_stock: Boolean(target.track_stock),
            inventory_kind: target.inventory_kind || null,
          };
          if (entry.product_type === "variable" && Array.isArray(entry.variations)) {
            reverted.variations = target.variations || entry.variations;
          }
          return reverted;
        }),
      );
      console.error("Erreur nature suivi stock:", error);
      showToast("error", error.message || "Erreur lors de l'activation du suivi");
    } finally {
      setTrackingKindPrompt(null);
    }
  };

  const formatCurrency = (amount) => {
    return `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0)} FCFA`;
  };
  const formatCurrencyRange = (min, max) => {
    const minVal = Number(min || 0);
    const maxVal = Number(max || 0);
    if (minVal === maxVal) return formatCurrency(minVal);
    return `${minVal.toLocaleString("fr-FR")} - ${maxVal.toLocaleString("fr-FR")} F CFA`;
  };
  const parseDecimalInput = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const normalized = String(value).replace(",", ".");
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const normalizeKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const parseBoolValue = (value, fallback = false) => {
    if (value === null || value === undefined || value === "") return fallback;
    const raw = String(value).trim().toLowerCase();
    if (["1", "true", "oui", "yes", "y"].includes(raw)) return true;
    if (["0", "false", "non", "no", "n"].includes(raw)) return false;
    return fallback;
  };

  const parseNumberValue = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const normalized = String(value)
      .replace(/[\s\u00A0\u202F]/g, "")
      .replace(",", ".")
      .replace(/[^0-9.\-]/g, "");
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const normalizeAttributeLabel = (value) => {
    if (!value) return "";
    return String(value)
      .replace(/^attr_/, "")
      .replace(/^attribute_/, "")
      .replace(/[_-]+/g, " ")
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const parseAttributesFromText = (value) => {
    if (!value) return {};
    if (typeof value === "object") return value;
    const raw = String(value).trim();
    if (!raw) return {};
    if (raw.startsWith("{")) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          return parsed;
        }
      } catch (error) {
        console.warn("Import produits: JSON attributes invalide", error);
      }
    }
    const attributes = {};
    raw
      .split(/[;|]/)
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .forEach((chunk) => {
        const [rawKey, ...rest] = chunk.split(/[:=]/);
        if (!rawKey || rest.length === 0) return;
        const key = rawKey.trim();
        const valuePart = rest.join(":").trim();
        if (!key || !valuePart) return;
        attributes[key] = valuePart;
      });
    return attributes;
  };

  const extractAttributesFromRow = (row) => {
    const attributes = {};
    Object.entries(row || {}).forEach(([key, value]) => {
      if (!key.startsWith("attr_") && !key.startsWith("attribute_")) return;
      if (value === null || value === undefined || value === "") return;
      const label = normalizeAttributeLabel(key);
      if (!label) return;
      attributes[label] = String(value).trim();
    });
    return attributes;
  };

  const normalizeDestination = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "cuisine";
    if (["bar", "boisson", "boissons"].includes(raw)) return "bar";
    if (["none", "aucun", "aucune"].includes(raw)) return "none";
    return "cuisine";
  };

  const getRowValue = (row, keys) => {
    for (const key of keys) {
      const normalized = normalizeKey(key);
      if (normalized in row) return row[normalized];
    }
    return "";
  };

  const splitImportList = (value) =>
    String(value || "")
      .split(/[;|\n]/)
      .map((item) => item.trim())
      .filter(Boolean);

  const parseImportExtras = (value, { type, category, defaultNames = new Set(), maxQuantity = 1 } = {}) =>
    splitImportList(value).map((chunk) => {
      const [rawName, ...rest] = chunk.split(/[:=]/);
      const name = String(rawName || "").trim();
      const parsedPrice = rest.length > 0 ? parseNumberValue(rest.join("="), 0) : 0;
      return {
        name,
        category: category || "Général",
        base_price: type === "supplement" ? parsedPrice : 0,
        type,
        price: type === "supplement" ? parsedPrice : 0,
        max_quantity: maxQuantity || 1,
        is_default: defaultNames.has(normalizeKey(name)),
      };
    }).filter((extra) => extra.name);

  const getVariationIdentity = (variation = {}) => {
    const sku = String(variation.sku || "").trim();
    if (sku) return `sku:${normalizeKey(sku)}`;
    const name = String(variation.name || "").trim();
    if (name) return `name:${normalizeKey(name)}`;
    const attrs = variation.attributes && typeof variation.attributes === "object"
      ? variation.attributes
      : {};
    const attrKey = Object.entries(attrs)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${normalizeKey(key)}=${normalizeKey(value)}`)
      .join("|");
    return attrKey ? `attrs:${attrKey}` : "";
  };

  const mergeVariationsForImport = (existingVariations = [], importedVariations = []) => {
    const merged = [];
    const byKey = new Map();

    const addVariation = (variation, imported = false) => {
      if (!variation) return;
      const key = getVariationIdentity(variation);
      const clean = {
        ...variation,
        price: parseNumberValue(variation.price, 0),
        cost_price: parseNumberValue(variation.cost_price, 0),
        stock_quantity: parseNumberValue(variation.stock_quantity, 0),
        track_stock: Boolean(variation.track_stock),
        is_active: variation.is_active !== false,
      };
      delete clean._rowNumber;

      if (key && byKey.has(key)) {
        const index = byKey.get(key);
        merged[index] = imported ? { ...merged[index], ...clean } : merged[index];
        return;
      }

      if (key) byKey.set(key, merged.length);
      merged.push(clean);
    };

    existingVariations.forEach((variation) => addVariation(variation, false));
    importedVariations.forEach((variation) => addVariation(variation, true));
    return merged;
  };

  const findExistingProductForImport = (productMap, { sku, categoryId, name }) => {
    const normalizedSku = normalizeKey(sku);
    if (normalizedSku && productMap.bySku.has(normalizedSku)) {
      return productMap.bySku.get(normalizedSku);
    }
    return productMap.byCategoryName.get(`${categoryId}-${normalizeKey(name)}`) || null;
  };

  const buildVariationFromRow = (row, rowNumber) => {
    const variationNameRaw = String(
      getRowValue(row, ["variation_name", "declinaison", "variation"]),
    ).trim();
    const variationPriceRaw = getRowValue(row, [
      "variation_price",
      "prix_variation",
      "price_variation",
    ]);
    const variationCostRaw = getRowValue(row, [
      "variation_cost_price",
      "cout_variation",
      "cost_price_variation",
    ]);
    const variationYangoRaw = getRowValue(row, [
      "variation_yango_price",
      "yango_price_variation",
      "prix_yango_variation",
      "prix_variation_yango",
      "yango_variation",
    ]);
    const variationGlovoRaw = getRowValue(row, [
      "variation_glovo_price",
      "glovo_price_variation",
      "prix_glovo_variation",
      "prix_variation_glovo",
      "glovo_variation",
    ]);
    const variationSku = String(
      getRowValue(row, ["variation_sku", "sku_variation"]),
    ).trim();
    const variationStockRaw = getRowValue(row, [
      "variation_stock_quantity",
      "variation_stock",
      "stock_quantity",
      "stock_variation",
    ]);
    const variationTrackRaw = getRowValue(row, [
      "variation_track_stock",
      "track_stock_variation",
    ]);
    const explicitAttributes = parseAttributesFromText(
      getRowValue(row, ["variation_attributes", "attributes", "attributs"]),
    );
    const columnAttributes = extractAttributesFromRow(row);
    const attributes = { ...columnAttributes, ...explicitAttributes };
    const hasAttributes = Object.keys(attributes).length > 0;
    const variationPrice = parseNumberValue(variationPriceRaw, null);
    const variationCost = parseNumberValue(variationCostRaw, null);
    const variationYangoPrice = parseNumberValue(variationYangoRaw, null);
    const variationGlovoPrice = parseNumberValue(variationGlovoRaw, null);
    const variationStock = parseNumberValue(variationStockRaw, null);
    const variationTrack = parseBoolValue(variationTrackRaw, false);
    const hasVariationData =
      hasAttributes ||
      Boolean(variationNameRaw) ||
      variationPrice !== null ||
      variationCost !== null ||
      variationYangoPrice !== null ||
      variationGlovoPrice !== null ||
      variationStock !== null ||
      String(variationTrackRaw || "").trim() !== "";

    if (!hasVariationData) return null;

    const derivedName =
      variationNameRaw ||
      (hasAttributes ? Object.values(attributes).join(" · ") : "");

    return {
      name: derivedName || undefined,
      attributes: hasAttributes ? attributes : {},
      price: variationPrice,
      cost_price: variationCost,
      yango_price: variationYangoPrice,
      glovo_price: variationGlovoPrice,
      sku: variationSku || undefined,
      track_stock: variationTrack,
      stock_quantity: variationStock,
      is_active: true,
      _invalidSellingPrice:
        (hasExplicitCatalogPrice(variationPriceRaw) && variationPrice === null) ||
        (hasExplicitCatalogPrice(variationYangoRaw) && variationYangoPrice === null) ||
        (hasExplicitCatalogPrice(variationGlovoRaw) && variationGlovoPrice === null),
      _rowNumber: rowNumber,
    };
  };

  const handleImportClick = () => {
    if (importInputRef.current) {
      importInputRef.current.value = "";
      importInputRef.current.click();
    }
  };

  const handleExportProducts = async () => {
    setIsExporting(true);
    try {
      const [productsResponse, extrasResponse] = await Promise.all([
        apiFetch("/api/products/admin", { includeBearer: true, cache: "no-store" }),
        apiFetch("/api/extras", { includeBearer: true, cache: "no-store" }),
      ]);
      if (!productsResponse.ok || !extrasResponse.ok) {
        throw new Error("Export impossible.");
      }
      const [catalogProducts, catalogExtras] = await Promise.all([
        productsResponse.json(),
        extrasResponse.json(),
      ]);
      const catalog = buildProductCatalogExport({
        products: Array.isArray(catalogProducts) ? catalogProducts : [],
        categories: safeCategories,
        extras: Array.isArray(catalogExtras) ? catalogExtras : [],
      });
      const blob = new Blob([JSON.stringify(catalog, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `moo-sync-produits-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showToast("success", `${catalog.products.length} produit(s) exporté(s).`);
    } catch (error) {
      console.error("Erreur export produits:", error);
      showToast("error", error.message || "Export impossible.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isApproved = approvedImportFileRef.current === file;
    if (isApproved) approvedImportFileRef.current = null;

    setIsImporting(true);

    try {
      let availableCategories = safeCategories;
      if (!availableCategories.length) {
        availableCategories = await loadCategories();
      }

      let structuredCatalog = null;
      let rawRows = [];
      if (file.name.toLowerCase().endsWith(".json")) {
        structuredCatalog = JSON.parse(await file.text());
        if (!isProductCatalogExport(structuredCatalog)) {
          throw new Error("Fichier d'export MOO Sync invalide.");
        }
      } else {
        const xlsx = await import("xlsx");
        const buffer = await file.arrayBuffer();
        const workbook = xlsx.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        rawRows = xlsx.utils.sheet_to_json(sheet, { defval: "" });
      }

      if (!structuredCatalog && !rawRows.length) {
        showToast("error", "Fichier vide ou illisible.");
        setIsImporting(false);
        return;
      }

      const categoryMap = new Map(
        (availableCategories || []).map((cat) => [normalizeKey(cat.name), cat.id]),
      );

      const extrasResponse = await apiFetch("/api/extras?include_inactive=true", {
        includeBearer: true,
        cache: "no-store",
      });
      const availableExtrasForImport = extrasResponse.ok
        ? await extrasResponse.json()
        : availableExtras;
      const extraMap = new Map(
        (availableExtrasForImport || []).map((extra) => [
          `${normalizeKey(extra.category || "Général")}-${normalizeKey(extra.name)}`,
          extra,
        ]),
      );

      // Ne jamais rapprocher un import avec l'état mémoire : une remise à zéro
      // effectuée dans Paramètres peut avoir archivé ces anciens identifiants.
      const existingResponse = await apiFetch("/api/products/admin?include_inactive=true", {
        includeBearer: true,
        cache: "no-store",
      });
      if (!existingResponse.ok) {
        throw new Error("Impossible de vérifier le catalogue avant l’import. Réessayez.");
      }
      const existingData = await existingResponse.json();
      const rows = rawRows.map((row) => {
        const normalizedRow = {};
        Object.entries(row || {}).forEach(([key, value]) => {
          normalizedRow[normalizeKey(key)] = value;
        });
        return normalizedRow;
      });

      if (!isApproved) {
        const categoryNamesById = new Map(
          (availableCategories || []).map((category) => [Number(category.id), category.name]),
        );
        const incomingProducts = structuredCatalog
          ? structuredCatalog.products
          : rows.map((row) => {
              const categoryId = Number(getRowValue(row, ["category_id", "categorie_id", "categoryid"]));
              return {
                name: getRowValue(row, ["name", "nom", "produit", "product_name"]),
                sku: getRowValue(row, ["sku", "code", "reference", "référence"]),
                category: getRowValue(row, [
                  "category", "categorie", "catégorie", "category_name", "categorie_name", "catégorie_name",
                ]) || categoryNamesById.get(categoryId),
              };
            });
        setCatalogImportPreview({
          file,
          summary: buildProductCatalogImportPreview({
            incomingProducts,
            existingProducts: Array.isArray(existingData) ? existingData : [],
            categories: availableCategories,
          }),
        });
        return;
      }

      showToast("info", `Import en cours: ${file.name}`);
      const productsForImport = await Promise.all(
        (Array.isArray(existingData) ? existingData : []).map(async (product) => {
          const next = { ...product };
          if (next.product_type === "variable") {
            const variationRes = await apiFetch(`/api/products/${next.id}/variations`, {
              includeBearer: true,
            });
            if (variationRes.ok) next.variations = await variationRes.json();
          }
          const extrasRes = await apiFetch(`/api/products/${next.id}/extras`, {
            includeBearer: true,
          });
          if (extrasRes.ok) next.extras = await extrasRes.json();
          return next;
        }),
      );
      const productMap = {
        bySku: new Map(),
        byCategoryName: new Map(),
      };
      productsForImport.forEach((product) => {
        const sku = normalizeKey(product?.sku);
        if (sku) productMap.bySku.set(sku, product);
        if (product?.category_id && product?.name) {
          productMap.byCategoryName.set(
            `${product.category_id}-${normalizeKey(product.name)}`,
            product,
          );
        }
      });

      const errors = [];
      const productBuckets = new Map();
      let successCount = 0;
      let extrasCreatedOrUpdated = 0;
      let extrasLinked = 0;

      const createCategory = async (categoryInput, rowNumber) => {
        const categoryData = typeof categoryInput === "object" && categoryInput
          ? categoryInput
          : { name: categoryInput };
        const cleanName = String(categoryData.name || "").trim();
        if (!cleanName) return null;
        const normalized = normalizeKey(cleanName);
        if (categoryMap.has(normalized)) {
          const existingId = categoryMap.get(normalized);
          if (typeof categoryInput === "object" && categoryInput) {
            try {
              await apiFetch(`/api/categories/${existingId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  ...categoryData,
                  name: cleanName,
                  is_active: true,
                }),
              });
            } catch (_error) {
              // Le produit reste importable même si la présentation de sa catégorie ne change pas.
            }
          }
          return existingId;
        }
        try {
          const response = await apiFetch("/api/categories", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: cleanName,
              description: categoryData.description || "",
              color: categoryData.color || null,
              icon: categoryData.icon || null,
              sort_order: categoryData.sort_order ?? null,
              is_active: true,
            }),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(data.error || "Erreur creation categorie");
          }
          const newId = data?.data?.id;
          if (!newId) {
            throw new Error("ID categorie manquant");
          }
          categoryMap.set(normalized, newId);
          setCategories((prev) => [...(prev || []), { id: newId, name: cleanName }]);
          return newId;
        } catch (error) {
          errors.push(
            `Ligne ${rowNumber}: creation categorie "${cleanName}" echouee`,
          );
          return null;
        }
      };

      const upsertImportExtra = async (extra, rowNumber) => {
        const key = `${normalizeKey(extra.category || "Général")}-${normalizeKey(extra.name)}`;
        const existing = extraMap.get(key);
        const payload = {
          ...(existing?.id ? { id: existing.id } : {}),
          name: extra.name,
          category: extra.category || existing?.category || "Général",
          base_price:
            extra.type === "supplement"
              ? parseNumberValue(extra.base_price ?? existing?.base_price, 0)
              : 0,
          description: extra.description || existing?.description || "",
          sort_order: extra.sort_order ?? existing?.sort_order ?? 0,
          track_stock: Boolean(extra.track_stock ?? existing?.track_stock),
          inventory_kind: extra.inventory_kind ?? existing?.inventory_kind ?? null,
          is_active: true,
        };
        try {
          const response = await apiFetch("/api/extras", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            includeBearer: true,
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data?.id) {
            throw new Error(data.error || "Erreur creation extra");
          }
          const saved = {
            ...(existing || {}),
            ...payload,
            id: data.id,
            type: extra.type,
            price: extra.price,
            max_quantity: extra.max_quantity,
            is_default: extra.is_default,
          };
          extraMap.set(key, saved);
          extrasCreatedOrUpdated += 1;
          return saved;
        } catch (error) {
          errors.push(
            `Ligne ${rowNumber}: extra "${extra.name}" non importé (${error.message || "erreur"})`,
          );
          return null;
        }
      };

      if (structuredCatalog) {
        for (const category of structuredCatalog.categories || []) {
          // eslint-disable-next-line no-await-in-loop
          await createCategory(category, 0);
        }

        for (const extra of structuredCatalog.extras || []) {
          // eslint-disable-next-line no-await-in-loop
          await upsertImportExtra({ ...extra, type: "supplement" }, 0);
        }

        const resolveRules = async (rules = []) => {
          const resolved = [];
          for (const rule of rules) {
            const definition = {
              ...(rule.extra || {}),
              type: rule.type || "supplement",
              price: rule.price,
              base_price: rule.price,
              max_quantity: rule.max_quantity,
              is_default: rule.is_default,
            };
            const key = `${normalizeKey(definition.category || "Général")}-${normalizeKey(definition.name)}`;
            let saved = extraMap.get(key);
            if (!saved?.id) {
              // eslint-disable-next-line no-await-in-loop
              saved = await upsertImportExtra(definition, 0);
            }
            if (!saved?.id) continue;
            resolved.push({
              id: saved.id,
              type: definition.type,
              price: parseNumberValue(definition.price, 0),
              max_quantity: Math.max(1, Number(definition.max_quantity) || 1),
              is_default: Boolean(definition.is_default),
            });
          }
          return resolved;
        };

        for (const importedProduct of structuredCatalog.products) {
          // eslint-disable-next-line no-await-in-loop
          const categoryId = await createCategory(importedProduct.category, 0);
          if (!categoryId) {
            errors.push(`Produit "${importedProduct.name}": catégorie introuvable`);
            continue;
          }
          const existingProduct = findExistingProductForImport(productMap, {
            sku: importedProduct.sku,
            categoryId,
            name: importedProduct.name,
          });
          // eslint-disable-next-line no-await-in-loop
          const selectedExtras = await resolveRules(importedProduct.extras);
          const variations = [];
          for (const variation of importedProduct.variations || []) {
            // eslint-disable-next-line no-await-in-loop
            const variationExtras = await resolveRules(variation.extras);
            variations.push({
              ...variation,
              extras: variationExtras,
              is_active: true,
            });
          }
          const payload = {
            ...importedProduct,
            ...(existingProduct?.id ? { id: existingProduct.id } : {}),
            category_id: categoryId,
            is_active: true,
            selectedExtras,
            mergeSelectedExtras: false,
            variations,
            product_type: variations.length ? "variable" : importedProduct.product_type || "simple",
          };
          delete payload.category;
          delete payload.extras;
          try {
            // eslint-disable-next-line no-await-in-loop
            const response = await apiFetch("/api/products", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              includeBearer: true,
              body: JSON.stringify(payload),
            });
            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              throw new Error(data.error || "Erreur import produit");
            }
            const savedProduct = await response.json().catch(() => ({}));
            if (importedProduct.daily_menu_active && savedProduct?.id) {
              const usesVariationPrices =
                importedProduct.daily_menu_scope === "per_variation" &&
                Array.isArray(importedProduct.variations);
              let dailyMenuPayload;
              if (usesVariationPrices) {
                const savedByIdentity = new Map(
                  (savedProduct.variations || []).map((variation) => [
                    getVariationIdentity(variation),
                    variation,
                  ]),
                );
                dailyMenuPayload = {
                  mode: "per_variation",
                  variations: importedProduct.variations
                    .filter((variation) => variation.daily_menu_active && variation.daily_menu_price !== null)
                    .map((variation) => ({
                      id: savedByIdentity.get(getVariationIdentity(variation))?.id,
                      price: variation.daily_menu_price,
                    }))
                    .filter((variation) => variation.id),
                };
              } else {
                dailyMenuPayload = {
                  mode: "global",
                  price: importedProduct.daily_menu_price,
                };
              }
              if (
                (dailyMenuPayload.mode === "global" && dailyMenuPayload.price !== null) ||
                (dailyMenuPayload.mode === "per_variation" && dailyMenuPayload.variations.length)
              ) {
                // eslint-disable-next-line no-await-in-loop
                const dailyMenuResponse = await apiFetch(`/api/products/${savedProduct.id}/daily-menu`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  includeBearer: true,
                  body: JSON.stringify(dailyMenuPayload),
                });
                if (!dailyMenuResponse.ok) {
                  throw new Error("Menu du jour non importé");
                }
              }
            }
            successCount += 1;
            extrasLinked += selectedExtras.length;
          } catch (error) {
            errors.push(`Produit "${importedProduct.name}": ${error.message || "erreur"}`);
          }
        }

        if (successCount > 0) {
          showToast("success", `${successCount} produit(s) importé(s).`);
          await loadProducts({ silent: true });
          await loadAvailableExtras();
          broadcastProductCatalogChange({
            type: "PRODUCTS_REFRESH_REQUESTED",
            source: "products-import",
            count: successCount,
          });
        }
        if (errors.length) {
          console.warn("Import produits - erreurs:", errors);
          showToast("error", `${errors.length} erreur(s).`);
        }
        return;
      }

      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const rowNumber = i + 2;

        const name = String(
          getRowValue(row, ["name", "nom", "produit", "product_name"]),
        ).trim();
        if (!name) {
          errors.push(`Ligne ${rowNumber}: nom manquant`);
          continue;
        }

        const categoryIdRaw = getRowValue(row, [
          "category_id",
          "categorie_id",
          "categoryid",
        ]);
        const categoryNameRaw = getRowValue(row, [
          "category",
          "categorie",
          "catégorie",
          "category_name",
          "categorie_name",
          "catégorie_name",
        ]);

        const categoryIdFromName = categoryMap.get(
          normalizeKey(categoryNameRaw),
        );
        let categoryId = parseInt(categoryIdRaw, 10) || categoryIdFromName || null;
        if (!categoryId && categoryNameRaw) {
          categoryId = await createCategory(categoryNameRaw, rowNumber);
        }

        if (!categoryId) {
          errors.push(`Ligne ${rowNumber}: categorie introuvable`);
          continue;
        }

        const basePriceRaw = getRowValue(row, ["price", "prix", "prix_vente"]);
        const yangoPriceRaw = getRowValue(row, [
          "yango_price",
          "prix_yango",
          "prix yango",
          "yango",
          "tarif_yango",
          "tarif yango",
        ]);
        const glovoPriceRaw = getRowValue(row, [
          "glovo_price",
          "prix_glovo",
          "prix glovo",
          "glovo",
          "tarif_glovo",
          "tarif glovo",
        ]);
        const parsedBasePrice = normalizeCatalogPrice(basePriceRaw);
        const parsedYangoPrice = normalizeOptionalCatalogPrice(yangoPriceRaw);
        const parsedGlovoPrice = normalizeOptionalCatalogPrice(glovoPriceRaw);
        if (
          (hasExplicitCatalogPrice(basePriceRaw) && parsedBasePrice === null) ||
          (hasExplicitCatalogPrice(yangoPriceRaw) && parsedYangoPrice === null) ||
          (hasExplicitCatalogPrice(glovoPriceRaw) && parsedGlovoPrice === null)
        ) {
          errors.push(`Ligne ${rowNumber}: prix de vente invalide`);
          continue;
        }

        const basePayload = {
          name,
          sku:
            String(
              getRowValue(row, ["sku", "code", "reference", "référence"]),
            ).trim() || null,
          description: String(
            getRowValue(row, ["description", "desc"]),
          ).trim(),
          image_url:
            String(
              getRowValue(row, [
                "image_url",
                "image",
                "images",
                "imagepath",
                "image_path",
                "photo",
                "photo_url",
              ]),
            ).trim() || null,
          category_id: categoryId,
          price: parsedBasePrice,
          yango_price: parsedYangoPrice,
          glovo_price: parsedGlovoPrice,
          cost_price: parseNumberValue(
            getRowValue(row, ["cost_price", "cout", "prix_achat"]),
            0,
          ),
          destination: normalizeDestination(
            getRowValue(row, [
              "destination",
              "print_destination",
              "impression",
            ]),
          ),
          preparation_time: parseNumberValue(
            getRowValue(row, [
              "preparation_time",
              "temps_preparation",
              "prep_time",
            ]),
            0,
          ),
          unit_type:
            String(getRowValue(row, ["unit_type", "unite", "unité", "unit"])).trim() ||
            "Unité",
          is_available: parseBoolValue(
            getRowValue(row, ["is_available", "disponible", "available"]),
            true,
          ),
          track_stock: parseBoolValue(
            getRowValue(row, ["track_stock", "suivi_stock", "stock"]),
            false,
          ),
        };
        const explicitProductType = String(
          getRowValue(row, ["type_produit", "product_type", "type"]),
        ).trim().toLowerCase();

        const extraCategory =
          String(
            getRowValue(row, ["extra_categorie", "extra_category", "categorie_extra", "catégorie_extra"]),
          ).trim() || "Général";
        const extrasMax = parseNumberValue(
          getRowValue(row, ["extras_max", "extra_max", "max_extras"]),
          1,
        );
        const defaultExtraNames = new Set(
          splitImportList(getRowValue(row, ["extras_defaut", "default_extras", "extras_default"]))
            .map((item) => normalizeKey(item)),
        );
        const rowExtras = [
          ...parseImportExtras(getRowValue(row, ["accompagnements", "accompaniments"]), {
            type: "accompaniment",
            category: extraCategory,
            defaultNames: defaultExtraNames,
            maxQuantity: extrasMax,
          }),
          ...parseImportExtras(getRowValue(row, ["supplements", "suppléments", "extras"]), {
            type: "supplement",
            category: extraCategory,
            defaultNames: defaultExtraNames,
            maxQuantity: extrasMax,
          }),
        ];

        const variation = buildVariationFromRow(row, rowNumber);
        if (variation?._invalidSellingPrice) {
          errors.push(`Ligne ${rowNumber}: prix de variation invalide`);
          continue;
        }

        const bucketKey = `${categoryId}-${normalizeKey(name)}`;
        let bucket = productBuckets.get(bucketKey);
        if (!bucket) {
          bucket = {
            base: basePayload,
            variations: [],
            simpleRows: 0,
            extras: new Map(),
            explicitProductType,
          };
          productBuckets.set(bucketKey, bucket);
        }
        if (explicitProductType) bucket.explicitProductType = explicitProductType;
        rowExtras.forEach((extra) => {
          bucket.extras.set(
            `${extra.type}-${normalizeKey(extra.category)}-${normalizeKey(extra.name)}`,
            { ...extra, rowNumber },
          );
        });

        if (variation) {
          delete variation._invalidSellingPrice;
          bucket.variations.push(variation);
        } else {
          bucket.simpleRows += 1;
        }
      }

      for (const bucket of productBuckets.values()) {
        const basePayload = bucket.base;
        const hasVariations = bucket.variations.length > 0;
        const existingProduct = findExistingProductForImport(productMap, {
          sku: basePayload.sku,
          categoryId: basePayload.category_id,
          name: basePayload.name,
        });
        const selectedExtras = [];
        for (const extra of bucket.extras.values()) {
          // eslint-disable-next-line no-await-in-loop
          const savedExtra = await upsertImportExtra(extra, extra.rowNumber || 0);
          if (!savedExtra?.id) continue;
          selectedExtras.push({
            id: savedExtra.id,
            type: extra.type,
            price: extra.type === "supplement" ? parseNumberValue(extra.price, 0) : 0,
            base_price: extra.type === "supplement" ? parseNumberValue(extra.base_price, 0) : 0,
            max_quantity: extra.max_quantity || 1,
            is_default: Boolean(extra.is_default),
          });
        }
        const hasAccompaniments =
          selectedExtras.some((extra) => extra.type === "accompaniment") ||
          Boolean(existingProduct?.has_accompaniments);
        const hasSupplements =
          selectedExtras.some((extra) => extra.type === "supplement") ||
          Boolean(existingProduct?.has_supplements);

        if (hasVariations && bucket.simpleRows > 0) {
          errors.push(
            `Produit "${basePayload.name}": melange lignes simples et variations`,
          );
        }

        let payload = {
          ...basePayload,
          price:
            basePayload.price ??
            normalizeCatalogPrice(existingProduct?.price),
          yango_price:
            basePayload.yango_price ?? existingProduct?.yango_price ?? null,
          glovo_price:
            basePayload.glovo_price ?? existingProduct?.glovo_price ?? null,
          ...(existingProduct?.id ? { id: existingProduct.id } : {}),
          has_accompaniments: hasAccompaniments,
          has_supplements: hasSupplements,
          selectedExtras,
          mergeSelectedExtras: true,
          product_type:
            existingProduct?.product_type === "variable" ||
            bucket.explicitProductType === "variable"
              ? "variable"
              : "simple",
        };

        if (!hasVariations && payload.product_type === "simple" && !isValidCatalogPrice(payload.price)) {
          errors.push(`Produit "${basePayload.name}": prix de vente manquant ou invalide`);
          continue;
        }

        if (hasVariations || existingProduct?.product_type === "variable") {
          const basePrice =
            basePayload.price ?? normalizeCatalogPrice(existingProduct?.price);
          const baseCost = parseNumberValue(basePayload.cost_price, 0);
          let hasInvalidVariationPrice = false;
          const normalizedVariations = bucket.variations.map((variation) => {
            const variationPrice =
              variation.price !== null && variation.price !== undefined
                ? variation.price
                : basePrice;
            const variationCost =
              variation.cost_price !== null && variation.cost_price !== undefined
                ? variation.cost_price
                : baseCost;
            if (!isValidCatalogPrice(variationPrice)) {
              hasInvalidVariationPrice = true;
              errors.push(
                `Ligne ${variation._rowNumber}: prix variation manquant ou invalide`,
              );
            }
            return {
              name: variation.name,
              attributes: variation.attributes,
              price: variationPrice,
              cost_price: variationCost || 0,
              yango_price:
                variation.yango_price !== null && variation.yango_price !== undefined
                  ? variation.yango_price
                  : null,
              glovo_price:
                variation.glovo_price !== null && variation.glovo_price !== undefined
                  ? variation.glovo_price
                  : null,
              sku: variation.sku || null,
              track_stock: variation.track_stock,
              stock_quantity:
                variation.stock_quantity !== null &&
                variation.stock_quantity !== undefined
                  ? variation.stock_quantity
                  : 0,
              is_active: variation.is_active !== false,
            };
          });
          if (hasInvalidVariationPrice) continue;
          const mergedVariations = mergeVariationsForImport(
            Array.isArray(existingProduct?.variations)
              ? existingProduct.variations
              : [],
            normalizedVariations,
          );
          const productPrice =
            basePrice ?? normalizeCatalogPrice(mergedVariations[0]?.price);
          const productCost =
            baseCost || mergedVariations[0]?.cost_price || 0;
          const trackStock =
            basePayload.track_stock ||
            mergedVariations.some((variation) => variation.track_stock);

          payload = {
            ...payload,
            price: productPrice,
            cost_price: productCost,
            track_stock: trackStock,
            product_type: "variable",
            variations: mergedVariations,
          };
          if (!isValidCatalogPrice(payload.price)) {
            errors.push(`Produit "${basePayload.name}": prix de vente manquant ou invalide`);
            continue;
          }
        }

        try {
          const res = await apiFetch("/api/products", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            includeBearer: true,
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            errors.push(
              `Produit "${basePayload.name}": ${
                err.error || "Erreur creation produit"
              }`,
            );
            continue;
          }
          successCount += 1;
          if (selectedExtras.length > 0) extrasLinked += selectedExtras.length;
        } catch (err) {
          errors.push(
            `Produit "${basePayload.name}": ${err.message || "Erreur reseau"}`,
          );
        }
      }

      if (successCount > 0) {
        showToast(
          "success",
          `${successCount} produit(s) importé(s), ${extrasCreatedOrUpdated} extra(s), ${extrasLinked} liaison(s).`,
        );
        await loadProducts({ silent: true });
        await loadAvailableExtras();
        broadcastProductCatalogChange({
          type: "PRODUCTS_REFRESH_REQUESTED",
          source: "products-import",
          count: successCount,
        });
      }
      if (errors.length) {
        console.warn("Import produits - erreurs:", errors);
        showToast(
          "error",
          `${errors.length} ligne(s) en erreur. Voir console.`,
        );
      }
    } catch (error) {
      console.error("Erreur import produits:", error);
      showToast("error", error.message || "Import echoue.");
    } finally {
      setIsImporting(false);
    }
  };

  const cancelCatalogImport = () => {
    approvedImportFileRef.current = null;
    setCatalogImportPreview(null);
  };

  const confirmCatalogImport = () => {
    const file = catalogImportPreview?.file;
    if (!file) return;
    approvedImportFileRef.current = file;
    setCatalogImportPreview(null);
    handleImportFile({ target: { files: [file] } });
  };

  const getVariationComboLabel = (variation) => {
    if (!variation) return "Variation";
    const attrs = variation.attributes && typeof variation.attributes === "object" ? variation.attributes : null;
    const parts = [];
    if (attrs) {
      Object.entries(attrs).forEach(([key, val]) => {
        if (val === undefined || val === null || `${val}`.trim() === "") return;
        const label = key && `${key}`.trim() ? `${key}: ${val}` : `${val}`;
        parts.push(label);
      });
    }
    if (parts.length) return parts.join(" • ");
    return variation.name || "Variation";
  };

  const getProductCardPriceDisplay = (product) => {
    if (!product) return formatCurrency(0);
    // Produit simple ou global
    if (product.product_type !== "variable") {
      return formatCurrency(product.price || 0);
    }
    const variations = Array.isArray(product.variations) ? product.variations.filter(Boolean) : [];
    if (!variations.length) {
      return formatCurrency(product.price || 0);
    }
    // Si toutes les variations ont le même prix, afficher un seul montant
    const prices = variations.map((v) => Number(v.price || 0));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) {
      return formatCurrency(min);
    }
    return formatCurrencyRange(min, max);
  };

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0b1428] to-[#050b18] text-slate-100"
    : "bg-slate-50 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/70";
  const mutedText = isDarkMode ? "text-white/60" : "text-slate-500";
  const dividerBorder = isDarkMode ? "border-white/10" : "border-slate-200";

  const totalProducts = products.length;
  const availableCount = products.filter((p) => p.is_available).length;
  const trackedCount = products.filter((p) => p.track_stock).length;
  const variableCount = products.filter(
    (p) => p.product_type === "variable",
  ).length;
  const inactiveCount = Math.max(totalProducts - availableCount, 0);

  const statCards = [
    { label: "Produits actifs", value: trackedCount },
    { label: "Disponibles", value: availableCount },
    { label: "Variables", value: variableCount },
    { label: "Indisponibles", value: inactiveCount },
  ];

  const navigationTabs = isBoutique
    ? [{ id: "products", label: "Catalogue" }]
    : [
        { id: "products", label: "Catalogue" },
        { id: "extras", label: "Extras & suppléments" },
      ];

  const openDailyMenuModal = (product) => {
    setDailyMenuProduct(product);
    setDailyMenuPrice(
      product.daily_menu_price !== null && product.daily_menu_price !== undefined
        ? product.daily_menu_price
        : product.price ?? "",
    );
    let inferredMode = product.daily_menu_scope || null;

    if (product.product_type === "variable" && Array.isArray(product.variations)) {
      const promoPrices = product.variations
        .filter((variation) => variation)
        .map((variation) => {
          if (variation.daily_menu_price !== null && variation.daily_menu_price !== undefined) {
            return variation.daily_menu_price;
          }
          if (variation.effective_price_for_day !== null && variation.effective_price_for_day !== undefined) {
            return variation.effective_price_for_day;
          }
          return null;
        })
        .filter((p) => p !== null);

      const basePrices = product.variations
        .filter((variation) => variation)
        .map((variation) => Number(variation.price) || 0);

      const distinctPromo = new Set(promoPrices.map((p) => String(p)));
      const distinctBase = new Set(basePrices.map((p) => String(p)));

      if (!inferredMode) {
        if (promoPrices.length > 1 && distinctPromo.size > 1) {
          inferredMode = "per_variation";
        } else if (promoPrices.length >= 1 && distinctPromo.size === 1) {
          inferredMode = product.daily_menu_price !== null && product.daily_menu_price !== undefined
            ? "global"
            : "per_variation";
        } else if (promoPrices.length === 0 && distinctBase.size > 1 && (product.daily_menu_price === null || product.daily_menu_price === undefined)) {
          inferredMode = "per_variation";
        } else {
          inferredMode = product.daily_menu_price !== null && product.daily_menu_price !== undefined
            ? "global"
            : "per_variation";
        }
      }

      setDailyMenuMode(inferredMode || "global");
      setDailyMenuVariationPrices(
        product.variations.map((variation) => ({
          id: variation.id,
          name: getVariationComboLabel(variation),
          price:
            variation.daily_menu_price !== null && variation.daily_menu_price !== undefined
              ? variation.daily_menu_price
              : variation.effective_price_for_day !== null && variation.effective_price_for_day !== undefined
                ? variation.effective_price_for_day
                : variation.price,
        })),
      );
    } else {
      setDailyMenuMode(inferredMode || "global");
      setDailyMenuVariationPrices([]);
    }
    setShowDailyMenuModal(true);
  };

  const closeDailyMenuModal = () => {
    if (isSavingDailyMenu) return;
    setShowDailyMenuModal(false);
    setDailyMenuProduct(null);
    setDailyMenuPrice("");
    setDailyMenuMode("global");
    setDailyMenuVariationPrices([]);
  };

  const updateDailyMenuState = (productId, nextFields) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productId
          ? {
              ...p,
              ...nextFields,
            }
          : p,
      ),
    );
  };

  const handleVariationPromoChange = (variationId, value) => {
    setDailyMenuVariationPrices((prev) =>
      prev.map((v) => (v.id === variationId ? { ...v, price: value } : v)),
    );
  };

  const handleSaveDailyMenu = async () => {
    if (!dailyMenuProduct) return;
    const priceValue = parseDecimalInput(dailyMenuPrice);
    const variationPriceMap = new Map(
      (dailyMenuProduct.variations || []).map((v) => [String(v.id), Number(v.price) || 0]),
    );
    if (dailyMenuProduct.product_type !== "variable") {
      if (!isValidCatalogPrice(dailyMenuPrice)) {
        showToast("error", "Veuillez saisir un prix promotionnel valide.");
        return;
      }
    } else if (dailyMenuMode === "global") {
      if (!isValidCatalogPrice(dailyMenuPrice)) {
        showToast("error", "Veuillez saisir un prix promotionnel valide.");
        return;
      }
    }
    try {
      setIsSavingDailyMenu(true);
      const variationsPayload =
        dailyMenuProduct.product_type === "variable" && dailyMenuMode === "per_variation"
          ? dailyMenuVariationPrices.map((v) => {
              const fallback = variationPriceMap.get(String(v.id)) || 0;
              return {
                id: v.id,
                price: hasExplicitCatalogPrice(v.price)
                  ? normalizeCatalogPrice(v.price)
                  : fallback,
              };
            })
          : undefined;
      if (
        dailyMenuProduct.product_type === "variable" &&
        dailyMenuMode === "per_variation" &&
        variationsPayload?.some((v) => !v.id || !isValidCatalogPrice(v.price))
      ) {
        showToast("error", "Chaque variation doit avoir un prix promo valide.");
        setIsSavingDailyMenu(false);
        return;
      }
      const response = await fetch(`/api/products/${dailyMenuProduct.id}/daily-menu`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          price: priceValue,
          mode: dailyMenuProduct.product_type === "variable" ? (dailyMenuMode || "global") : "global",
          variations: variationsPayload,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible d'activer le menu du jour.");
      }
      const data = await response.json();
    if (dailyMenuProduct.product_type === "variable") {
        const nextVariations =
          Array.isArray(dailyMenuProduct.variations) && dailyMenuProduct.variations.length
            ? dailyMenuProduct.variations.map((variation) => {
                if (dailyMenuMode === "global") {
                  return {
                  ...variation,
                  daily_menu_active: 1,
                  daily_menu_active_today: true,
                  daily_menu_price: priceValue,
                  daily_menu_date: data.date,
                  effective_price_for_day: priceValue,
                };
              }
              const custom = dailyMenuVariationPrices.find((v) => String(v.id) === String(variation.id));
              if (custom) {
                const promo = parseDecimalInput(custom.price);
                return {
                  ...variation,
                  daily_menu_active: 1,
                  daily_menu_active_today: true,
                  daily_menu_price: promo,
                  daily_menu_date: data.date,
                  effective_price_for_day: promo,
                };
              }
              return {
                ...variation,
                daily_menu_active: 0,
                daily_menu_active_today: false,
                daily_menu_price: null,
                daily_menu_date: null,
                effective_price_for_day: null,
              };
            })
          : [];
        const promoRange = nextVariations.length
          ? nextVariations.map((variation) =>
              variation.effective_price_for_day !== null && variation.effective_price_for_day !== undefined
                ? Number(variation.effective_price_for_day) || 0
                : Number(variation.price) || 0,
            )
          : [priceValue];
        const minRange =
          promoRange.length > 0 ? Math.min(...promoRange) : Number(dailyMenuProduct.price) || 0;
        const maxRange =
          promoRange.length > 0 ? Math.max(...promoRange) : Number(dailyMenuProduct.price) || 0;
        updateDailyMenuState(dailyMenuProduct.id, {
          daily_menu_active: 1,
          daily_menu_active_today: true,
          daily_menu_price: dailyMenuMode === "global" ? priceValue : null,
          daily_menu_date: data.date,
          variations: nextVariations,
          daily_menu_scope: dailyMenuMode,
          price_min_today: minRange,
          price_max_today: maxRange,
        });
      } else {
        updateDailyMenuState(dailyMenuProduct.id, {
          daily_menu_active: 1,
          daily_menu_active_today: true,
          daily_menu_price: priceValue,
          daily_menu_date: data.date,
          effective_price_for_day: priceValue,
          daily_menu_scope: "global",
          price_min_today: priceValue,
          price_max_today: priceValue,
        });
      }
      broadcastProductCatalogChange({
        type: "PRODUCT_DAILY_MENU",
        productId: dailyMenuProduct.id,
        daily_menu_price: priceValue,
        daily_menu_date: data.date,
      });
      showToast("success", "Produit ajouté au menu du jour pour la journée.");
      closeDailyMenuModal();
    } catch (error) {
      console.error("❌ Menu du jour:", error);
      showToast("error", error.message || "Erreur lors de l'activation.");
    } finally {
      setIsSavingDailyMenu(false);
    }
  };

  const handleDisableDailyMenu = async () => {
    if (!dailyMenuProduct) return;
    try {
      setIsSavingDailyMenu(true);
      const response = await fetch(`/api/products/${dailyMenuProduct.id}/daily-menu`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Impossible de désactiver le menu du jour.");
      }
      const baseRange = Array.isArray(dailyMenuProduct.variations) && dailyMenuProduct.variations.length
        ? {
            min: Math.min(
              ...dailyMenuProduct.variations.map((variation) => Number(variation.price) || 0),
            ),
            max: Math.max(
              ...dailyMenuProduct.variations.map((variation) => Number(variation.price) || 0),
            ),
          }
        : {
            min: Number(dailyMenuProduct.price) || 0,
            max: Number(dailyMenuProduct.price) || 0,
          };
      updateDailyMenuState(dailyMenuProduct.id, {
        daily_menu_active: 0,
        daily_menu_active_today: false,
        daily_menu_price: null,
        daily_menu_date: null,
        daily_menu_scope: null,
        price_min_today: baseRange.min,
        price_max_today: baseRange.max,
      });
      broadcastProductCatalogChange({
        type: "PRODUCT_DAILY_MENU_DISABLED",
        productId: dailyMenuProduct.id,
      });
      showToast("success", "Produit retiré du menu du jour.");
      closeDailyMenuModal();
    } catch (error) {
      console.error("❌ Menu du jour désactivation:", error);
      showToast("error", error.message || "Erreur lors de la désactivation.");
    } finally {
      setIsSavingDailyMenu(false);
    }
  };

  const availabilityFilters = [
    { id: "all", label: "Tous" },
    { id: "available", label: "Disponibles" },
    { id: "unavailable", label: "Indisponibles" },
    { id: "popular", label: "Favoris" },
    { id: "trending", label: "Tendance" },
  ];

  const sortLabels = {
    name: "Nom",
    price: "Prix",
    popularity: "Popularité",
    sales: "Ventes",
  };

  const selectedSortLabel = sortLabels[sortBy] || "Nom";
  const selectedAvailabilityLabel =
    availabilityFilters.find((filter) => filter.id === filterBy)?.label ||
    "Tous";
  const selectedCategoryLabel =
    selectedCategory === "all"
      ? "Toutes"
      : safeCategories.find((category) => String(category.id) === selectedCategory)
        ?.name || "Catégorie";
  const defaultViewMode = isBoutique ? "list" : "grid";
  const selectedViewLabel = viewMode === "list" ? "Liste" : "Grille";

  const activeProductFiltersCount = [
    searchTerm && searchTerm.trim() ? 1 : 0,
    selectedCategory !== "all" ? 1 : 0,
    filterBy !== "all" ? 1 : 0,
    sortBy !== "name" ? 1 : 0,
    viewMode !== defaultViewMode ? 1 : 0,
  ].reduce((total, value) => total + value, 0);

  const resetProductFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setFilterBy("all");
    setSortBy("name");
    setViewMode(defaultViewMode);
    setCurrentPage(1);
  };

  // Filtrage et tri des produits
  const filteredProducts = products
    .filter((product) => {
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const variationSkus = (product.variations || [])
        .map((variation) => variation?.sku)
        .filter(Boolean);
      const searchableValues = [product.name, product.sku, ...variationSkus]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      const matchesSearch =
        normalizedSearch.length === 0 ||
        searchableValues.some((value) => value.includes(normalizedSearch));
      const matchesCategory =
        selectedCategory === "all" ||
        product.category_id === parseInt(selectedCategory);
      const matchesFilter =
        filterBy === "all" ||
        (filterBy === "available" && product.is_available) ||
        (filterBy === "unavailable" && !product.is_available) ||
        (filterBy === "popular" &&
          productAnalytics[product.id]?.popularity > 70) ||
        (filterBy === "trending" &&
          productAnalytics[product.id]?.monthlyTrend === "up");

      return matchesSearch && matchesCategory && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "price":
          return b.price - a.price;
        case "popularity":
          return (
            (productAnalytics[b.id]?.popularity || 0) -
            (productAnalytics[a.id]?.popularity || 0)
          );
        case "sales":
          return (
            (productAnalytics[b.id]?.totalSales || 0) -
            (productAnalytics[a.id]?.totalSales || 0)
          );
        default:
          return 0;
      }
    });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / ITEMS_PER_PAGE),
  );
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const renderDailyMenuModal = () => {
    if (isBoutique || !showDailyMenuModal || !dailyMenuProduct) return null;
    const isActive = dailyMenuProduct.daily_menu_active_today;
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="relative w-full max-w-lg">
          <div className={`${cardClasses} relative`}>
            <div className="rounded-t-3xl px-6 py-4 flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-orange-500/10">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Menu du jour</p>
                <h3 className="text-xl font-semibold mt-1">{dailyMenuProduct.name}</h3>
                <p className={`text-xs ${mutedText}`}>
                  Tarif promotionnel valable uniquement sur la journée de service en cours.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDailyMenuModal}
                className="rounded-full p-2 bg-white/10 hover:bg-white/20"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-4">
                <div className="grid gap-3">
                  <div>
                    <p className="text-sm font-semibold">Prix actuel</p>
                    <p className="text-lg font-bold">{formatCurrency(dailyMenuProduct.price || 0)}</p>
                  </div>
                  {dailyMenuProduct.product_type === "variable" ? (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Mode d'application</p>
                      <div className="flex flex-wrap gap-3">
                        {[
                          { id: "global", label: "Un seul tarif pour toutes les variations" },
                          { id: "per_variation", label: "Un tarif par variation" },
                        ].map((opt) => (
                          <label
                            key={opt.id}
                            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm border cursor-pointer ${
                              dailyMenuMode === opt.id
                                ? "border-amber-400 bg-amber-50 text-amber-800"
                                : isDarkMode
                                  ? "border-white/10 text-white/80"
                                  : "border-slate-200 text-slate-700"
                            }`}
                          >
                            <input
                              type="radio"
                              name="daily-menu-mode"
                              value={opt.id}
                              checked={dailyMenuMode === opt.id}
                              onChange={(e) => setDailyMenuMode(e.target.value)}
                              className="h-4 w-4 text-amber-500"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {dailyMenuProduct.product_type === "variable" && dailyMenuMode === "per_variation" ? (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">Prix par variation *</p>
                      <div className="space-y-2">
                        {dailyMenuVariationPrices.map((variation) => (
                          <div key={variation.id} className="flex items-center gap-3">
                            <div className="flex-1">
                              <p className="text-sm font-semibold">{variation.name}</p>
                            </div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={variation.price}
                              onChange={(e) => handleVariationPromoChange(variation.id, e.target.value)}
                              className={`w-32 rounded-2xl px-3 py-2 text-sm border ${
                                isDarkMode
                                  ? "bg-white/5 border-white/10 text-white"
                                  : "bg-white border-slate-200 text-slate-900"
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                      <p className={`text-xs mt-1 ${mutedText}`}>
                        Chaque variation utilisera son prix promo pour la journée en cours.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="text-sm font-semibold block mb-1">
                        Prix du jour (promotionnel) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={dailyMenuPrice}
                        onChange={(e) => setDailyMenuPrice(e.target.value)}
                        className={`w-full rounded-2xl px-4 py-2 text-sm border ${
                          isDarkMode
                            ? "bg-white/5 border-white/10 text-white"
                            : "bg-white border-slate-200 text-slate-900"
                        }`}
                        placeholder="Ex: 4500"
                      />
                      <p className={`text-xs mt-1 ${mutedText}`}>
                        Ce prix sera appliqué en caisse pour la journée de service en cours.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={closeDailyMenuModal}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 disabled:opacity-50"
                disabled={isSavingDailyMenu}
              >
                Annuler
              </button>
              {isActive && (
                <button
                  type="button"
                  onClick={handleDisableDailyMenu}
                  disabled={isSavingDailyMenu}
                  className="rounded-2xl border border-rose-500/40 px-4 py-2 text-sm font-semibold text-rose-200 hover:border-rose-400/80 disabled:opacity-50"
                >
                  {isSavingDailyMenu ? "Traitement…" : "Retirer du menu du jour"}
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveDailyMenu}
                disabled={isSavingDailyMenu}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                {isSavingDailyMenu ? "Enregistrement…" : "Valider"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, filterBy, sortBy, viewMode]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const renderPaginationControls = () => {
    if (totalPages <= 1) return null;
    const buttonClasses = (disabled) =>
      `px-4 py-2 rounded-xl text-sm font-semibold transition ${
        disabled
          ? "bg-white/5 text-white/30 cursor-not-allowed"
          : isDarkMode
            ? "bg-white/10 text-white hover:bg-white/20"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`;

    return (
      <div className="mt-6 flex items-center justify-center gap-4">
        <button
          type="button"
          className={buttonClasses(currentPage === 1)}
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        >
          ← Précédent
        </button>
        <span className="text-sm font-semibold">
          Page {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          className={buttonClasses(currentPage === totalPages)}
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
        >
          Suivant →
        </button>
      </div>
    );
  };

  const ProductCard = ({ product }) => {
    const analytics = productAnalytics[product.id] || {};
    const categoryLabel =
      safeCategories.find((c) => c.id === product.category_id)?.name ||
      "Non classé";
    const imageSrc = product.image_url || product.image_path || null;
    const ordersCount =
      analytics.ordersCount ?? product.total_items_sold ?? 0;
    const totalRevenue =
      analytics.totalSales ?? product.total_revenue_amount ?? 0;

    return (
      <div className={`${cardClasses} rounded-3xl p-4 sm:p-5 space-y-4`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`h-14 w-14 rounded-2xl border ${dividerBorder} overflow-hidden bg-white/5 flex items-center justify-center`}
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <Package className="h-6 w-6 text-amber-300" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.3em] text-amber-300">
                {categoryLabel}
              </p>
              <h3 className="text-lg sm:text-xl font-semibold truncate">
                {product.name}
              </h3>
              <p className={`text-xs ${mutedText}`}>
                {!isBoutique && (
                  <>
                    {product.destination_name ||
                      (product.destination === "none"
                        ? "Aucune destination"
                        : product.destination)} ·{" "}
                  </>
                )}
                {product.product_type === "variable"
                  ? "Produit variable"
                  : "Produit simple"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                product.is_available
                  ? "bg-emerald-400/20 text-emerald-200"
                  : "bg-rose-500/20 text-rose-200"
              }`}
            >
              {product.is_available ? "Disponible" : "Indisponible"}
            </span>
            {!isBoutique && (
              <button
                onClick={() => openDailyMenuModal(product)}
                className={`rounded-full border p-2 transition ${
                  product.daily_menu_active_today
                    ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow-lg"
                    : isDarkMode
                      ? "border-white/10 text-white/80 hover:border-amber-400/60"
                      : "border-slate-200 text-slate-700 hover:border-amber-400/60"
                }`}
                title={
                  product.daily_menu_active_today
                    ? "Menu du jour actif"
                    : "Ajouter au menu du jour"
                }
              >
                <Sun size={16} />
              </button>
            )}
          </div>
        </div>

        <p className={`text-sm ${mutedText} line-clamp-2 hidden sm:block`}>
          {product.description || "Aucune description enregistrée pour l'instant."}
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
              Tarif
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {getProductCardPriceDisplay(product)}
            </p>
          </div>
          <div className="hidden sm:block sm:flex-1">
            <div className={`flex justify-between text-xs ${mutedText}`}>
              <span>Popularité</span>
              <span>{analytics.popularity || 0}%</span>
            </div>
            <div className="mt-1 h-2 w-full rounded-full bg-white/10">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500"
                style={{ width: `${analytics.popularity || 0}%` }}
              />
            </div>
          </div>
        </div>

        <div className={`flex flex-col gap-3 border-t ${dividerBorder} pt-3 sm:flex-row sm:items-center sm:justify-between`}>
          <div className="grid grid-cols-2 gap-3 text-xs sm:flex sm:gap-6">
            <div>
              <p className={mutedText}>Commandes</p>
              <p className="font-semibold">{ordersCount}</p>
            </div>
            <div>
              <p className={mutedText}>Revenus</p>
              <p className="font-semibold">
                {formatCurrency(totalRevenue)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleToggleProductTracking(product)}
              className={`rounded-full border border-white/10 p-2 ${
                product.track_stock
                  ? "text-emerald-300 hover:border-emerald-400/60"
                  : "text-amber-300 hover:border-amber-400/60"
              }`}
              title={
                product.track_stock
                  ? "Désactiver le suivi de stock"
                  : "Activer le suivi de stock"
              }
            >
              <LocateFixed size={16} />
            </button>
            <button
              onClick={() => handleEditProduct(product)}
              className="rounded-full border border-white/10 p-2 hover:border-emerald-400/60"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => handleToggleProductAvailability(product)}
              className={`rounded-full border border-white/10 p-2 ${
                product.is_available
                  ? "text-emerald-300 hover:border-emerald-400/60"
                  : "text-amber-300 hover:border-amber-400/60"
              }`}
              title={
                product.is_available
                  ? "Désactiver ce produit"
                  : "Activer ce produit"
              }
            >
              <Power size={16} />
            </button>
            <button
              onClick={() => handleDeleteProduct(product.id)}
              className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ProductListItem = ({ product }) => {
    const analytics = productAnalytics[product.id] || {};
    const categoryLabel =
      safeCategories.find((c) => c.id === product.category_id)?.name ||
      "Non classé";
    const imageSrc = product.image_url || product.image_path || null;
    const ordersCount =
      analytics.ordersCount ?? product.total_items_sold ?? 0;
    const totalRevenue =
      analytics.totalSales ?? product.total_revenue_amount ?? 0;

    return (
      <div
        className={`flex flex-col gap-4 rounded-3xl border ${dividerBorder} p-4 sm:flex-row sm:items-center sm:justify-between ${
          isDarkMode ? "bg-white/5" : "bg-white"
        }`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="h-12 w-12 rounded-2xl border border-white/10 overflow-hidden bg-white/5 flex items-center justify-center">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={product.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-5 w-5 text-amber-300" />
            )}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">{product.name}</p>
            <p className={`text-xs ${mutedText}`}>
              {categoryLabel} · {product.product_type === "variable" ? "Variable" : "Simple"}
            </p>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:flex-nowrap sm:justify-end">
          <div className="text-sm font-semibold">
            {getProductCardPriceDisplay(product)}
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col text-xs text-right">
              <span className={mutedText}>Commandes</span>
              <span className="font-semibold">{ordersCount}</span>
            </div>
            <div className="hidden lg:flex flex-col text-xs text-right">
              <span className={mutedText}>Revenus</span>
              <span className="font-semibold">
                {formatCurrency(totalRevenue)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isBoutique && (
              <button
                onClick={() => openDailyMenuModal(product)}
                className={`rounded-full border p-2 transition ${
                  product.daily_menu_active_today
                    ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow-lg"
                    : isDarkMode
                      ? "border-white/10 text-white/80 hover:border-amber-400/60"
                      : "border-slate-200 text-slate-700 hover:border-amber-400/60"
                }`}
                title={
                  product.daily_menu_active_today
                    ? "Menu du jour actif"
                    : "Ajouter au menu du jour"
                }
              >
                <Sun size={16} />
              </button>
            )}
            <button
              onClick={() => handleToggleProductTracking(product)}
              className={`rounded-full border border-white/10 p-2 ${
                product.track_stock
                  ? "text-emerald-300 hover:border-emerald-400/60"
                  : "text-amber-300 hover:border-amber-400/60"
              }`}
              title={
                product.track_stock
                  ? "Désactiver le suivi de stock"
                  : "Activer le suivi de stock"
              }
            >
              <LocateFixed size={16} />
            </button>
            <button
              onClick={() => handleEditProduct(product)}
              className="rounded-full border border-white/10 p-2 hover:border-emerald-400/60"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => handleToggleProductAvailability(product)}
              className={`rounded-full border border-white/10 p-2 ${
                product.is_available
                  ? "text-emerald-300 hover:border-emerald-400/60"
                  : "text-amber-300 hover:border-amber-400/60"
              }`}
              title={
                product.is_available
                  ? "Désactiver ce produit"
                  : "Activer ce produit"
              }
            >
              <Power size={16} />
            </button>
            <button
              onClick={() => handleDeleteProduct(product.id)}
              className="rounded-full border border-white/10 p-2 text-rose-400 hover:border-rose-400/60"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Gestion du téléversement d'images
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // La logique de cette fonction a été déplacée et améliorée dans AddProductModal
  // const resetImageInputs = () => { ... };

  // Fonction pour gérer la modification d'un produit
  const handleEditProduct = async (product) => {
    // Vérification de sécurité
    if (!product) {
      console.error("Produit non fourni à handleEditProduct");
    showToast("error", "Erreur : Produit non trouvé");
      return;
    }

    // Charger les extras disponibles EN PREMIER si pas déjà fait
    let extrasList = availableExtras;
    if (extrasList.length === 0) {
      console.log("🔄 Chargement des extras disponibles...");
      extrasList = await loadAvailableExtras();
      console.log("✅ Extras disponibles chargés:", extrasList.length);
    }
    const extrasIndex = Object.fromEntries(
      extrasList.map((extra) => [extra.id, extra]),
    );

    // Initialiser les données du produit avec des valeurs par défaut
    const editingData = {
      ...product,
      id: product.id,
      name: product.name || "",
      sku: product.sku || "",
      description: product.description || "",
      category_id: product.category_id ? product.category_id.toString() : "",
      price: product.price !== null && product.price !== undefined
        ? product.price.toString()
        : "",
      cost_price: product.cost_price ? product.cost_price.toString() : "",
      yango_price:
        product.yango_price !== null && product.yango_price !== undefined
          ? product.yango_price.toString()
          : "",
      glovo_price:
        product.glovo_price !== null && product.glovo_price !== undefined
          ? product.glovo_price.toString()
          : "",
      destination: product.destination || "cuisine",
      preparation_time: product.preparation_time
        ? product.preparation_time.toString()
        : "",
      unit_type: product.unit_type || "Unité",
      product_type: product.product_type || "simple",
      has_accompaniments: Boolean(product.has_accompaniments),
      has_supplements: Boolean(product.has_supplements),
      free_accompaniment_quota: product.free_accompaniment_quota ?? 1,
      is_available: Boolean(product.is_available),
      track_stock: Boolean(product.track_stock),
    };

    setEditingProduct(editingData);
    setShowEditingAggregatorPrices(
      hasExplicitCatalogPrice(editingData.yango_price) ||
        hasExplicitCatalogPrice(editingData.glovo_price),
    );
    setEditProductType(editingData.product_type);
    if (editingData.product_type === "variable") {
      const hasGlobalPrice =
        hasExplicitCatalogPrice(editingData.price) &&
        (!product.variations ||
          product.variations.every(
            (v) =>
              v.price === undefined ||
              v.price === null ||
              Number(v.price) === Number(editingData.price),
          ));
      setEditingUseGlobalPrice(hasGlobalPrice);
      const tracksVariations = (product.variations || []).some(
        (variation) => Boolean(variation.track_stock),
      );
      setEditingUseGlobalStock(Boolean(product.track_stock) && !tracksVariations);
      setEditingUseVariationStock(tracksVariations);
    } else {
      setEditingUseGlobalPrice(true);
      setEditingUseGlobalStock(Boolean(product.track_stock));
      setEditingUseVariationStock(false);
    }

    // Charger les extras du produit
    let productExtrasFormatted = [];
    try {
      console.log(
        "🔄 Chargement des extras pour le produit:",
        product.name,
        "ID:",
        product.id,
      );
      const response = await fetch(`/api/products/${product.id}/extras`);
      if (response.ok) {
        const productExtras = await response.json();
        console.log("📋 Extras bruts de l'API:", productExtras);

        // S'assurer que chaque extra a les bonnes propriétés
        const formattedExtras = productExtras.map((extra) => ({
          ...extra,
          price:
            extra.price !== undefined ? extra.price : extra.base_price || 0,
          max_quantity: extra.max_quantity || 1,
          type:
            extra.type ||
            (extra.category === "Accompagnements"
              ? "accompaniment"
              : "supplement"),
        }));

        console.log("✅ Extras formatés pour l'édition:", formattedExtras);
        productExtrasFormatted = formattedExtras;
        setEditingSelectedExtras(formattedExtras);
      } else {
        console.log("❌ Aucun extra trouvé pour ce produit");
        setEditingSelectedExtras([]);
      }
    } catch (error) {
      console.error("❌ Erreur lors du chargement des extras:", error);
      setEditingSelectedExtras([]);
    }

    // Initialiser l'image (privilégier le mode "media" pour les images déjà stockées)
    const existingImage = product.image_path || product.image_url || null;
    const looksLikeInternalMedia =
      existingImage && existingImage.startsWith("/images/");

    if (existingImage) {
      setEditingImagePreview(existingImage);
      setEditingImageUploadMode(
        looksLikeInternalMedia
          ? "media"
          : product.image_url
            ? "url"
            : "none",
      );
      setEditingSelectedMediaImage(
        looksLikeInternalMedia
          ? {
              path: existingImage,
              name: product.name || "Image produit",
            }
          : null,
      );
    } else {
      setEditingImagePreview(null);
      setEditingImageUploadMode("none");
      setEditingSelectedMediaImage(null);
    }
    setEditingSelectedImageFile(null);

    let normalizedVariations = [];
    if (
      product.product_type === "variable" &&
      Array.isArray(product.variations)
    ) {
      normalizedVariations = product.variations.map((variation, index) => ({
        ...variation,
        uid: `edit-${variation.id ?? index}`,
        price: variation.price != null ? variation.price.toString() : "",
        cost_price:
          variation.cost_price != null ? variation.cost_price.toString() : "",
        yango_price:
          variation.yango_price != null ? variation.yango_price.toString() : "",
        glovo_price:
          variation.glovo_price != null ? variation.glovo_price.toString() : "",
        stock_quantity: variation.stock_quantity ?? variation.stock ?? 0,
        attributes: variation.attributes || {},
        is_active: variation.is_active !== false,
      }));
    }

    setEditingVariations(normalizedVariations);
    syncEditingVariationExtrasState(normalizedVariations);

    const variationExtrasMap = {};
    let variationExtrasDetected = false;
    normalizedVariations.forEach((variation, index) => {
      const key = getEditingVariationKey(variation, index);
      const extrasForVariation = (variation.extras || []).map((extra) => {
        const base = extrasIndex[extra.id] || {};
        const resolvedType =
          extra.type ||
          (base.category === "Accompagnements"
            ? "accompaniment"
            : "supplement");
        return {
          ...base,
          id: extra.id,
          type: resolvedType,
          price: extra.price ?? base.base_price ?? 0,
          max_quantity: extra.max_quantity || 1,
        };
      });
      if (extrasForVariation.length > 0) {
        variationExtrasDetected = true;
        variationExtrasMap[key] = extrasForVariation;
      }
    });
    setEditingVariationExtras(variationExtrasMap);
    setEditingExtraScope(
      variationExtrasDetected && normalizedVariations.length > 0
        ? "perVariation"
        : "global",
    );
    if (variationExtrasDetected) {
      setEditingSelectedExtras([]);
    } else {
      setEditingSelectedExtras(productExtrasFormatted);
    }

    const derivedAttributesMap = {};
    normalizedVariations.forEach((variation) => {
      Object.entries(variation.attributes || {}).forEach(([name, value]) => {
        if (!derivedAttributesMap[name]) {
          derivedAttributesMap[name] = new Set();
        }
        derivedAttributesMap[name].add(value);
      });
    });
    const derivedAttributes = Object.entries(derivedAttributesMap).map(
      ([name, values]) => ({
        name,
        values: Array.from(values),
        newValue: "",
      }),
    );
    setEditingAttributes(derivedAttributes);

    // Réinitialiser l'étape du modal
    setEditCurrentStep(1);
    setShowEditModal(true);
  };

  // Fonction pour réinitialiser l'état d'édition
  const resetEditingState = () => {
    setEditingProduct(null);
    setEditingSelectedExtras([]);
    setEditingImagePreview(null);
    setEditingImageUploadMode("none");
    setEditingSelectedImageFile(null);
    setEditingSelectedMediaImage(null);
    setShowEditModal(false);
    setEditCurrentStep(1);
    setEditProductType("simple");
    setEditingVariations([]);
    setEditingExtraScope("global");
    setEditingVariationExtras({});
    setEditingUseGlobalPrice(true);
    setEditingUseGlobalStock(true);
    setEditingUseVariationStock(false);
    setShowEditingAggregatorPrices(false);
    setShowEditQuickCategoryModal(false);
    setEditQuickCategoryName("");
    setIsCreatingEditQuickCategory(false);
  };

  // Fonction pour gérer la sélection d'image depuis la médiathèque
  const handleEditingSelectFromMedia = (image) => {
    setEditingImagePreview(image.path);
    setEditingImageUploadMode("media");
    setEditingSelectedMediaImage(image);
    setEditingSelectedImageFile(null);
    setShowEditingMediaModal(false);
    showToast("success", `Image "${image.name}" sélectionnée pour l'édition !`);
  };

  // Fonction pour gérer le changement de fichier image
  const handleEditingImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setEditingSelectedImageFile(file);
      setEditingImageUploadMode("upload");
      setEditingSelectedMediaImage(null);
      const reader = new FileReader();
      reader.onload = (event) => {
        setEditingImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour gérer le changement d'URL d'image
  const handleEditingImageUrlChange = (e) => {
    const { value } = e.target;
    setEditingImagePreview(value);
    setEditingImageUploadMode("url");
    setEditingSelectedImageFile(null);
    setEditingSelectedMediaImage(null);
  };

  // Fonction pour ajouter un extra à la liste d'édition
  const addEditingExtra = (extra, type = null) => {
    if (!editingSelectedExtras.find((e) => e.id === extra.id)) {
      // Détecter automatiquement le type si pas fourni
      const extraType =
        type ||
        (extra.category === "Accompagnements" ? "accompaniment" : "supplement");

      setEditingSelectedExtras([
        ...editingSelectedExtras,
        {
          ...extra,
          price: extra.base_price || 0,
          max_quantity: 1,
          type: extraType,
        },
      ]);
    }
  };

  // Fonction pour supprimer un extra de la liste d'édition
  const removeEditingExtra = (extraId) => {
    setEditingSelectedExtras(
      editingSelectedExtras.filter((extra) => extra.id !== extraId),
    );
  };

  const updateEditingVariation = (index, field, value) => {
    setEditingVariations((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  // Fonction pour mettre à jour un extra dans la liste d'édition
  const updateEditingExtra = (extraId, field, value) => {
    const newExtras = editingSelectedExtras.map((extra) =>
      extra.id === extraId ? { ...extra, [field]: value } : extra,
    );
    setEditingSelectedExtras(newExtras);
  };

  // Fonction pour mettre à jour le produit
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    const normalizedName = editingProduct?.name?.trim().toLowerCase();
    if (normalizedName) {
      const duplicateExists = products.some(
        (product) =>
          product.id !== editingProduct.id &&
          product.name?.trim().toLowerCase() === normalizedName,
      );
      if (duplicateExists) {
        showToast(
          "error",
          "Un produit portant ce nom existe déjà dans le catalogue.",
        );
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const currentProductType =
        editProductType || editingProduct.product_type || "simple";

      const trackingActive =
        currentProductType === "variable"
          ? editingUseGlobalStock || editingUseVariationStock
          : !!editingProduct.track_stock;
      if (trackingActive && !String(editingProduct.inventory_kind || "").trim()) {
        showToast(
          "error",
          "Veuillez préciser si l'article suivi est un produit revendu ou un produit composé.",
        );
        setIsSubmitting(false);
        return;
      }

      // Validation côté client AVANT d'envoyer au serveur
      if (!editingProduct.name || !editingProduct.name.trim()) {
        showToast("error", "Le nom du produit est obligatoire");
        setIsSubmitting(false);
        return;
      }

      if (!editingProduct.category_id || editingProduct.category_id === "") {
        showToast("error", "La catégorie est obligatoire");
        setIsSubmitting(false);
        return;
      }

      if (currentProductType === "simple") {
        if (!isValidCatalogPrice(editingProduct.price)) {
          showToast("error", "Le prix de vente est obligatoire et ne peut pas être négatif");
          setIsSubmitting(false);
          return;
        }
      } else if (editingUseGlobalPrice) {
        if (!isValidCatalogPrice(editingProduct.price)) {
          showToast("error", "Le prix global est obligatoire et ne peut pas être négatif");
          setIsSubmitting(false);
          return;
        }
      } else {
        const hasInvalidVariationPrice = editingVariations.some(
          (variation) =>
            !isValidCatalogPrice(variation.price),
        );
        if (hasInvalidVariationPrice) {
          showToast(
            "error",
            "Chaque variation doit avoir un prix de vente valide qui ne peut pas être négatif.",
          );
          setIsSubmitting(false);
          return;
        }
      }

      const formDataUpdate = new FormData();
      formDataUpdate.append("name", editingProduct.name.trim());
      formDataUpdate.append("sku", (editingProduct.sku || "").trim());
      formDataUpdate.append("description", editingProduct.description || "");
      formDataUpdate.append("category_id", parseInt(editingProduct.category_id));
      const normalizedPrice =
        currentProductType === "variable"
          ? editingUseGlobalPrice
            ? parseFloat(editingProduct.price)
            : parseFloat(
                editingVariations[0]?.price ?? editingProduct.price ?? 0,
              )
          : parseFloat(editingProduct.price);
      formDataUpdate.append("price", normalizedPrice || 0);
      formDataUpdate.append("cost_price", parseFloat(editingProduct.cost_price || 0));
      formDataUpdate.append("yango_price", editingProduct.yango_price ?? "");
      formDataUpdate.append("glovo_price", editingProduct.glovo_price ?? "");
      const destinationValue = editingProduct.destination || "cuisine";
      formDataUpdate.append("destination", destinationValue);
      formDataUpdate.append(
        "preparation_time",
        parseInt(editingProduct.preparation_time || 0),
      );
      formDataUpdate.append("is_available", editingProduct.is_available ? 1 : 0);
      formDataUpdate.append(
        "track_stock",
        currentProductType === "variable"
          ? editingUseGlobalStock || editingUseVariationStock
            ? 1
            : 0
          : editingProduct.track_stock
            ? 1
            : 0,
      );
      formDataUpdate.append("inventory_kind", editingProduct.inventory_kind || "");
      formDataUpdate.append(
        "is_recipe_ingredient",
        editingProduct.inventory_kind === "resold" && editingProduct.is_recipe_ingredient
          ? "true"
          : "false",
      );
      formDataUpdate.append("unit_type", editingProduct.unit_type || "Unité");
      formDataUpdate.append("product_type", currentProductType);
      formDataUpdate.append(
        "has_accompaniments",
        editingProduct.has_accompaniments ? 1 : 0,
      );
      formDataUpdate.append(
        "has_supplements",
        editingProduct.has_supplements ? 1 : 0,
      );

      formDataUpdate.append(
        "free_accompaniment_quota",
        String(
          Math.max(0, Number(editingProduct.free_accompaniment_quota) || 0),
        ),
      );

      // Image
      if (
        editingImageUploadMode === "upload" &&
        editingSelectedImageFile instanceof File
      ) {
        formDataUpdate.append("image", editingSelectedImageFile);
      } else if (editingImageUploadMode === "url" && editingImagePreview) {
        formDataUpdate.append("image_url", editingImagePreview);
      } else if (
        editingImageUploadMode === "media" &&
        editingSelectedMediaImage
      ) {
        formDataUpdate.append("image_path", editingSelectedMediaImage.path);
      } else if (editingImagePreview) {
        formDataUpdate.append("image_path", editingImagePreview);
      }

      const globalExtrasPayload = editingSelectedExtras.map(
        formatExtraPayload,
      );

      if (
        editingExtraScope !== "perVariation" ||
        currentProductType !== "variable"
      ) {
        formDataUpdate.append(
          "selectedExtras",
          JSON.stringify(globalExtrasPayload),
        );
      }

      if (currentProductType === "variable") {
        const attributesPayload = (editingAttributes || [])
          .filter((attr) => attr.name?.trim())
          .map((attr) => ({
            name: attr.name,
            values: attr.values || [],
          }));
        formDataUpdate.append("attributes", JSON.stringify(attributesPayload));

        const variationsPayload = editingVariations.map((variation, index) => {
          const key = getEditingVariationKey(variation, index);
          const extrasForVariation =
            editingExtraScope === "perVariation"
              ? (editingVariationExtras[key] || []).map(formatExtraPayload)
              : globalExtrasPayload;

          const variationPrice = editingUseGlobalPrice
            ? parseFloat(editingProduct.price)
            : parseFloat(variation.price);
          const variationCost =
            variation.cost_price !== undefined && variation.cost_price !== null
              ? parseFloat(variation.cost_price)
              : parseFloat(editingProduct.cost_price || 0);

          return {
            ...variation,
            price: variationPrice || 0,
            cost_price: variationCost || 0,
            track_stock: editingUseVariationStock,
            stock_quantity: 0,
            extras: extrasForVariation,
          };
        });
        formDataUpdate.append("variations", JSON.stringify(variationsPayload));
      }

      const response = await fetch(`/api/products/${editingProduct.id}`, {
        method: "PUT",
        body: formDataUpdate,
      });

      if (response.ok) {
        showToast("success", "🎉 Produit mis à jour avec succès !");
        broadcastProductCatalogChange({
          type: "PRODUCT_UPDATED",
          productId: editingProduct.id,
        });
        resetEditingState();
        await loadProducts({ silent: true });
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.error ||
            errorData.message ||
            "Erreur lors de la mise à jour du produit",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      showToast("error", `❌ Erreur: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour valider l'étape courante du modal d'édition
  const validateEditStep = () => {
    // Vérification de sécurité : s'assurer que editingProduct existe
    if (!editingProduct) {
      console.error("editingProduct est null ou undefined");
      return false;
    }

    const resolvedStepId = productStepGuide[editCurrentStep - 1]?.id || 1;

    switch (resolvedStepId) {
      case 1:
        const hasName =
          editingProduct.name && editingProduct.name.trim().length > 0;
        const hasCategory =
          editingProduct.category_id && editingProduct.category_id !== "";

        if (!hasName) {
          showToast("error", "Le nom du produit est obligatoire");
          return false;
        }
        if (!hasCategory) {
          showToast("error", "La catégorie est obligatoire");
          return false;
        }
        return true;
      case 2:
        if (editProductType === "simple") {
          const hasPrice = isValidCatalogPrice(editingProduct.price);

          if (!hasPrice) {
            showToast(
              "error",
              "Le prix de vente est obligatoire et ne peut pas être négatif",
            );
            return false;
          }
          return true;
        }

        if (
          editingAttributes.length === 0 ||
          editingAttributes.some(
            (attr) =>
              !attr.name?.trim() ||
              !Array.isArray(attr.values) ||
              attr.values.length === 0,
          )
        ) {
          showToast(
            "error",
            "Définissez au moins un attribut et des valeurs pour vos variations.",
          );
          return false;
        }

        if (editingVariations.length === 0) {
          showToast(
            "error",
            "Générez au moins une variation avant de continuer.",
          );
          return false;
        }

        if (editingUseGlobalPrice) {
          if (!isValidCatalogPrice(editingProduct.price)) {
            showToast(
              "error",
              "Le prix global est obligatoire et ne peut pas être négatif.",
            );
            return false;
          }
        } else {
          if (
            editingVariations.some(
              (variation) => !isValidCatalogPrice(variation.price),
            )
          ) {
            showToast(
              "error",
              "Chaque variation doit avoir un prix de vente valide qui ne peut pas être négatif.",
            );
            return false;
          }
        }
        return true;
      case 3:
        return true; // Étape optionnelle
      case 4:
        return true; // Récapitulatif
      default:
        return false;
    }
  };

  // Fonction pour passer à l'étape suivante
  const handleEditNext = () => {
    if (validateEditStep()) {
      setEditCurrentStep((prev) => Math.min(prev + 1, editTotalSteps));
    } else {
      showToast("error", "Veuillez remplir tous les champs obligatoires");
    }
  };

  // Fonction pour revenir à l'étape précédente
  const handleEditBack = () =>
    setEditCurrentStep((prev) => Math.max(prev - 1, 1));

  const handleCreateCategoryFromEditModal = async () => {
    const categoryName = String(editQuickCategoryName || "").trim();
    if (!categoryName) {
      showToast("error", "Le nom de catégorie est requis");
      return;
    }

    setIsCreatingEditQuickCategory(true);
    try {
      const created = await createCategoryQuick(categoryName);
      setEditingProduct((prev) =>
        prev
          ? {
              ...prev,
              category_id: String(created.id),
            }
          : prev,
      );
      setShowEditQuickCategoryModal(false);
      setEditQuickCategoryName("");
      showToast("success", "Catégorie ajoutée et sélectionnée");
    } catch (error) {
      showToast(
        "error",
        error?.message || "Impossible de créer la catégorie",
      );
    } finally {
      setIsCreatingEditQuickCategory(false);
    }
  };

  // Fonction pour rendre les étapes du modal d'édition
  const renderEditModal = () => {
    if (!isEditModalVisible) return null;

    const totalSteps = editTotalSteps;
    const modalSurfaceStyle = isDarkMode
      ? { backgroundColor: "#0b1428", color: "#f8fafc" }
      : {};
    const sectionCardClasses = isDarkMode
      ? "bg-white/5 border border-white/10"
      : "bg-white border-2 border-gray-200";
    const baseInputClasses = isDarkMode
      ? "w-full rounded-xl border border-white/15 bg-[#0b1428] px-4 py-3 text-sm text-slate-100 placeholder-white/40 focus:border-amber-400/60 focus:outline-none"
      : "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400";
    const compactInputClasses = isDarkMode
      ? "w-24 px-3 py-2 border border-white/15 rounded-lg bg-[#0b1428] text-slate-100 placeholder-white/40 focus:border-amber-400/60 focus:outline-none"
      : "w-24 p-2 border rounded-lg focus:border-orange-400 focus:ring-0 text-gray-900";
    const labelClass = isDarkMode
      ? "block text-sm font-semibold text-slate-100 mb-2"
      : "block text-sm font-semibold text-gray-800 mb-2";
    const mutedText = isDarkMode ? "text-white/60" : "text-gray-600";
    const chipButtonClass = (active) =>
      active
        ? "rounded-full px-4 py-2 text-xs font-semibold bg-amber-400 text-slate-900"
        : `rounded-full px-4 py-2 text-xs font-semibold ${
            isDarkMode
              ? "border border-white/10 text-white/70"
              : "border border-slate-200 text-slate-500"
          }`;
    const hasEditableAggregatorPricing =
      Boolean(aggregatorPricingSettings.yango) ||
      Boolean(aggregatorPricingSettings.glovo) ||
      hasExplicitCatalogPrice(editingProduct?.yango_price) ||
      hasExplicitCatalogPrice(editingProduct?.glovo_price);
    const editingAggregatorPriceFields = [
      {
        key: "yango_price",
        label: "Prix Yango",
        enabled:
          aggregatorPricingSettings.yango ||
          hasExplicitCatalogPrice(editingProduct?.yango_price) ||
          editingVariations.some((variation) =>
            hasExplicitCatalogPrice(variation?.yango_price),
          ),
      },
      {
        key: "glovo_price",
        label: "Prix Glovo",
        enabled:
          aggregatorPricingSettings.glovo ||
          hasExplicitCatalogPrice(editingProduct?.glovo_price) ||
          editingVariations.some((variation) =>
            hasExplicitCatalogPrice(variation?.glovo_price),
          ),
      },
    ].filter((field) => field.enabled);
    const renderEditingAggregatorPricing = () => {
      if (!hasEditableAggregatorPricing && editingAggregatorPriceFields.length === 0) return null;

      return (
        <div className="space-y-3">
          {!showEditingAggregatorPrices ? (
            <button
              type="button"
              onClick={() => setShowEditingAggregatorPrices(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-400/50 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-400/10"
            >
              <Plus className="h-4 w-4" />
              Ajouter un prix agrégateur
            </button>
          ) : (
            <div
              className={`rounded-2xl border p-4 ${
                isDarkMode ? "border-amber-400/20 bg-amber-400/5" : "border-amber-200 bg-amber-50/60"
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">Prix agrégateurs</p>
                  <p className={`text-xs ${mutedText}`}>
                    Prix net restaurateur, sans calcul de commission.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEditingAggregatorPrices(false)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${
                    isDarkMode ? "bg-white/10 text-white/70" : "bg-white text-slate-500"
                  }`}
                >
                  Masquer
                </button>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {editingAggregatorPriceFields.map((field) => (
                  <div key={field.key}>
                    <label className={labelClass}>{field.label} (FCFA)</label>
                    <input
                      type="number"
                      min="0"
                      step="50"
                      value={editingProduct?.[field.key] || ""}
                      onChange={(e) =>
                        setEditingProduct((prev) =>
                          prev ? { ...prev, [field.key]: e.target.value } : prev,
                        )
                      }
                      className={baseInputClasses}
                      placeholder="Optionnel"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };
    const variationCardBorder = isDarkMode ? "border-white/10" : "border-slate-200";
    const editBodyClasses = isDarkMode
      ? "flex-grow overflow-y-auto p-4 sm:p-6 bg-[#050c1b]"
      : "flex-grow overflow-y-auto p-4 sm:p-6 bg-gray-50";
    const editFooterClasses = isDarkMode
      ? "border-t border-white/10 bg-white/5"
      : "border-t border-gray-200 bg-white";
    const editStepGuide = productStepGuide;
    const editHeaderBackgroundClass = isDarkMode
      ? "bg-[#070f1f] border-b border-white/10"
      : "bg-white border-b border-gray-200";
    const renderEditHighlightCards = () => (
      <div
        className={`mb-6 grid grid-cols-1 sm:grid-cols-2 ${
          isBoutique ? "lg:grid-cols-3" : "lg:grid-cols-4"
        } gap-4`}
      >
        {editStepGuide.map((step, index) => {
          const Icon = step.icon;
          const stepNumber = index + 1;
          const isActive = editCurrentStep === stepNumber;
          const cardBase = isDarkMode
            ? "bg-white/5 border border-white/10"
            : "bg-white border border-gray-200";
          const activeCardClasses = isActive
            ? isDarkMode
              ? "border-amber-400/60 shadow-lg shadow-amber-500/20"
              : "border-orange-200 shadow-lg shadow-orange-200/60 bg-gradient-to-br from-orange-50 to-amber-50"
            : "";
          const iconWrapper = isActive
            ? isDarkMode
              ? "bg-amber-400/25 text-amber-200"
              : "bg-orange-100 text-orange-600"
            : isDarkMode
              ? "bg-white/5 text-white/40"
              : "bg-gray-100 text-gray-400";
          const labelClass = isActive
            ? isDarkMode
              ? "text-[10px] uppercase tracking-[0.35em] text-amber-300"
              : "text-[10px] uppercase tracking-[0.35em] text-orange-500"
            : isDarkMode
              ? "text-[10px] uppercase tracking-[0.35em] text-white/40"
              : "text-[10px] uppercase tracking-[0.35em] text-gray-500";
          const titleClass = isDarkMode
            ? "text-sm font-semibold text-slate-100"
            : "text-sm font-semibold text-slate-900";
          const descriptionClass = isDarkMode ? "text-xs text-white/40" : "text-xs text-gray-500";
          return (
            <div
              key={`edit-step-highlight-${step.id}`}
              className={`${cardBase} rounded-2xl p-4 flex items-center gap-3 ${activeCardClasses}`}
            >
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${iconWrapper}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <p className={labelClass}>Étape {stepNumber}</p>
                <p className={titleClass}>{step.title}</p>
                <p className={descriptionClass}>{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    );
    const renderEditHeader = () => {
      const currentStepConfig = editStepGuide[editCurrentStep - 1];
      return (
        <div className={`p-4 sm:p-6 ${editHeaderBackgroundClass}`}>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="flex-1">
                <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                  Catalogue MOO
                </p>
                <h2
                  className={`text-2xl font-semibold ${
                    isDarkMode ? "text-slate-50" : "text-slate-900"
                  }`}
                >
                  Mettre à jour un produit
                </h2>
                {currentStepConfig ? (
                  <p className={`mt-2 text-sm ${mutedText} sm:hidden`}>
                    {currentStepConfig.title} • {currentStepConfig.description}
                  </p>
                ) : null}
              </div>
            <div className="flex items-center gap-3">
              <div
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                  isDarkMode ? "bg-white/10 text-white" : "bg-orange-50 text-orange-600"
                }`}
              >
                Étape {editCurrentStep} / {totalSteps}
              </div>
              <button
                onClick={resetEditingState}
                  className={`rounded-xl border px-3 py-2 transition ${
                    isDarkMode
                      ? "border-white/15 text-white hover:bg-white/10"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="hidden sm:block">{renderEditHighlightCards()}</div>
            <div
              className={`h-2 rounded-full overflow-hidden ${
                isDarkMode ? "bg-white/10" : "bg-gray-100"
              }`}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-500"
                style={{ width: `${(editCurrentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        </div>
      );
    };

    const renderStep = () => {
      const resolvedStepId = editStepGuide[editCurrentStep - 1]?.id || 1;
      switch (resolvedStepId) {
        case 1:
          return (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Nom du produit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingProduct.name || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        name: e.target.value,
                      })
                    }
                    className={baseInputClasses}
                    placeholder="Ex: Pizza Margherita, Café expresso..."
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>SKU (optionnel)</label>
                  <input
                    type="text"
                    value={editingProduct.sku || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        sku: e.target.value,
                      })
                    }
                    className={baseInputClasses}
                    placeholder="Ex: SKU-0001"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Description</label>
                  <textarea
                    value={editingProduct.description || ""}
                    onChange={(e) =>
                      setEditingProduct({
                        ...editingProduct,
                        description: e.target.value,
                      })
                    }
                    rows="3"
                    className={`${baseInputClasses} resize-none`}
                    placeholder="Décrivez votre produit..."
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Catégorie <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <select
                      value={editingProduct.category_id || ""}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          category_id: e.target.value,
                        })
                      }
                      className={baseInputClasses}
                      required
                    >
                      <option value="">Sélectionner une catégorie...</option>
                      {safeCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowEditQuickCategoryModal(true)}
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                        isDarkMode
                          ? "border border-white/20 bg-white/5 text-slate-100 hover:bg-white/10"
                          : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      + Catégorie
                    </button>
                  </div>
                </div>

                {!isBoutique && (
                  <div>
                    <label className={labelClass}>Destination d'impression</label>
                    <select
                      value={editingProduct.destination || "cuisine"}
                      onChange={(e) =>
                        setEditingProduct({
                          ...editingProduct,
                          destination: e.target.value,
                        })
                      }
                      className={baseInputClasses}
                    >
                      {productionDestinations.map((destination) => (
                        <option key={destination.code} value={destination.code}>
                          {getProductionDestinationLabel(destination)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className={labelClass}>Image du produit</label>
                  <div className={`${sectionCardClasses} rounded-xl p-4`}>
                    <div className="flex flex-wrap gap-3 mb-4">
                      {[
                        { id: "none", label: "❌ Aucune" },
                        { id: "upload", label: "📤 Téléverser" },
                        { id: "url", label: "🔗 URL" },
                        { id: "media", label: "📁 Médiathèque" },
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                      onClick={() => {
                        if (option.id === "upload") {
                          setEditingImageUploadMode("upload");
                          document
                            .getElementById("edit-image-upload")
                            .click();
                          return;
                        }
                        if (option.id === "media") {
                          setEditingImageUploadMode("media");
                          setShowEditingMediaModal(true);
                          return;
                            }
                            if (option.id === "url") {
                              setEditingImageUploadMode("url");
                              setEditingSelectedImageFile(null);
                              return;
                            }
                            if (option.id === "none") {
                              setEditingImagePreview(null);
                              setEditingImageUploadMode("none");
                              setEditingSelectedImageFile(null);
                              setEditingSelectedMediaImage(null);
                            }
                          }}
                          className={`rounded-full px-4 py-1.5 text-xs font-semibold ${
                            editingImageUploadMode === option.id
                              ? "bg-amber-400 text-slate-900"
                              : chipButtonClass(false)
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="file"
                      id="edit-image-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={handleEditingImageFileChange}
                    />
                    {editingImageUploadMode === "url" && (
                      <input
                        type="text"
                        value={editingImagePreview || ""}
                        onChange={handleEditingImageUrlChange}
                        className={baseInputClasses}
                        placeholder="https://exemple.com/image.jpg"
                      />
                    )}
                    {editingImagePreview && (
                      <div className="mt-4 text-center space-y-2">
                        <img
                          src={editingImagePreview}
                          alt="Aperçu"
                          className={`h-32 w-32 object-cover rounded-xl mx-auto border ${
                            isDarkMode ? "border-white/10" : "border-slate-200"
                          }`}
                        />
                        <p className={`text-sm ${mutedText}`}>
                          Aperçu de l'image actuelle
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        case 2:
          return (
            <div className="space-y-6">
              <div className={`${sectionCardClasses} rounded-xl p-6`}>
                <label className={labelClass}>Type de produit</label>
                <div className="flex gap-4">
                  {[
                    { id: "simple", label: "📦 Simple" },
                    { id: "variable", label: "🎯 Variable" },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleEditProductTypeChange(option.id)}
                      className={`flex-1 rounded-xl px-6 py-3 text-sm font-semibold transition ${
                        editProductType === option.id
                          ? "bg-amber-400 text-slate-900 shadow-lg"
                          : chipButtonClass(false)
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {editProductType === "simple" ? (
                <div className={`${sectionCardClasses} rounded-xl p-6 space-y-4`}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>
                        Prix de vente (FCFA) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="50"
                        value={editingProduct.price ?? ""}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            price: e.target.value,
                          })
                        }
                        className={baseInputClasses}
                      />
                    </div>
                  </div>
                  {renderEditingAggregatorPricing()}

                  {Boolean(editingProduct.track_stock) && (
                    <div
                      className={`rounded-2xl border p-4 ${
                        isDarkMode
                          ? "border-white/10 bg-white/5"
                          : "border-amber-200 bg-amber-50/60"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-slate-100" : "text-slate-800"
                        }`}
                      >
                        Nature du produit suivi
                      </p>
                      <p className={`text-xs mt-1 ${mutedText}`}>
                        Précisez si cet article est revendu tel quel ou s'il est produit via une recette.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {[
                          { id: "resold", label: "Produit revendu" },
                          { id: "composed", label: "Produit composé" },
                        ].map((opt) => (
                          <label
                            key={opt.id}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 border cursor-pointer ${
                              editingProduct.inventory_kind === opt.id
                                ? "border-amber-400 bg-amber-400/20"
                                : isDarkMode
                                  ? "border-white/10 bg-white/5"
                                  : "border-slate-200 bg-white"
                            }`}
                          >
                            <input
                              type="radio"
                              name="edit_inventory_kind"
                              value={opt.id}
                              checked={editingProduct.inventory_kind === opt.id}
                              onChange={(e) =>
                                setEditingProduct({
                                  ...editingProduct,
                                  inventory_kind: e.target.value,
                                })
                              }
                              className="h-4 w-4"
                            />
                            <span className="text-sm font-semibold">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {editingProduct.inventory_kind === "resold" && (
                        <label className="mt-3 flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!editingProduct.is_recipe_ingredient}
                            onChange={(e) =>
                              setEditingProduct({
                                ...editingProduct,
                                is_recipe_ingredient: e.target.checked,
                              })
                            }
                            className="h-4 w-4"
                          />
                          <span className="text-sm font-semibold">
                            Utilisable comme ingrédient de recette
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div className={`${sectionCardClasses} rounded-xl p-6 space-y-4`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold">Attributs</h4>
                        <p className={`text-sm ${mutedText}`}>
                          Définissez les caractéristiques (taille, parfums, portions...) qui
                          serviront à générer vos déclinaisons.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={addEditingAttribute}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg hover:from-orange-600 hover:to-amber-600"
                        >
                          <Plus className="w-4 h-4" /> Ajouter un attribut
                        </button>
                        {editingAttributes.length > 0 && (
                          <button
                            type="button"
                            onClick={generateEditingVariations}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow hover:from-blue-600 hover:to-indigo-600"
                          >
                            <RefreshCw className="w-4 h-4" /> Générer les déclinaisons
                          </button>
                        )}
                      </div>
                    </div>
                    {editingAttributes.length === 0 ? (
                      <p className={`text-sm ${mutedText}`}>
                        Aucun attribut défini pour le moment.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {editingAttributes.map((attr, index) => (
                          <div
                            key={`edit-attr-${index}`}
                            className={`rounded-2xl p-4 space-y-3 ${
                              isDarkMode
                                ? "bg-white/5 border border-white/10"
                                : "bg-orange-50/60 border border-orange-100"
                            }`}
                          >
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div className="flex-1">
                                <label className={labelClass}>Nom de l'attribut</label>
                                <input
                                  type="text"
                                  value={attr.name}
                                  onChange={(e) =>
                                    handleEditingAttributeChange(
                                      index,
                                      "name",
                                      e.target.value,
                                    )
                                  }
                                  className={baseInputClasses}
                                  placeholder="Ex: Taille, Goût..."
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeEditingAttribute(index)}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-rose-500 hover:text-rose-300"
                              >
                                <Trash2 size={14} /> Supprimer
                              </button>
                            </div>
                            <div className="space-y-2">
                              <label className={labelClass}>Valeurs</label>
                              {Array.isArray(attr.values) && attr.values.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {attr.values.map((value, valueIndex) => (
                                    <span
                                      key={`${value}-${valueIndex}`}
                                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                        isDarkMode
                                          ? "bg-white/10 text-white"
                                          : "bg-white text-slate-700"
                                      }`}
                                    >
                                      {value}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeEditingAttributeValue(index, valueIndex)
                                        }
                                        className="text-white/60 hover:text-white"
                                      >
                                        <X size={12} />
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className={`text-xs ${mutedText}`}>
                                  Ajoutez des valeurs pour cet attribut.
                                </p>
                              )}
                              <div className="flex flex-col gap-2 md:flex-row">
                                <input
                                  type="text"
                                  value={attr.newValue || ""}
                                  onChange={(e) =>
                                    handleEditingAttributeChange(
                                      index,
                                      "newValue",
                                      e.target.value,
                                    )
                                  }
                                  className={baseInputClasses}
                                  placeholder="Nouvelle valeur"
                                />
                                <button
                                  type="button"
                                  onClick={() => addEditingAttributeValue(index)}
                                  className="md:w-auto w-full px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow"
                                >
                                  Ajouter
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className={`${sectionCardClasses} rounded-xl p-6 space-y-4`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-semibold">Déclinaisons</h4>
                        <p className={`text-sm ${mutedText}`}>
                          Ajustez les prix et le statut pour chaque combinaison
                          générée.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={generateEditingVariations}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow hover:from-blue-600 hover:to-indigo-600"
                      >
                        <RefreshCw className="w-4 h-4" /> Générer les déclinaisons
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <label className={`flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                        <input
                          type="checkbox"
                          checked={editingUseGlobalPrice}
                          onChange={(e) => setEditingUseGlobalPrice(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        />
                        Prix global (une seule valeur)
                      </label>
                      <label className={`flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                        <input
                          type="checkbox"
                          checked={editingUseGlobalStock}
                          onChange={(e) => {
                            setEditingUseGlobalStock(e.target.checked);
                            if (e.target.checked) {
                              setEditingUseVariationStock(false);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        />
                        Suivi stock global
                      </label>
                      <label className={`flex items-center gap-2 text-sm font-semibold ${mutedText}`}>
                        <input
                          type="checkbox"
                          checked={editingUseVariationStock}
                          onChange={(e) => {
                            setEditingUseVariationStock(e.target.checked);
                            if (e.target.checked) {
                              setEditingUseGlobalStock(false);
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                        />
                        Suivi stock par déclinaison
                      </label>
                    </div>

                    {(editingUseGlobalStock || editingUseVariationStock) && (
                      <div
                        className={`rounded-2xl border p-4 ${
                          isDarkMode
                            ? "border-white/10 bg-white/5"
                            : "border-amber-200 bg-amber-50/60"
                        }`}
                      >
                        <p
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-slate-100" : "text-slate-800"
                          }`}
                        >
                          Nature du produit suivi
                        </p>
                        <p className={`text-xs mt-1 ${mutedText}`}>
                          Les variations héritent de ce choix.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-3">
                          {[
                            { id: "resold", label: "Produit revendu" },
                            { id: "composed", label: "Produit composé" },
                          ].map((opt) => (
                            <label
                              key={opt.id}
                              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 border cursor-pointer ${
                                editingProduct.inventory_kind === opt.id
                                  ? "border-amber-400 bg-amber-400/20"
                                  : isDarkMode
                                    ? "border-white/10 bg-white/5"
                                    : "border-slate-200 bg-white"
                              }`}
                            >
                              <input
                                type="radio"
                                name="edit_inventory_kind"
                                value={opt.id}
                                checked={editingProduct.inventory_kind === opt.id}
                                onChange={(e) =>
                                  setEditingProduct({
                                    ...editingProduct,
                                    inventory_kind: e.target.value,
                                  })
                                }
                                className="h-4 w-4"
                              />
                              <span className="text-sm font-semibold">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                        {editingProduct.inventory_kind === "resold" && (
                          <label className="mt-3 flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!editingProduct.is_recipe_ingredient}
                              onChange={(e) =>
                                setEditingProduct({
                                  ...editingProduct,
                                  is_recipe_ingredient: e.target.checked,
                                })
                              }
                              className="h-4 w-4"
                            />
                            <span className="text-sm font-semibold">
                              Utilisable comme ingrédient de recette
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                    {editingUseGlobalPrice && (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className={labelClass}>Prix de vente global (FCFA)</label>
                          <input
                            type="number"
                            min="0"
                            step="50"
                            value={editingProduct.price ?? ""}
                            onChange={(e) =>
                              setEditingProduct({
                                ...editingProduct,
                                price: e.target.value,
                              })
                            }
                            className={baseInputClasses}
                          />
                        </div>
                      </div>
                    )}
                    {renderEditingAggregatorPricing()}

                    {editingVariations.length === 0 ? (
                      <p className={mutedText}>
                        Ajoutez des attributs puis cliquez sur « Générer » pour créer
                        vos variations.
                      </p>
                    ) : (
                      editingVariations.map((variation, index) => {
                        const key = getEditingVariationKey(variation, index);
                        const label = Object.entries(variation.attributes || {})
                          .map(([name, value]) => `${name}: ${value}`)
                          .join(" • ");
                        return (
                          <div
                            key={key}
                            className={`rounded-2xl border ${variationCardBorder} p-4 space-y-3`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`text-xs ${mutedText}`}>Variation</p>
                                <p className="font-semibold">
                                  {label || `Variation ${index + 1}`}
                                </p>
                              </div>
                              <label className="flex items-center gap-2 text-xs font-semibold">
                                <input
                                  type="checkbox"
                                  checked={variation.is_active !== false}
                                  onChange={(e) =>
                                    updateEditingVariation(
                                      index,
                                      "is_active",
                                      e.target.checked,
                                    )
                                  }
                                  className="h-4 w-4 rounded border-white/20 text-emerald-400 focus:ring-emerald-400/40"
                                />
                                Actif
                              </label>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              {!editingUseGlobalPrice && (
                                <div>
                                  <label className={labelClass}>
                                    Prix vente (FCFA)
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="50"
                                    value={variation.price}
                                    onChange={(e) =>
                                      updateEditingVariation(
                                        index,
                                        "price",
                                        e.target.value,
                                      )
                                    }
                                    className={baseInputClasses}
                                  />
                                </div>
                              )}
                              <div>
                                <label className={labelClass}>SKU (optionnel)</label>
                                <input
                                  type="text"
                                  value={variation.sku || ""}
                                  onChange={(e) =>
                                    updateEditingVariation(
                                      index,
                                      "sku",
                                      e.target.value,
                                    )
                                  }
                                  className={baseInputClasses}
                                  placeholder="Ex: SKU-0001"
                                />
                              </div>
                              {showEditingAggregatorPrices &&
                                editingAggregatorPriceFields.map((field) => (
                                  <div key={`${key}-${field.key}`}>
                                    <label className={labelClass}>
                                      {field.label} variation (FCFA)
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="50"
                                      value={variation[field.key] || ""}
                                      onChange={(e) =>
                                        updateEditingVariation(
                                          index,
                                          field.key,
                                          e.target.value,
                                        )
                                      }
                                      className={baseInputClasses}
                                      placeholder="Optionnel"
                                    />
                                  </div>
                                ))}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        case 3: {
          const editAccompaniments = editingSelectedExtras.filter(
            (e) => e.type === "accompaniment",
          );
          const editSupplements = editingSelectedExtras.filter(
            (e) => e.type === "supplement",
          );
          const unselectedExtras = availableExtras.filter(
            (extra) =>
              !editingSelectedExtras.find((selected) => selected.id === extra.id),
          );

          return (
            <div className="space-y-6">
              {editProductType === "variable" && (
                <div className="flex flex-wrap gap-3">
                  {[{ id: "global", label: "Extras globaux" }, { id: "perVariation", label: "Par variation" }].map(
                    (option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleEditingExtraScopeChange(option.id)}
                        className={chipButtonClass(editingExtraScope === option.id)}
                      >
                        {option.label}
                      </button>
                    ),
                  )}
                </div>
              )}

              {editingExtraScope === "perVariation" && editProductType === "variable" ? (
                editingVariations.length === 0 ? (
                  <p className={mutedText}>
                    Ajoutez d'abord des déclinaisons pour définir leurs extras.
                  </p>
                ) : (
                  editingVariations.map((variation, index) => {
                    const key = getEditingVariationKey(variation, index);
                    const extrasForVariation = editingVariationExtras[key] || [];
                    const label = Object.values(variation.attributes || {}).join(" • ");
                    return (
                      <div
                        key={key}
                        className={`${sectionCardClasses} rounded-2xl p-5 space-y-3`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-xs ${mutedText}`}>Variation</p>
                            <p className="font-semibold">
                              {label || `Variation ${index + 1}`}
                            </p>
                          </div>
                          <label className={`flex items-center gap-2 text-xs ${mutedText}`}>
                            Gratuits / article
                            <input
                              type="number"
                              min="0"
                              placeholder="Global"
                              value={variation.free_accompaniment_quota ?? ""}
                              onChange={(event) =>
                                updateEditingVariation(
                                  index,
                                  "free_accompaniment_quota",
                                  event.target.value === ""
                                    ? null
                                    : Math.max(0, parseInt(event.target.value, 10) || 0),
                                )
                              }
                              className={`${compactInputClasses} w-20`}
                            />
                          </label>
                        </div>
                        {extrasForVariation.length === 0 ? (
                          <p className={`text-sm ${mutedText}`}>
                            Aucun extra pour cette variation.
                          </p>
                        ) : (
                          extrasForVariation.map((extra) => (
                            <div
                              key={`${key}-${extra.id}`}
                              className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center justify-between text-sm"
                            >
                              <div>
                                <p className="font-semibold">{extra.name}</p>
                                <p className={`text-xs ${mutedText}`}>
                                  {extra.type === "accompaniment"
                                    ? "Accompagnement"
                                    : "Supplément"}
                                </p>
                              </div>
                              <div className="flex items-center gap-3">
                                {extra.type === "supplement" && (
                                  <input
                                    type="number"
                                    min="0"
                                    value={extra.price}
                                    onChange={(e) =>
                                      updateEditingVariationExtra(
                                        key,
                                        extra.id,
                                        "price",
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    className={compactInputClasses}
                                  />
                                )}
                                <input
                                  type="number"
                                  min="1"
                                  value={extra.max_quantity}
                                  onChange={(e) =>
                                    updateEditingVariationExtra(
                                      key,
                                      extra.id,
                                      "max_quantity",
                                      parseInt(e.target.value, 10) || 1,
                                    )
                                  }
                                  className={`${compactInputClasses} w-16`}
                                />
                                <button
                                  onClick={() => removeEditingVariationExtra(key, extra.id)}
                                  className="text-rose-400 hover:text-rose-200"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                        <div className="grid gap-3 md:grid-cols-2">
                          {["accompaniment", "supplement"].map((type) => (
                            <div key={`${key}-${type}`}>
                              <p className="text-xs uppercase tracking-[0.3em] mb-2">
                                {type === "accompaniment"
                                  ? "Accompagnements"
                                  : "Suppléments"}
                              </p>
                              <div className="max-h-32 overflow-y-auto space-y-2 rounded-xl border border-white/10 p-2">
                                {availableExtras.map((extra) => (
                                  <button
                                    key={`${key}-${type}-${extra.id}`}
                                    type="button"
                                    onClick={() =>
                                      addEditingVariationExtra(key, extra, type)
                                    }
                                    className="flex w-full items-center justify-between rounded-lg px-3 py-1 text-xs hover:bg-white/10"
                                  >
                                    <span>{extra.name}</span>
                                    <span className={mutedText}>Ajouter</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )
              ) : (
                <div className={`${sectionCardClasses} rounded-xl p-6 space-y-4`}>
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-md font-semibold">Accompagnements</h4>
                    <label className={`flex items-center gap-2 text-xs ${mutedText}`}>
                      Gratuits / article
                      <input
                        type="number"
                        min="0"
                        value={editingProduct?.free_accompaniment_quota ?? 1}
                        onChange={(event) =>
                          setEditingProduct((previous) => ({
                            ...previous,
                            free_accompaniment_quota: Math.max(
                              0,
                              parseInt(event.target.value, 10) || 0,
                            ),
                          }))
                        }
                        className={`${compactInputClasses} w-16`}
                      />
                    </label>
                  </div>
                  <div>
                    {editAccompaniments.length === 0 ? (
                      <p className={`text-sm ${mutedText}`}>
                        Aucun accompagnement sélectionné.
                      </p>
                    ) : (
                      editAccompaniments.map((extra) => (
                        <div
                          key={extra.id}
                          className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-3"
                        >
                          <span>{extra.name}</span>
                          <div className="flex items-center gap-3">
                            <label className={`text-xs ${mutedText}`}>
                              Qté max
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={extra.max_quantity}
                              onChange={(e) =>
                                updateEditingExtra(
                                  extra.id,
                                  "max_quantity",
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              className={`${compactInputClasses} w-16`}
                            />
                            <button
                              onClick={() => removeEditingExtra(extra.id)}
                              className="text-rose-400 hover:text-rose-200"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div>
                    <h4 className="text-md font-semibold">Suppléments</h4>
                    {editSupplements.length === 0 ? (
                      <p className={`text-sm ${mutedText}`}>
                        Aucun supplément sélectionné.
                      </p>
                    ) : (
                      editSupplements.map((extra) => (
                        <div
                          key={extra.id}
                          className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-3"
                        >
                          <span>{extra.name}</span>
                          <div className="flex items-center gap-3">
                            <input
                              type="number"
                              min="0"
                              step="50"
                              value={extra.price}
                              onChange={(e) =>
                                updateEditingExtra(
                                  extra.id,
                                  "price",
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              className={`${compactInputClasses} w-24`}
                            />
                            <input
                              type="number"
                              min="1"
                              value={extra.max_quantity}
                              onChange={(e) =>
                                updateEditingExtra(
                                  extra.id,
                                  "max_quantity",
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              className={`${compactInputClasses} w-16`}
                            />
                            <button
                              onClick={() => removeEditingExtra(extra.id)}
                              className="text-rose-400 hover:text-rose-200"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div>
                    <h4 className="text-md font-semibold">Ajouter un extra</h4>
                    {unselectedExtras.length === 0 ? (
                      <p className={`text-sm ${mutedText}`}>
                        Tous les extras disponibles ont été ajoutés.
                      </p>
                    ) : (
                      <div className="grid gap-3 md:grid-cols-2">
                        {unselectedExtras.map((extra) => (
                          <div
                            key={extra.id}
                            className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2 text-sm"
                          >
                            <span>{extra.name}</span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => addEditingExtra(extra, "accompaniment")}
                                className="rounded-full bg-blue-500/80 px-2 py-1 text-xs text-white"
                              >
                                + Accomp.
                              </button>
                              <button
                                type="button"
                                onClick={() => addEditingExtra(extra, "supplement")}
                                className="rounded-full bg-emerald-500/80 px-2 py-1 text-xs text-white"
                              >
                                + Suppl.
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        }
        case 4:
          return (
            <div className="space-y-6">
              <div className={`${sectionCardClasses} rounded-2xl p-6 space-y-4`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                      Nom
                    </p>
                    <p className="text-lg font-semibold">{editingProduct.name || "-"}</p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                      Catégorie
                    </p>
                    <p className="text-lg font-semibold">
                      {safeCategories.find((c) => c.id === editingProduct.category_id)?.name ||
                        "-"}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                      Type
                    </p>
                    <p className="text-lg font-semibold">
                      {editingProduct.product_type === "variable" ? "Variable" : "Simple"}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                      Prix
                    </p>
                    <p className="text-lg font-semibold">
                  {editingProduct.product_type === "variable"
                    ? "Selon variation"
                    : `${editingProduct.price || 0} CFA`}
                    </p>
                  </div>
                </div>

                {editingProduct.product_type === "variable" &&
                  editingVariations.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">Déclinaisons</h4>
                      {editingVariations.map((variation, index) => (
                        <div
                          key={getEditingVariationKey(variation, index)}
                          className={`rounded-xl px-4 py-3 text-sm ${
                            isDarkMode ? "bg-white/5" : "bg-gray-50"
                          }`}
                        >
                          <p className="font-semibold">
                            {Object.values(variation.attributes || {}).join(" / ")}
                          </p>
                          <p className={`text-xs ${mutedText}`}>
                          Prix: {variation.price || 0} CFA
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                {editingProduct.product_type === "variable" &&
                editingExtraScope === "perVariation" ? (
                  <div className="space-y-1">
                    <h4 className="font-semibold">Extras par variation</h4>
                    {editingVariations.map((variation, index) => {
                      const key = getEditingVariationKey(variation, index);
                      const extrasForVariation = editingVariationExtras[key] || [];
                      return (
                        <p key={`${key}-summary`} className={`text-sm ${mutedText}`}>
                          {Object.values(variation.attributes || {}).join(" / ")} :{" "}
                          {extrasForVariation.length} extra(s)
                        </p>
                      );
                    })}
                  </div>
                ) : (
                  editingSelectedExtras.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">Extras globaux</h4>
                      <div className="flex flex-wrap gap-2">
                        {editingSelectedExtras.map((extra) => (
                          <span
                            key={`summary-${extra.id}`}
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              isDarkMode
                                ? "bg-white/10 text-white"
                                : "bg-orange-50 text-orange-700"
                            }`}
                          >
                            {extra.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          );
        default:
          return null;
      }
    };

    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-0 sm:p-4 backdrop-blur-sm sm:items-center">
        <div
          ref={editModalRef}
          onFocusCapture={(event) => {
            if (editModalRef.current?.contains(event.target)) {
              lastEditFocusRef.current = event.target;
            }
          }}
          className="shadow-2xl w-full max-w-4xl h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[95vh] rounded-none sm:rounded-2xl flex flex-col overflow-hidden"
          style={modalSurfaceStyle}
        >
          {renderEditHeader()}

          <div className={editBodyClasses}>
            <form onSubmit={handleUpdateProduct}>{renderStep()}</form>
          </div>

          <div className={`${editFooterClasses} sticky bottom-0 p-4 sm:p-6`}>
            <div className="flex justify-between items-center">
              <button
                type="button"
                onClick={handleEditBack}
                disabled={editCurrentStep === 1}
                className={`px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 ${
                  isDarkMode
                    ? "bg-white/5 text-slate-100 hover:bg-white/10"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                ← Précédent
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: editTotalSteps }, (_, index) => index + 1).map((step) => (
                  <div
                    key={step}
                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                      step === editCurrentStep
                        ? "bg-orange-500 scale-125"
                        : step < editCurrentStep
                          ? "bg-green-500"
                          : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>

              <div>
                {editCurrentStep < editTotalSteps && (
                  <button
                    type="button"
                    onClick={handleEditNext}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                  >
                    Suivant →
                  </button>
                )}
                {editCurrentStep === editTotalSteps && (
                  <button
                    type="submit"
                    onClick={handleUpdateProduct}
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Mise à jour...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Sauvegarder
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {showEditQuickCategoryModal && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-4"
            role="presentation"
            onClick={() => {
              if (isCreatingEditQuickCategory) return;
              setShowEditQuickCategoryModal(false);
            }}
          >
            <div
              className={`${cardClasses} w-full max-w-md rounded-2xl p-5`}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
                Catégories
              </p>
              <h3 className="mt-1 text-lg font-semibold">
                Ajouter une catégorie
              </h3>
              <p className={`mt-2 text-sm ${mutedText}`}>
                Créez une catégorie rapidement sans quitter l&apos;édition.
              </p>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={editQuickCategoryName}
                  onChange={(e) => setEditQuickCategoryName(e.target.value)}
                  className={baseInputClasses}
                  placeholder="Ex: Grillades, Cocktails..."
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditQuickCategoryModal(false)}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                      isDarkMode
                        ? "border border-white/20 text-slate-100 hover:bg-white/10"
                        : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateCategoryFromEditModal}
                    disabled={isCreatingEditQuickCategory}
                    className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
                  >
                    {isCreatingEditQuickCategory ? "Création..." : "Créer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>,
      document.body,
    );
  };

  // Charger les extras disponibles
  const loadAvailableExtras = async () => {
    try {
      const response = await fetch("/api/extras");
      if (response.ok) {
        const data = await response.json();
        setAvailableExtras(data);
        return data;
      }
    } catch (error) {
      console.error("❌ Erreur chargement extras:", error);
    }
    return [];
  };

  // Effet pour charger les extras au montage
  useEffect(() => {
    loadAvailableExtras();
  }, []);

  if (loading) {
    return (
      <div className={`min-h-full flex items-center justify-center ${containerBg}`}>
        <div className="text-center space-y-4">
          <div className="mx-auto h-14 w-14 rounded-full border-4 border-amber-400 border-b-transparent animate-spin" />
          <p className={`text-sm font-semibold tracking-[0.3em] uppercase ${mutedText}`}>
            Chargement du catalogue...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-full px-4 py-6 pb-[calc(env(safe-area-inset-bottom)+10rem)] lg:pb-6 ${containerBg}`}>
      <div className="mx-auto max-w-7xl space-y-6">
        <header className={`${cardClasses} rounded-3xl p-4 sm:p-6 space-y-5 sm:space-y-6`}>
          <div className="sm:hidden space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-amber-400/20 p-2 text-amber-300">
                  <Package size={22} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400">
                    Catalogue MOO
                  </p>
                  <h1 className="text-lg font-semibold">
                    Produits
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadProducts}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 text-amber-300 hover:border-amber-400/60"
                  aria-label="Actualiser"
                  title="Actualiser"
                >
                  <RefreshCw size={16} />
                </button>
                {activeTab === "products" && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 text-slate-900 shadow-lg shadow-emerald-500/30"
                    aria-label="Nouveau produit"
                    title="Nouveau produit"
                  >
                    <Plus size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {statCards.map((stat) => (
                <div
                  key={stat.label}
                  className={`min-w-[140px] rounded-2xl border ${dividerBorder} px-3 py-2 ${isDarkMode ? "bg-white/5" : "bg-white/80"}`}
                >
                  <p className={`text-[10px] uppercase tracking-[0.3em] ${mutedText}`}>
                    {stat.label}
                  </p>
                  <p className="text-lg font-semibold mt-1">{stat.value}</p>
                </div>
              ))}
            </div>
            <div className="flex w-full gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-1">
              {navigationTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold transition ${
                      isActive
                        ? "bg-amber-400 text-slate-900 shadow-lg"
                        : isDarkMode
                          ? "text-slate-300"
                          : "text-slate-500"
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`text-[10px] font-semibold ${
                        isActive ? "text-slate-900/70" : mutedText
                      }`}
                    >
                      {tab.id === "products"
                        ? filteredProducts.length
                        : availableExtras.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hidden sm:block space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="rounded-2xl bg-amber-400/20 p-2 sm:p-3 text-amber-300">
                  <Package size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-amber-400">
                    Catalogue MOO
                  </p>
                  <h1 className="text-xl sm:text-3xl font-semibold tracking-tight">
                    {isBoutique ? "Catalogue produits" : "Produits & déclinaisons"}
                  </h1>
                  <p className={`text-sm ${mutedText}`}>
                    {isBoutique
                      ? "Gérez votre catalogue, vos SKU et vos variations."
                      : "Pilotez recettes, extras et variations pour toute votre équipe."}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {activeTab === "products" && (
                  <input
                    ref={importInputRef}
                    type="file"
                    accept=".json,.xls,.xlsx,.csv"
                    className="hidden"
                    onChange={handleImportFile}
                  />
                )}
                <button
                  onClick={loadProducts}
                  className="w-full sm:w-auto rounded-2xl border border-white/10 px-4 py-2 text-sm font-semibold text-amber-300 hover:border-amber-400/60 flex items-center justify-center gap-2"
                >
                  <RefreshCw size={16} />
                  Actualiser
                </button>
                {activeTab === "products" && (
                  <>
                    <button
                      onClick={handleImportClick}
                      disabled={isImporting}
                      className={`w-full sm:w-auto rounded-2xl border border-white/10 px-5 py-2 text-sm font-semibold text-white/90 shadow-lg flex items-center justify-center gap-2 ${
                        isImporting
                          ? "cursor-not-allowed opacity-60"
                          : "hover:border-amber-400/60"
                      }`}
                    >
                      <Upload size={16} />
                      {isImporting ? "Import..." : "Importer"}
                    </button>
                    <button
                      onClick={handleExportProducts}
                      disabled={isExporting}
                      className="w-full sm:w-auto rounded-2xl border border-white/10 px-5 py-2 text-sm font-semibold text-white/90 hover:border-amber-400/60 flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      <Download size={16} />
                      {isExporting ? "Export..." : "Exporter"}
                    </button>
                    <a
                      href="/samples/products-import-sample.xls"
                      download="products-import-sample.xls"
                      className="w-full sm:w-auto rounded-2xl border border-white/10 px-5 py-2 text-sm font-semibold text-white/80 hover:border-amber-400/60 flex items-center justify-center gap-2"
                    >
                      <Download size={16} />
                      Modele XLS
                    </a>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="w-full sm:w-auto rounded-2xl bg-gradient-to-r from-emerald-400 to-lime-400 px-5 py-2 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
                    >
                      <Plus size={16} />
                      Nouveau produit
                    </button>
                  </>
                )}
              </div>
            </div>
          <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {statCards.map((stat) => (
              <div
                key={stat.label}
                className={`min-w-[160px] rounded-2xl border ${dividerBorder} px-4 py-3 ${isDarkMode ? "bg-white/5" : "bg-white/80"}`}
              >
                  <p className={`text-xs uppercase tracking-[0.3em] ${mutedText}`}>
                    {stat.label}
                  </p>
                  <p className="text-2xl font-semibold mt-2">{stat.value}</p>
                </div>
              ))}
            </div>
            <div className="flex w-full gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-1 sm:flex-wrap">
              {navigationTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? "bg-amber-400 text-slate-900 shadow-lg"
                        : isDarkMode
                          ? "text-slate-300"
                          : "text-slate-500"
                    }`}
                  >
                    <span>{tab.label}</span>
                    <span
                      className={`text-xs font-semibold ${
                        isActive ? "text-slate-900/70" : mutedText
                      }`}
                    >
                      {tab.id === "products"
                        ? filteredProducts.length
                        : availableExtras.length}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        {activeTab === "products" && (
          <section className={`${cardClasses} rounded-3xl p-4 sm:p-6 space-y-4 sm:space-y-5`}>
            <div className="sm:hidden space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={
                      isBoutique
                        ? "Rechercher un produit, SKU..."
                        : "Rechercher..."
                    }
                    className={`w-full rounded-2xl border ${dividerBorder} bg-white/5 px-9 py-2.5 text-sm focus:border-amber-400/60 focus:outline-none ${
                      isDarkMode ? "text-slate-100 placeholder-white/40" : "bg-white text-slate-900"
                    }`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200"
                >
                  <Filter size={14} />
                  Filtrer{activeProductFiltersCount > 0 ? ` (${activeProductFiltersCount})` : ""}
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 text-[11px] text-slate-200/80">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                >
                  Tri: {selectedSortLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                >
                  Vue: {selectedViewLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                >
                  Dispo: {selectedAvailabilityLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-1.5"
                >
                  Cat: {selectedCategoryLabel}
                </button>
              </div>
            </div>

            <div className="hidden sm:block space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={
                      isBoutique
                        ? "Rechercher un produit, un SKU ou un code..."
                        : "Rechercher un plat, une référence, un code..."
                    }
                    className={`w-full rounded-2xl border ${dividerBorder} bg-white/5 px-11 py-3 text-sm focus:border-amber-400/60 focus:outline-none ${
                      isDarkMode ? "text-slate-100 placeholder-white/40" : "bg-white text-slate-900"
                    }`}
                  />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`w-full sm:w-auto rounded-2xl border ${dividerBorder} bg-white/5 px-4 py-2 text-sm ${
                      isDarkMode ? "text-slate-100" : "bg-white text-slate-900"
                    }`}
                  >
                    <option value="name">Nom (A-Z)</option>
                    <option value="price">Prix (desc)</option>
                    <option value="popularity">Popularité</option>
                    <option value="sales">Ventes</option>
                  </select>
                  <div className={`flex w-full sm:w-auto rounded-2xl border ${dividerBorder} p-1`}>
                    {[
                      { id: "grid", label: "Grille" },
                      { id: "list", label: "Liste" },
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id)}
                        className={`flex-1 px-4 py-2 rounded-2xl text-sm font-semibold transition sm:flex-none ${
                          viewMode === mode.id
                            ? "bg-amber-400 text-slate-900 shadow-lg"
                            : isDarkMode
                              ? "text-slate-300"
                              : "text-slate-500"
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex w-full gap-2 overflow-x-auto pb-1 sm:flex-wrap">
                {availabilityFilters.map((filter) => {
                  const isActive = filterBy === filter.id;
                  return (
                    <button
                      key={filter.id}
                      onClick={() => setFilterBy(filter.id)}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                        isActive
                          ? "bg-amber-400 text-slate-900"
                          : `border ${dividerBorder} ${mutedText}`
                      }`}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex w-full gap-2 overflow-x-auto pb-1 sm:flex-wrap">
                {[{ id: "all", name: "Toutes les catégories" }, ...safeCategories].map(
                  (category) => {
                    const value =
                      category.id === "all" ? "all" : String(category.id);
                    const isActive = selectedCategory === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setSelectedCategory(value)}
                        className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition ${
                          isActive
                            ? "bg-emerald-400 text-slate-900"
                            : `border ${dividerBorder} ${mutedText}`
                        }`}
                      >
                        {category.name}
                      </button>
                    );
                  },
                )}
              </div>
            </div>
          </section>
        )}

        {mobileFiltersOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:hidden"
            role="presentation"
            onClick={() => setMobileFiltersOpen(false)}
          >
            <div
              className={`${cardClasses} w-full max-w-xl rounded-3xl p-5 max-h-[85vh] overflow-y-auto`}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                    Filtres
                  </p>
                  <h3 className="text-lg font-semibold">Affiner le catalogue</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-slate-200"
                >
                  Fermer
                </button>
              </div>

              <div className="mt-4 space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Tri
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className={`w-full rounded-2xl border ${dividerBorder} bg-white/5 px-4 py-2.5 text-sm ${
                      isDarkMode ? "text-slate-100" : "bg-white text-slate-900"
                    }`}
                  >
                    <option value="name">Nom (A-Z)</option>
                    <option value="price">Prix (desc)</option>
                    <option value="popularity">Popularité</option>
                    <option value="sales">Ventes</option>
                  </select>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Affichage
                  </label>
                  <div className={`flex w-full rounded-2xl border ${dividerBorder} p-1`}>
                    {[
                      { id: "grid", label: "Grille" },
                      { id: "list", label: "Liste" },
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id)}
                        className={`flex-1 px-4 py-2 rounded-2xl text-sm font-semibold transition ${
                          viewMode === mode.id
                            ? "bg-amber-400 text-slate-900 shadow-lg"
                            : isDarkMode
                              ? "text-slate-300"
                              : "text-slate-500"
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Disponibilité
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availabilityFilters.map((filter) => {
                      const isActive = filterBy === filter.id;
                      return (
                        <button
                          key={filter.id}
                          onClick={() => setFilterBy(filter.id)}
                          className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                            isActive
                              ? "bg-amber-400 text-slate-900"
                              : `border ${dividerBorder} ${mutedText}`
                          }`}
                        >
                          {filter.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-semibold tracking-wide text-slate-400">
                    Catégories
                  </label>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                    {[{ id: "all", name: "Toutes les catégories" }, ...safeCategories].map(
                      (category) => {
                        const value =
                          category.id === "all" ? "all" : String(category.id);
                        const isActive = selectedCategory === value;
                        return (
                          <button
                            key={value}
                            onClick={() => setSelectedCategory(value)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                              isActive
                                ? "bg-emerald-400 text-slate-900"
                                : `border ${dividerBorder} ${mutedText}`
                            }`}
                          >
                            {category.name}
                          </button>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between gap-3">
                <button
                  onClick={resetProductFilters}
                  className="inline-flex items-center gap-2 rounded-2xl border border-current px-4 py-2 text-xs font-semibold text-yellow-500 transition hover:bg-yellow-500 hover:text-white"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="rounded-2xl bg-white/10 px-4 py-2 text-xs font-semibold text-white/90"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        )}

        <section
          className={`${cardClasses} rounded-3xl ${
            activeTab === "products" ? "p-6" : "p-0"
          }`}
        >
          {activeTab === "products" ? (
            filteredProducts.length === 0 ? (
              <div className="py-16 text-center space-y-3">
                <Package className="mx-auto h-12 w-12 text-amber-300" />
                <p className="text-lg font-semibold">
                  Aucun produit ne correspond à vos filtres
                </p>
                <p className={`text-sm ${mutedText}`}>
                  Ajustez votre recherche ou créez une nouvelle référence.
                </p>
              </div>
            ) : (
              <>
                {viewMode === "grid" ? (
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {paginatedProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginatedProducts.map((product) => (
                      <ProductListItem key={`${product.id}-row`} product={product} />
                    ))}
                  </div>
                )}
                {renderPaginationControls()}
              </>
            )
          ) : (
            <div className="p-6">
              <ExtrasManagement />
            </div>
          )}
        </section>
      </div>

      {renderDailyMenuModal()}

      <AddProductModal
        showModal={showAddModal}
        setShowModal={setShowAddModal}
        categories={safeCategories}
        productionDestinations={productionDestinations}
        onQuickCreateCategory={createCategoryQuick}
        availableExtras={availableExtras}
        loadProducts={loadProducts}
        mediaImages={mediaImages}
        isDarkMode={isDarkMode}
        existingProducts={products}
        onCatalogChange={broadcastProductCatalogChange}
        isBoutique={isBoutique}
        aggregatorPricingSettings={aggregatorPricingSettings}
      />

      {renderEditModal()}

      {catalogImportPreview && (
        <div
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
          role="presentation"
          onClick={cancelCatalogImport}
        >
          <div
            className={`${cardClasses} w-full max-w-lg rounded-3xl p-6`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-import-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="catalog-import-title" className="text-xl font-semibold">Vérifier l’import</h3>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              {[
                ["À créer", catalogImportPreview.summary.create],
                ["À modifier", catalogImportPreview.summary.update],
                ["À réactiver", catalogImportPreview.summary.reactivate],
              ].map(([label, value]) => (
                <div key={label} className={`rounded-2xl border p-3 ${dividerBorder}`}>
                  <div className="text-2xl font-semibold text-amber-300">{value}</div>
                  <div className={`mt-1 text-xs ${mutedText}`}>{label}</div>
                </div>
              ))}
            </div>
            <p className={`mt-4 text-sm ${mutedText}`}>
              Correspondance par SKU, sinon par catégorie et nom.
            </p>
            {catalogImportPreview.summary.withoutSku > 0 && (
              <p className="mt-2 text-sm text-amber-300">
                {catalogImportPreview.summary.withoutSku} produit(s) sans SKU : un nom modifié sera créé comme nouveau produit.
              </p>
            )}
            <p className={`mt-2 text-xs ${mutedText}`}>L’export contient uniquement les fiches actives.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelCatalogImport}
                className={`rounded-2xl border px-4 py-2 text-sm font-semibold ${dividerBorder}`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmCatalogImport}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-2 text-sm font-semibold text-slate-900"
              >
                Importer
              </button>
            </div>
          </div>
        </div>
      )}

      {trackingKindPrompt && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
          role="presentation"
          onClick={() => setTrackingKindPrompt(null)}
        >
          <div
            className={`${cardClasses} w-full max-w-lg rounded-3xl p-6`}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
              Suivi de stock
            </p>
            <h3 className="mt-1 text-lg font-semibold">
              Quel type de produit ?
            </h3>
            <p className={`mt-2 text-sm ${mutedText}`}>
              Choisissez avant d&apos;activer le suivi de stock.
            </p>
            <div className="mt-4 flex flex-col gap-3">
              {[
                { id: "resold", label: "Produit revendu", detail: "Vendu tel quel : on retire ce produit du stock." },
                { id: "composed", label: "Produit composé", detail: "Préparé avec une recette : on retire ses ingrédients du stock." },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={trackingKindChoice === opt.id}
                  onClick={() => setTrackingKindChoice(opt.id)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${
                    trackingKindChoice === opt.id
                      ? "border-amber-400 bg-amber-400/15"
                      : isDarkMode
                        ? "border-white/10 bg-white/5 hover:border-white/20"
                        : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <span className="block">{opt.label}</span>
                  <span className={`mt-1 block text-xs font-normal ${mutedText}`}>{opt.detail}</span>
                </button>
              ))}
            </div>
            {trackingKindPrompt.product.product_type === "variable" && (
              <div className="mt-5">
                <p className="mb-2 text-sm font-semibold">Comment suivre le stock ?</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    { id: "global", label: "Stock commun", detail: "Un seul stock pour toutes les déclinaisons." },
                    { id: "variation", label: "Par déclinaison", detail: "Chaque déclinaison a son propre stock." },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={trackingScopeChoice === option.id}
                      onClick={() => setTrackingScopeChoice(option.id)}
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold ${trackingScopeChoice === option.id
                        ? "border-amber-400 bg-amber-400/15"
                        : isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"}`}
                    >
                      <span className="block">{option.label}</span>
                      <span className={`mt-1 block text-xs font-normal ${mutedText}`}>{option.detail}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setTrackingKindPrompt(null)}
                className={`rounded-2xl px-4 py-2 text-sm font-semibold ${
                  isDarkMode
                    ? "border border-white/10 text-slate-200 hover:border-white/30"
                    : "border border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmTrackingKindPrompt}
                disabled={trackingKindPrompt.product.product_type === "variable" && !trackingScopeChoice}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                Activer
              </button>
            </div>
          </div>
        </div>
      )}

      <MediaModal
        isOpen={showEditingMediaModal}
        onClose={() => setShowEditingMediaModal(false)}
        onSelectImage={selectMediaImage}
        isEditing={true}
      />
    </div>
  )
};

const AddProductModal = ({
  showModal,
  setShowModal,
  categories,
  productionDestinations = [],
  onQuickCreateCategory,
  availableExtras,
  loadProducts,
  mediaImages,
  isDarkMode = false,
  existingProducts = [],
  onCatalogChange,
  isBoutique = false,
  aggregatorPricingSettings = { yango: false, glovo: false },
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [productType, setProductType] = useState("simple");
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    description: "",
    category_id: "",
    destination: "cuisine",
    price: "",
    cost_price: "",
    yango_price: "",
    glovo_price: "",
    preparation_time: "",
    is_available: true,
    track_stock: true,
    inventory_kind: "",
    is_recipe_ingredient: false,
    unit_type: "Unité",
    free_accompaniment_quota: 1,
    image_source: "none", // 'none', 'upload', 'url', 'media'
    image_file: null,
    image_url: "",
    image_path: "",
  });
  const [attributes, setAttributes] = useState([]);
  const [variations, setVariations] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [extraScope, setExtraScope] = useState("global"); // global | perVariation
  const [variationExtras, setVariationExtras] = useState({});
  const [showQuickCategoryModal, setShowQuickCategoryModal] = useState(false);
  const [quickCategoryName, setQuickCategoryName] = useState("");
  const [isCreatingQuickCategory, setIsCreatingQuickCategory] = useState(false);
  const [accompanimentSearch, setAccompanimentSearch] = useState("");
  const [supplementSearch, setSupplementSearch] = useState("");
  const [useGlobalPrice, setUseGlobalPrice] = useState(true);
  const [useGlobalStock, setUseGlobalStock] = useState(true);
  const [useVariationStock, setUseVariationStock] = useState(false);
  const [showAggregatorPriceFields, setShowAggregatorPriceFields] =
    useState(false);
  const modalRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useBodyScrollLock(showModal);

  useEffect(() => {
    if (
      !showModal ||
      typeof document === "undefined" ||
      !modalRef.current
    ) {
      return;
    }

    const activeElement = document.activeElement;
    if (
      activeElement &&
      activeElement !== document.body &&
      modalRef.current.contains(activeElement)
    ) {
      return;
    }

    const fallbackTarget =
      (lastFocusedRef.current &&
        modalRef.current.contains(lastFocusedRef.current) &&
        lastFocusedRef.current) ||
      modalRef.current.querySelector(
        "input, textarea, select, button:not([disabled])",
      );

    fallbackTarget?.focus({ preventScroll: true });
  }, [
    showModal,
    currentStep,
    formData,
    productType,
    attributes,
    variations,
    extraScope,
  ]);

  useEffect(() => {
    if (productType === "simple" && extraScope !== "global") {
      setExtraScope("global");
      setVariationExtras({});
    }
    if (productType === "simple") {
      setUseGlobalPrice(true);
      setUseGlobalStock(true);
      setUseVariationStock(false);
    }
  }, [productType, extraScope]);

  const modalSurfaceStyle = isDarkMode
    ? { backgroundColor: "#0b1428", color: "#f8fafc" }
    : {};
  const sectionCardClasses = isDarkMode
    ? "bg-white/5 border border-white/10"
    : "bg-white border-2 border-gray-200";
  const labelClass = isDarkMode
    ? "block text-sm font-semibold text-slate-100 mb-2"
    : "block text-sm font-semibold text-gray-800 mb-2";
  const baseInputClasses = isDarkMode
    ? "w-full px-4 py-3 border border-white/20 rounded-xl bg-[#0b1428] text-slate-100 placeholder-white/40 focus:border-amber-400 focus:ring-0 transition-colors"
    : "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400";
  const compactInputClasses = isDarkMode
    ? "w-24 px-3 py-2 border border-white/20 rounded-lg bg-[#0b1428] text-slate-100 placeholder-white/40 focus:border-amber-400 focus:ring-0"
    : "w-24 p-2 border rounded-lg focus:border-orange-400 focus:ring-0 text-gray-900";
  const paragraphClass = isDarkMode ? "text-white/60" : "text-gray-600";
  const stepGuide = getProductStepGuide(isBoutique);
  const totalSteps = stepGuide.length;

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStep(totalSteps);
    }
  }, [currentStep, totalSteps]);

  const bodyClasses = isDarkMode
    ? "flex-grow overflow-y-auto p-4 sm:p-6 bg-[#050c1b]"
    : "flex-grow overflow-y-auto p-4 sm:p-6 bg-gray-50";
  const footerClasses = isDarkMode
    ? "border-t border-white/10 bg-white/5"
    : "border-t border-gray-200 bg-white";
  const cardTitleClass = isDarkMode
    ? "text-lg font-semibold text-slate-100"
    : "text-lg font-semibold text-gray-900";
  const mutedTextClass = isDarkMode ? "text-white/60" : "text-gray-500";
  const hasAggregatorPricingEnabled =
    Boolean(aggregatorPricingSettings.yango) ||
    Boolean(aggregatorPricingSettings.glovo) ||
    hasExplicitCatalogPrice(formData.yango_price) ||
    hasExplicitCatalogPrice(formData.glovo_price);
  const aggregatorPriceFields = [
    {
      key: "yango_price",
      label: "Prix Yango",
      enabled:
        aggregatorPricingSettings.yango || hasExplicitCatalogPrice(formData.yango_price),
    },
    {
      key: "glovo_price",
      label: "Prix Glovo",
      enabled:
        aggregatorPricingSettings.glovo || hasExplicitCatalogPrice(formData.glovo_price),
    },
  ].filter((field) => field.enabled);
  const renderAggregatorPricingFields = () => {
    if (!hasAggregatorPricingEnabled) return null;

    if (!showAggregatorPriceFields) {
      return (
        <button
          type="button"
          onClick={() => setShowAggregatorPriceFields(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-amber-400/50 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-400/10"
        >
          <Plus className="h-4 w-4" />
          Ajouter un prix agrégateur
        </button>
      );
    }

    return (
      <div
        className={`rounded-2xl border p-4 ${
          isDarkMode
            ? "border-amber-400/20 bg-amber-400/5"
            : "border-amber-200 bg-amber-50/60"
        }`}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Prix agrégateurs</p>
            <p className={`text-xs ${mutedTextClass}`}>
              Prix net restaurateur, sans calcul de commission.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAggregatorPriceFields(false)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              isDarkMode ? "bg-white/10 text-white/70" : "bg-white text-slate-500"
            }`}
          >
            Masquer
          </button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {aggregatorPriceFields.map((field) => (
            <div key={field.key}>
              <label className={labelClass}>{field.label} (FCFA)</label>
              <input
                type="number"
                name={field.key}
                value={formData[field.key] || ""}
                onChange={handleChange}
                className={baseInputClasses}
                placeholder="Optionnel"
                min="0"
                step="50"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };
  const getToggleClasses = (active) =>
    [
      "px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 inline-flex items-center justify-center gap-2 text-center",
      active
        ? isDarkMode
          ? "bg-amber-400 text-slate-900 shadow-lg shadow-amber-500/30"
          : "bg-orange-500 text-white shadow-lg"
        : isDarkMode
          ? "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"
          : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50",
    ].join(" ");
  const dashedUploadClasses = isDarkMode
    ? "rounded-2xl border border-dashed border-white/15 bg-white/5"
    : "rounded-2xl border-2 border-dashed border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50";
  const fileInputClasses = isDarkMode
    ? "w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-white/10 file:text-white hover:file:bg-white/20"
    : "w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-orange-700 file:bg-orange-50 hover:file:bg-orange-100";
  const headerBackgroundClass = isDarkMode
    ? "bg-[#070f1f] border-b border-white/10"
    : "bg-white border-b border-gray-200";
  const stepBadgeClass = isDarkMode
    ? "bg-white/10 text-white"
    : "bg-orange-50 text-orange-600";

  const renderStepHighlightCards = () => (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 ${
        isBoutique ? "lg:grid-cols-3" : "lg:grid-cols-4"
      } gap-4`}
    >
      {stepGuide.map((step, index) => {
        const Icon = step.icon;
        const stepNumber = index + 1;
        const isActive = currentStep === stepNumber;
        const cardBase = isDarkMode
          ? "bg-white/5 border border-white/10"
          : "bg-white border border-gray-200";
        const cardActive = isDarkMode
          ? "border-amber-400/60 shadow-lg shadow-amber-500/20"
          : "border-orange-200 shadow-lg shadow-orange-200/60 bg-gradient-to-br from-orange-50 to-amber-50";
        const iconWrapper = isActive
          ? isDarkMode
            ? "bg-amber-400/25 text-amber-200"
            : "bg-orange-100 text-orange-600"
          : isDarkMode
            ? "bg-white/5 text-white/40"
            : "bg-gray-100 text-gray-400";
        const labelClass = isActive
          ? `text-[10px] uppercase tracking-[0.35em] ${
              isDarkMode ? "text-amber-300" : "text-orange-500"
            }`
          : `text-[10px] uppercase tracking-[0.35em] ${
              isDarkMode ? "text-white/40" : "text-gray-500"
            }`;
        const titleClassHighlight = isDarkMode
          ? "text-sm font-semibold text-slate-100"
          : "text-sm font-semibold text-slate-900";
        const descriptionClass = isDarkMode ? "text-xs text-white/40" : "text-xs text-gray-500";
        return (
          <div
            key={`step-highlight-${step.id}`}
            className={`${cardBase} rounded-2xl p-4 flex items-center gap-3 ${
              isActive ? cardActive : ""
            }`}
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${iconWrapper}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className={labelClass}>Étape {stepNumber}</p>
              <p className={titleClassHighlight}>{step.title}</p>
              <p className={descriptionClass}>{step.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
  const renderModalHeader = () => {
    const currentStepConfig = stepGuide[currentStep - 1];
    return (
      <div className={`p-4 sm:p-6 ${headerBackgroundClass}`}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex-1">
              <p className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}>
                Catalogue MOO
              </p>
              <h2
                className={`text-2xl font-semibold ${
                  isDarkMode ? "text-slate-50" : "text-slate-900"
                }`}
              >
                Créer un produit
              </h2>
              {currentStepConfig ? (
                <p className={`mt-2 text-sm ${mutedTextClass} sm:hidden`}>
                  {currentStepConfig.title} • {currentStepConfig.description}
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${stepBadgeClass}`}>
                Étape {currentStep} / {totalSteps}
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetModalState();
                }}
                className={`rounded-xl border px-3 py-2 transition ${
                  isDarkMode
                    ? "border-white/15 text-white hover:bg-white/10"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="hidden sm:block">{renderStepHighlightCards()}</div>
          <div
            className={`h-2 rounded-full overflow-hidden ${
              isDarkMode ? "bg-white/10" : "bg-gray-100"
            }`}
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-500"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  };
  const addAttribute = () => {
    setAttributes([...attributes, { name: "", values: [], newValue: "" }]);
  };

  const removeAttribute = (index) => {
    const newAttributes = attributes.filter((_, i) => i !== index);
    setAttributes(newAttributes);
  };

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };

  const addAttributeValue = (index) => {
    const newAttributes = [...attributes];
    const newValue = newAttributes[index].newValue.trim();
    if (newValue && !newAttributes[index].values.includes(newValue)) {
      newAttributes[index].values.push(newValue);
      newAttributes[index].newValue = "";
      setAttributes(newAttributes);
    }
  };

  const removeAttributeValue = (attrIndex, valueIndex) => {
    const newAttributes = [...attributes];
    newAttributes[attrIndex].values.splice(valueIndex, 1);
    setAttributes(newAttributes);
  };

  const getVariationKey = (variation, index) =>
    variation.uid || variation.id || `variation-${index}`;

  const syncVariationExtrasState = (nextVariations) => {
    setVariationExtras((prev) => {
      const next = {};
      nextVariations.forEach((variation, index) => {
        const key = getVariationKey(variation, index);
        next[key] = prev[key] || [];
      });
      return next;
    });
  };

  const generateVariations = () => {
    if (
      attributes.length === 0 ||
      attributes.some((attr) => attr.values.length === 0)
    ) {
      showToast("info", "Veuillez d'abord définir des attributs et leurs valeurs.");
      return;
    }

    const combinations = attributes.reduce((acc, attr) => {
      if (acc.length === 0) {
        return attr.values.map((val) => ({ [attr.name]: val }));
      }
      return acc.flatMap((combo) =>
        attr.values.map((val) => ({ ...combo, [attr.name]: val })),
      );
    }, []);

    const timestamp = Date.now();
    const newVariations = combinations.map((attrs, index) => ({
      uid: `${timestamp}-${index}-${Math.random().toString(16).slice(2)}`,
      attributes: attrs,
      price: "",
      cost_price: "",
      yango_price: "",
      glovo_price: "",
      track_stock: false,
      stock_quantity: 0,
      is_active: true,
      extras: [],
    }));
    setVariations(newVariations);
    syncVariationExtrasState(newVariations);
  };

  const updateVariation = (index, field, value) => {
    const newVariations = [...variations];
    newVariations[index][field] = value;
    setVariations(newVariations);
    if (extraScope === "perVariation") {
      syncVariationExtrasState(newVariations);
    }
  };

  const addExtra = (extra, type) => {
    if (extraScope !== "global") {
      showToast(
        "info",
        'Passez en mode "extras globaux" ou utilisez la configuration par variation.',
      );
      return;
    }
    if (selectedExtras.some((e) => e.id === extra.id)) {
      showToast("info", `"${extra.name}" est déjà sélectionné.`);
      return;
    }

    const newExtra = {
      ...extra,
      type: type,
      price: extra.base_price || 0,
      max_quantity: 1,
    };
    setSelectedExtras((prev) => [...prev, newExtra]);
  };

  const updateSelectedExtra = (extraId, field, value) => {
    if (extraScope !== "global") return;
    setSelectedExtras((prev) =>
      prev.map((extra) => {
        if (extra.id === extraId) {
          return { ...extra, [field]: value };
        }
        return extra;
      }),
    );
  };

  const removeExtra = (extraId) => {
    if (extraScope !== "global") return;
    setSelectedExtras((prev) => prev.filter((e) => e.id !== extraId));
  };

  const addVariationExtra = (variationKey, extra, type) => {
    setVariationExtras((prev) => {
      const current = prev[variationKey] || [];
      if (current.some((item) => item.id === extra.id)) {
        showToast("info", `"${extra.name}" est déjà associé à cette variation.`);
        return prev;
      }
      const payload = {
        ...extra,
        type,
        price: extra.base_price || 0,
        max_quantity: 1,
      };
      return { ...prev, [variationKey]: [...current, payload] };
    });
  };

  const updateVariationExtra = (variationKey, extraId, field, value) => {
    setVariationExtras((prev) => {
      const list = prev[variationKey] || [];
      return {
        ...prev,
        [variationKey]: list.map((extra) =>
          extra.id === extraId ? { ...extra, [field]: value } : extra,
        ),
      };
    });
  };

  const removeVariationExtra = (variationKey, extraId) => {
    setVariationExtras((prev) => {
      const list = prev[variationKey] || [];
      return {
        ...prev,
        [variationKey]: list.filter((extra) => extra.id !== extraId),
      };
    });
  };

  const handleExtraScopeChange = (scope) => {
    setExtraScope(scope);
    if (scope === "global") {
      setVariationExtras({});
    } else {
      setSelectedExtras([]);
      syncVariationExtrasState(variations);
    }
  };

  const handleImageSourceChange = (source) => {
    setFormData((prev) => ({
      ...prev,
      image_source: source,
      image_file: null,
      image_url: "",
      image_path: "",
    }));
    setImagePreview(null);
  };

  const validateStep = () => {
    const resolvedStepId = stepGuide[currentStep - 1]?.id || 1;
    switch (resolvedStepId) {
      case 1:
        if (!formData.name.trim()) {
          showToast("error", "Le nom du produit est obligatoire.");
          return false;
        }
        if (!formData.category_id) {
          showToast("error", "Veuillez sélectionner une catégorie.");
          return false;
        }
        {
          const normalizedName = formData.name.trim().toLowerCase();
          const duplicateExists = existingProducts.some(
            (product) =>
              product.name?.trim().toLowerCase() === normalizedName,
          );
          if (duplicateExists) {
            showToast(
              "error",
              "Un produit portant ce nom existe déjà dans le catalogue.",
            );
            return false;
          }
        }
        return true;
      case 2:
        if (productType === "simple") {
          if (!isValidCatalogPrice(formData.price)) {
            showToast(
              "error",
              "Le prix de vente du produit est obligatoire et ne peut pas être négatif.",
            );
            return false;
          }
        }
        if (productType === "variable") {
          if (
            attributes.length === 0 ||
            attributes.some((a) => !a.name.trim() || a.values.length === 0)
          ) {
            showToast(
              "error",
              "Pour un produit variable, vous devez définir au moins un attribut avec un nom et des valeurs.",
            );
            return false;
          }
          if (variations.length === 0) {
            showToast(
              "error",
              "Générez au moins une variation avant de continuer.",
            );
            return false;
          }
          if (useGlobalPrice) {
            if (!isValidCatalogPrice(formData.price)) {
              showToast(
                "error",
                "Le prix global est obligatoire et ne peut pas être négatif.",
              );
              return false;
            }
          } else {
            if (
              variations.some(
                (v) => !isValidCatalogPrice(v.price),
              )
            ) {
              showToast(
                "error",
                "Chaque déclinaison doit avoir un prix valide qui ne peut pas être négatif.",
              );
              return false;
            }
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };
  const handleBack = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleQuickCreateCategory = async () => {
    const categoryName = String(quickCategoryName || "").trim();
    if (!categoryName) {
      showToast("error", "Le nom de catégorie est requis");
      return;
    }
    if (typeof onQuickCreateCategory !== "function") {
      showToast("error", "Création rapide indisponible");
      return;
    }

    setIsCreatingQuickCategory(true);
    try {
      const created = await onQuickCreateCategory(categoryName);
      setFormData((prev) => ({
        ...prev,
        category_id: String(created?.id || ""),
      }));
      setQuickCategoryName("");
      setShowQuickCategoryModal(false);
      showToast("success", "Catégorie ajoutée et sélectionnée");
    } catch (error) {
      showToast(
        "error",
        error?.message || "Impossible de créer la catégorie",
      );
    } finally {
      setIsCreatingQuickCategory(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image_file: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageUrlChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      image_url: value,
      image_file: null,
      image_path: "",
    }));
    setImagePreview(value);
  };

  const handleSelectFromMedia = (image) => {
    setFormData((prev) => ({
      ...prev,
      image_source: "media",
      image_path: image.path,
      image_url: "",
      image_file: null,
    }));
    setImagePreview(image.path);
    setIsMediaModalOpen(false);
  };

  const resetModalState = () => {
    setCurrentStep(1);
    setProductType("simple");
    setFormData({
      name: "",
      sku: "",
      description: "",
      category_id: "",
      destination: "cuisine",
      price: "",
      cost_price: "",
      yango_price: "",
      glovo_price: "",
      preparation_time: "",
      is_available: true,
      track_stock: true,
      inventory_kind: "",
      is_recipe_ingredient: false,
      unit_type: "Unité",
      free_accompaniment_quota: 1,
      image_source: "none",
      image_file: null,
      image_url: "",
      image_path: "",
    });
    setAttributes([]);
    setVariations([]);
    setSelectedExtras([]);
    setImagePreview(null);
    setIsMediaModalOpen(false);
    setExtraScope("global");
    setVariationExtras({});
    setUseGlobalPrice(true);
    setUseGlobalStock(true);
    setUseVariationStock(false);
    setShowAggregatorPriceFields(false);
    setShowQuickCategoryModal(false);
    setQuickCategoryName("");
    setIsCreatingQuickCategory(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trackingActive =
      productType === "variable"
        ? useGlobalStock || useVariationStock
        : !!formData.track_stock;
    if (trackingActive && !String(formData.inventory_kind || "").trim()) {
      showToast(
        "error",
        "Veuillez préciser si l'article suivi est un produit revendu ou un produit composé.",
      );
      return;
    }
    const normalizedName = formData.name?.trim().toLowerCase();
    if (
      normalizedName &&
      existingProducts.some(
        (product) =>
          product.name?.trim().toLowerCase() === normalizedName,
      )
    ) {
      showToast(
        "error",
        "Un produit portant ce nom existe déjà dans le catalogue.",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();

      // Validation finale
      if (!formData.name.trim()) {
        throw new Error("Le nom du produit est obligatoire");
      }
      if (!formData.category_id) {
        throw new Error("La catégorie est obligatoire");
      }

      // Données de base - s'assurer que les valeurs ne sont pas undefined
      submitData.append("name", formData.name.trim());
      submitData.append("sku", (formData.sku || "").trim());
      submitData.append("description", formData.description || "");
      submitData.append("category_id", formData.category_id);
      const destinationValue = formData.destination || "cuisine";
      submitData.append("destination", destinationValue);
      submitData.append("preparation_time", formData.preparation_time || "0");
      submitData.append(
        "is_available",
        formData.is_available ? "true" : "false",
      );
      submitData.append(
        "track_stock",
        productType === "variable"
          ? useGlobalStock || useVariationStock
            ? "true"
            : "false"
          : formData.track_stock
            ? "true"
            : "false",
      );
      submitData.append("inventory_kind", formData.inventory_kind || "");
      submitData.append(
        "is_recipe_ingredient",
        formData.inventory_kind === "resold" && formData.is_recipe_ingredient
          ? "true"
          : "false",
      );
      submitData.append("unit_type", formData.unit_type || "Unité");
      submitData.append("product_type", productType);
      submitData.append("has_accompaniments", "false");
      submitData.append("has_supplements", "false");
      submitData.append(
        "free_accompaniment_quota",
        String(Math.max(0, Number(formData.free_accompaniment_quota) || 0)),
      );
      submitData.append("yango_price", formData.yango_price ?? "");
      submitData.append("glovo_price", formData.glovo_price ?? "");

      // Gestion du prix selon le type de produit
      if (productType === "simple") {
        if (!isValidCatalogPrice(formData.price)) {
          throw new Error(
            "Le prix de vente est obligatoire et ne peut pas être négatif",
          );
        }
        submitData.append("price", formData.price.toString());
        submitData.append(
          "cost_price",
          (formData.cost_price || "0").toString(),
        );
      } else {
        // Pour les produits variables
        if (variations.length === 0) {
          throw new Error(
            "Au moins une variation est requise pour un produit variable",
          );
        }
        if (useGlobalPrice) {
          if (!isValidCatalogPrice(formData.price)) {
            throw new Error(
              "Le prix global est obligatoire et ne peut pas être négatif",
            );
          }
          submitData.append("price", formData.price.toString());
          submitData.append(
            "cost_price",
            (formData.cost_price || "0").toString(),
          );
        } else {
          const firstVariationPrice = variations[0].price ?? "0";
          submitData.append("price", firstVariationPrice.toString());
          submitData.append(
            "cost_price",
            (formData.cost_price || "0").toString(),
          );
        }
        submitData.append("attributes", JSON.stringify(attributes));

        const variationsPayload = variations.map((variation, index) => {
          const key = getVariationKey(variation, index);
          const extrasForVariation = isBoutique
            ? []
            : extraScope === "perVariation"
              ? (variationExtras[key] || []).map(formatExtraPayload)
              : selectedExtras.map(formatExtraPayload);

          const {
            uid,
            stock,
            stock_quantity,
            price,
            cost_price,
            attributes: attr,
            extras,
            ...rest
          } = variation;

          return {
            ...rest,
            attributes: attr,
            price: useGlobalPrice ? formData.price : price,
            cost_price: cost_price || formData.cost_price || 0,
            sku: variation.sku || null,
            track_stock: useVariationStock,
            stock_quantity: 0,
            extras: extrasForVariation,
          };
        });
        submitData.append("variations", JSON.stringify(variationsPayload));
      }

      // Gestion de l'image
      if (formData.image_source === "upload" && formData.image_file) {
        submitData.append("image", formData.image_file);
      } else if (formData.image_source === "url" && formData.image_url) {
        submitData.append("image_url", formData.image_url);
      } else if (formData.image_source === "media" && formData.image_path) {
        submitData.append("image_path", formData.image_path);
      }

      // Extras sélectionnés
      const extrasPayload = isBoutique
        ? []
        : extraScope === "global"
          ? selectedExtras.map(formatExtraPayload)
          : [];
      submitData.append("selectedExtras", JSON.stringify(extrasPayload));

      console.log("Données envoyées:", {
        name: formData.name,
        category_id: formData.category_id,
        price: formData.price,
        product_type: productType,
      });

      const response = await fetch("/api/products", {
        method: "POST",
        body: submitData,
        // Ne pas définir Content-Type, le navigateur le fera automatiquement avec la boundary pour FormData
      });
      const createdProduct = await response.json().catch(() => null);

      if (response.ok) {
        showToast("success", "🎉 Produit créé avec succès !");
        setShowModal(false);
        resetModalState();
        void loadProducts({ silent: true });
        onCatalogChange?.({
          type: "PRODUCT_CREATED",
          productId: createdProduct?.id,
        });
      } else {
        throw new Error(
          createdProduct?.error || "Erreur lors de la création du produit",
        );
      }
    } catch (error) {
      console.error("Erreur création produit:", error);
      showToast("error", error.message || "Erreur lors de la création du produit");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const resolvedStepId = stepGuide[currentStep - 1]?.id || 1;
    switch (resolvedStepId) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Nom du Produit <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={baseInputClasses}
                  placeholder="Ex: Pizza Margherita, Café Expresso..."
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>SKU (optionnel)</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className={baseInputClasses}
                  placeholder="Ex: BIS-500-SUCRE"
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className={baseInputClasses}
                  placeholder="Décrivez votre produit..."
                />
              </div>

              <div>
                <label className={labelClass}>
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className={baseInputClasses}
                    required
                  >
                    <option value="">Sélectionner une catégorie...</option>
                    {(Array.isArray(categories) ? categories : []).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowQuickCategoryModal(true)}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                      isDarkMode
                        ? "border border-white/20 bg-white/5 text-slate-100 hover:bg-white/10"
                        : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    + Catégorie
                  </button>
                </div>
              </div>

              {!isBoutique && (
                <div>
                  <label className={labelClass}>Destination d'impression</label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    className={baseInputClasses}
                  >
                    {productionDestinations.map((destination) => (
                      <option key={destination.code} value={destination.code}>
                        {getProductionDestinationLabel(destination)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="md:col-span-2">
                <label className={labelClass}>Image du Produit</label>
                <div className={`${dashedUploadClasses} p-4`}>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => handleImageSourceChange("none")}
                      className={getToggleClasses(
                        formData.image_source === "none",
                      )}
                    >
                      ❌ Aucune
                    </button>
                    <button
                      type="button"
                      onClick={() => handleImageSourceChange("upload")}
                      className={getToggleClasses(
                        formData.image_source === "upload",
                      )}
                    >
                      📤 Téléverser
                    </button>
                    <button
                      type="button"
                      onClick={() => handleImageSourceChange("url")}
                      className={getToggleClasses(
                        formData.image_source === "url",
                      )}
                    >
                      🔗 URL
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleImageSourceChange("media");
                        setIsMediaModalOpen(true);
                      }}
                      className={getToggleClasses(
                        formData.image_source === "media",
                      )}
                    >
                      📁 Médiathèque
                    </button>
                  </div>

                  {formData.image_source === "upload" && (
                    <div className="mt-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className={fileInputClasses}
                      />
                    </div>
                  )}

                  {formData.image_source === "url" && (
                    <div className="mt-3">
                      <input
                        type="text"
                        name="image_url"
                        placeholder="https://example.com/image.png"
                        value={formData.image_url}
                        onChange={handleImageUrlChange}
                        className={baseInputClasses}
                      />
                    </div>
                  )}

                  {imagePreview && (
                    <div className="mt-4 text-center">
                      <img
                        src={imagePreview}
                        alt="Aperçu"
                        className={`h-32 w-32 object-cover rounded-xl mx-auto border ${
                          isDarkMode ? "border-white/15" : "border-gray-200"
                        }`}
                      />
                      <p className={`text-sm mt-2 ${mutedTextClass}`}>
                        Aperçu de l'image
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <div className={`${sectionCardClasses} rounded-2xl p-6 space-y-4`}>
              <label className={labelClass}>Type de produit</label>
              <div className="flex flex-col gap-3 md:flex-row">
                <button
                  type="button"
                  onClick={() => setProductType("simple")}
                  className={`${getToggleClasses(productType === "simple")} flex-1`}
                >
                  📦 Simple
                </button>
                <button
                  type="button"
                  onClick={() => setProductType("variable")}
                  className={`${getToggleClasses(productType === "variable")} flex-1`}
                >
                  🎯 Variable
                </button>
              </div>
            </div>

            {productType === "simple" ? (
              <div className={`${sectionCardClasses} rounded-2xl p-6 shadow-sm`}>
                <h4 className={`${cardTitleClass} mb-4`}>Prix</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>
                      Prix de Vente (FCFA) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className={baseInputClasses}
                      placeholder="Ex: 2500"
                      min="0"
                      step="50"
                      required
                    />
                  </div>
                </div>
                {renderAggregatorPricingFields()}
              </div>
            ) : (
              <div className="space-y-6">
                <div className={`${sectionCardClasses} rounded-2xl p-6 space-y-4`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h4 className={cardTitleClass}>Attributs</h4>
                      <p className={`text-sm ${mutedTextClass}`}>
                        Créez dynamiquement les caractéristiques (taille, parfum,
                        grammage...) qui composent vos variations.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addAttribute}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg hover:from-orange-600 hover:to-amber-600 transition"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter un attribut
                    </button>
                  </div>
                  {attributes.length === 0 ? (
                    <p className={`text-sm ${mutedTextClass}`}>
                      Aucun attribut défini pour le moment.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {attributes.map((attr, index) => (
                        <div
                          key={index}
                          className={`rounded-2xl p-4 space-y-3 ${
                            isDarkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-orange-50/60 border border-orange-100"
                          }`}
                        >
                          <div className="flex flex-col md:flex-row gap-3">
                            <input
                              type="text"
                              placeholder="Ex: Taille, Couleur"
                              value={attr.name}
                              onChange={(e) =>
                                handleAttributeChange(index, "name", e.target.value)
                              }
                              className={`${baseInputClasses} flex-1`}
                            />
                            <button
                              type="button"
                              onClick={() => removeAttribute(index)}
                              className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-semibold ${
                                isDarkMode
                                  ? "text-red-300 hover:bg-red-500/10"
                                  : "text-red-600 hover:bg-red-50"
                              }`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex flex-col md:flex-row gap-3">
                            <input
                              type="text"
                              placeholder="Ex: Petit, Rouge (puis Entrée)"
                              value={attr.newValue}
                              onChange={(e) =>
                                handleAttributeChange(
                                  index,
                                  "newValue",
                                  e.target.value,
                                )
                              }
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addAttributeValue(index);
                                }
                              }}
                              className={`${baseInputClasses} flex-1`}
                            />
                            <button
                              type="button"
                              onClick={() => addAttributeValue(index)}
                              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-sky-500 to-blue-500 text-white shadow hover:from-sky-600 hover:to-blue-600"
                            >
                              Ajouter la valeur
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {attr.values.map((val, vIndex) => (
                              <span
                                key={vIndex}
                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                                  isDarkMode
                                    ? "bg-white/10 text-white"
                                    : "bg-white text-gray-800 border border-gray-200"
                                }`}
                              >
                                {val}
                                <button
                                  type="button"
                                  onClick={() => removeAttributeValue(index, vIndex)}
                                  className={`text-xs ${
                                    isDarkMode ? "text-red-300" : "text-red-500"
                                  }`}
                                >
                                  ✕
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className={`${sectionCardClasses} rounded-2xl p-6 space-y-4`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h4 className={cardTitleClass}>Déclinaisons générées</h4>
                      <p className={`text-sm ${mutedTextClass}`}>
                        Chaque combinaison d'attributs crée une fiche variation
                        indépendante avec son prix et son statut.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={generateVariations}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow hover:from-blue-600 hover:to-indigo-600"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Générer les déclinaisons
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <label
                      className={`flex items-center gap-2 text-sm font-semibold ${mutedTextClass}`}
                    >
                      <input
                        type="checkbox"
                        checked={useGlobalPrice}
                        onChange={(e) => setUseGlobalPrice(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                      />
                      Prix global (une seule valeur)
                    </label>
                    <label
                      className={`flex items-center gap-2 text-sm font-semibold ${mutedTextClass}`}
                    >
                      <input
                        type="checkbox"
                        checked={useGlobalStock}
                        onChange={(e) => {
                          setUseGlobalStock(e.target.checked);
                          if (e.target.checked) {
                            setUseVariationStock(false);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                      />
                      Suivi stock global
                    </label>
                    <label
                      className={`flex items-center gap-2 text-sm font-semibold ${mutedTextClass}`}
                    >
                      <input
                        type="checkbox"
                        checked={useVariationStock}
                        onChange={(e) => {
                          setUseVariationStock(e.target.checked);
                          if (e.target.checked) {
                            setUseGlobalStock(false);
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                      />
                      Suivi stock par déclinaison
                    </label>
                  </div>

                  {(useGlobalStock || useVariationStock) && (
                    <div
                      className={`rounded-2xl border p-4 ${
                        isDarkMode
                          ? "border-white/10 bg-white/5"
                          : "border-amber-200 bg-amber-50/60"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-slate-100" : "text-slate-800"
                        }`}
                      >
                        Nature du produit suivi
                      </p>
                      <p className={`text-xs mt-1 ${mutedTextClass}`}>
                        Les variations héritent de ce choix.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {[
                          { id: "resold", label: "Produit revendu" },
                          { id: "composed", label: "Produit composé" },
                        ].map((opt) => (
                          <label
                            key={opt.id}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 border cursor-pointer ${
                              formData.inventory_kind === opt.id
                                ? "border-amber-400 bg-amber-400/20"
                                : isDarkMode
                                  ? "border-white/10 bg-white/5"
                                  : "border-slate-200 bg-white"
                            }`}
                          >
                            <input
                              type="radio"
                              name="inventory_kind"
                              value={opt.id}
                              checked={formData.inventory_kind === opt.id}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  inventory_kind: e.target.value,
                                }))
                              }
                              className="h-4 w-4"
                            />
                            <span className="text-sm font-semibold">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                      {formData.inventory_kind === "resold" && (
                        <label className="mt-3 flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!formData.is_recipe_ingredient}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                is_recipe_ingredient: e.target.checked,
                              }))
                            }
                            className="h-4 w-4"
                          />
                          <span className="text-sm font-semibold">
                            Utilisable comme ingrédient de recette
                          </span>
                        </label>
                      )}
                    </div>
                  )}
                  {useGlobalPrice && (
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className={labelClass}>Prix de vente global (FCFA)</label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          className={baseInputClasses}
                          placeholder="Ex: 2500"
                          min="0"
                          step="50"
                        />
                      </div>
                    </div>
                  )}
                  {renderAggregatorPricingFields()}
                  {variations.length === 0 ? (
                    <p className={`text-sm ${mutedTextClass}`}>
                      Ajoutez des attributs puis cliquez sur "Générer" pour créer les
                      combinaisons disponibles.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {variations.map((v, index) => (
                        <div
                          key={getVariationKey(v, index)}
                          className={`rounded-2xl p-4 flex flex-wrap gap-3 items-center ${
                            isDarkMode
                              ? "bg-white/5 border border-white/10"
                              : "bg-white border border-gray-200"
                          }`}
                        >
                          <div className="flex-1 text-sm font-semibold">
                            {Object.entries(v.attributes).map(([key, val]) => (
                              <span key={key} className="mr-2">
                                {key}:{" "}
                                <span className="font-normal text-sm">{val}</span>
                              </span>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="SKU (optionnel)"
                            value={v.sku || ""}
                            onChange={(e) =>
                              updateVariation(index, "sku", e.target.value)
                            }
                            className={compactInputClasses}
                          />
                          {!useGlobalPrice && (
                            <input
                              type="number"
                              placeholder="Prix *"
                              value={v.price}
                              onChange={(e) =>
                                updateVariation(index, "price", e.target.value)
                              }
                              className={compactInputClasses}
                              min="0"
                              step="50"
                            />
                          )}
                          {showAggregatorPriceFields &&
                            aggregatorPriceFields.map((field) => (
                              <input
                                key={`${getVariationKey(v, index)}-${field.key}`}
                                type="number"
                                placeholder={`${field.label}`}
                                value={v[field.key] || ""}
                                onChange={(e) =>
                                  updateVariation(index, field.key, e.target.value)
                                }
                                className={compactInputClasses}
                                min="0"
                                step="50"
                              />
                            ))}
                          <label
                            className={`flex items-center gap-2 text-xs font-semibold ${
                              mutedTextClass
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={v.is_active !== false}
                              onChange={(e) =>
                                updateVariation(index, "is_active", e.target.checked)
                              }
                              className="rounded border currentColor"
                            />
                            Actif
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Options Communes */}
            <div className={`${sectionCardClasses} rounded-2xl p-6 shadow-sm`}>
              <h4 className={`${cardTitleClass} mb-4`}>Options du produit</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Temps de préparation (min)</label>
                  <input
                    type="number"
                    name="preparation_time"
                    value={formData.preparation_time}
                    onChange={handleChange}
                    className={baseInputClasses}
                    placeholder="Ex: 15"
                    min="0"
                  />
                </div>
                <div className="md:col-span-2 flex flex-wrap gap-4">
                  <label
                    className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
                      isDarkMode
                        ? "bg-emerald-500/10 border-emerald-500/30"
                        : "bg-green-50 border-green-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      id="is_available"
                      name="is_available"
                      checked={formData.is_available}
                      onChange={handleChange}
                      className="h-5 w-5 text-green-600 border-green-300 rounded focus:ring-green-500"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-slate-100" : "text-green-800"
                      }`}
                    >
                      Activer le produit
                    </span>
                  </label>
                  {productType === "simple" && (
                    <label
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${
                        isDarkMode
                          ? "bg-blue-500/10 border-blue-500/30"
                          : "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="track_stock"
                        name="track_stock"
                        checked={formData.track_stock}
                        onChange={handleChange}
                        className="h-5 w-5 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                      />
                      <span
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-slate-100" : "text-blue-800"
                        }`}
                      >
                        Activer le suivi de stock
                      </span>
                    </label>
                  )}
                </div>

                {productType === "simple" && formData.track_stock && (
                  <div
                    className={`mt-4 rounded-2xl border p-4 ${
                      isDarkMode
                        ? "border-white/10 bg-white/5"
                        : "border-amber-200 bg-amber-50/60"
                    }`}
                  >
                    <p
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-slate-100" : "text-slate-800"
                      }`}
                    >
                      Nature du produit suivi
                    </p>
                    <p className={`text-xs mt-1 ${mutedTextClass}`}>
                      Précisez si cet article est revendu tel quel ou s'il est produit via une recette.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {[
                        { id: "resold", label: "Produit revendu" },
                        { id: "composed", label: "Produit composé" },
                      ].map((opt) => (
                        <label
                          key={opt.id}
                          className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 border cursor-pointer ${
                            formData.inventory_kind === opt.id
                              ? "border-amber-400 bg-amber-400/20"
                              : isDarkMode
                                ? "border-white/10 bg-white/5"
                                : "border-slate-200 bg-white"
                          }`}
                        >
                          <input
                            type="radio"
                            name="inventory_kind"
                            value={opt.id}
                            checked={formData.inventory_kind === opt.id}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                inventory_kind: e.target.value,
                              }))
                            }
                            className="h-4 w-4"
                          />
                          <span className="text-sm font-semibold">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {formData.inventory_kind === "resold" && (
                      <label className="mt-3 flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.is_recipe_ingredient}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              is_recipe_ingredient: e.target.checked,
                            }))
                          }
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-semibold">
                          Utilisable comme ingrédient de recette
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 3:
        const globalAccompaniments = selectedExtras.filter(
          (e) => e.type === "accompaniment",
        );
        const globalSupplements = selectedExtras.filter(
          (e) => e.type === "supplement",
        );
        const unselectedExtras = availableExtras.filter(
          (ae) => !selectedExtras.find((se) => se.id === ae.id),
        );
        const normalizedAccompanimentSearch = accompanimentSearch
          .trim()
          .toLowerCase();
        const normalizedSupplementSearch = supplementSearch.trim().toLowerCase();
        const filteredGlobalAccompOptions = unselectedExtras.filter((extra) =>
          (extra.name || "")
            .toLowerCase()
            .includes(normalizedAccompanimentSearch),
        );
        const filteredGlobalSupplementOptions = unselectedExtras.filter(
          (extra) =>
            (extra.name || "")
              .toLowerCase()
              .includes(normalizedSupplementSearch),
        );

        return (
          <div className="space-y-6">
            {productType === "variable" && (
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "global", label: "Même extras pour toutes les variantes" },
                  { id: "perVariation", label: "Configurer variation par variation" },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleExtraScopeChange(option.id)}
                    className={`${getToggleClasses(extraScope === option.id)} px-6`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}

            {extraScope === "global" || productType === "simple" ? (
              <div className="space-y-6">
                <div className={`${sectionCardClasses} rounded-2xl p-5 space-y-3`}>
                  <div className="flex items-center justify-between">
                    <h4 className={`${cardTitleClass} text-base`}>
                      Accompagnements sélectionnés
                    </h4>
                    <label className={`flex items-center gap-2 text-xs ${mutedTextClass}`}>
                      Gratuits / article
                      <input
                        type="number"
                        min="0"
                        value={formData.free_accompaniment_quota}
                        onChange={(event) =>
                          setFormData((previous) => ({
                            ...previous,
                            free_accompaniment_quota: Math.max(
                              0,
                              parseInt(event.target.value, 10) || 0,
                            ),
                          }))
                        }
                        className={`${compactInputClasses} w-16`}
                      />
                    </label>
                  </div>
                  {globalAccompaniments.length > 0 ? (
                    <div className="space-y-3">
                      {globalAccompaniments.map((extra) => (
                        <div
                          key={extra.id}
                          className={`flex items-center justify-between p-3 rounded-xl border ${
                            isDarkMode
                              ? "bg-white/5 border-white/15"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <span className="font-medium">{extra.name}</span>
                          <div className="flex items-center gap-4">
                            <label className={`text-sm ${mutedTextClass}`}>
                              Qté max:
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={extra.max_quantity}
                              onChange={(e) =>
                                updateSelectedExtra(
                                  extra.id,
                                  "max_quantity",
                                  parseInt(e.target.value, 10) || 1,
                                )
                              }
                              className={`${compactInputClasses} w-20`}
                            />
                            <button
                              onClick={() => removeExtra(extra.id)}
                              className="text-red-500 hover:text-red-300 p-1"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${mutedTextClass}`}>
                      Aucun accompagnement sélectionné.
                    </p>
                  )}
                </div>

                <div className={`${sectionCardClasses} rounded-2xl p-5 space-y-3`}>
                  <div className="flex items-center justify-between">
                    <h4 className={`${cardTitleClass} text-base`}>
                      Suppléments sélectionnés
                    </h4>
                    <span className={`text-xs ${mutedTextClass}`}>
                      {globalSupplements.length} sélectionné(s)
                    </span>
                  </div>
                  {globalSupplements.length > 0 ? (
                    <div className="space-y-3">
                      {globalSupplements.map((extra) => (
                        <div
                          key={extra.id}
                          className={`flex items-center justify-between p-3 rounded-xl border ${
                            isDarkMode
                              ? "bg-white/5 border-white/15"
                              : "bg-white border-gray-200"
                          }`}
                        >
                          <span className="font-medium">{extra.name}</span>
                          <div className="flex items-center gap-4">
                            <div>
                              <label className={`text-sm ${mutedTextClass}`}>
                                Prix (FCFA):
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="50"
                                value={extra.price}
                                onChange={(e) =>
                                  updateSelectedExtra(
                                    extra.id,
                                    "price",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className={`${compactInputClasses} w-24`}
                              />
                            </div>
                            <div>
                              <label className={`text-sm ${mutedTextClass}`}>
                                Qté max:
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={extra.max_quantity}
                                onChange={(e) =>
                                  updateSelectedExtra(
                                    extra.id,
                                    "max_quantity",
                                    parseInt(e.target.value, 10) || 1,
                                  )
                                }
                                className={`${compactInputClasses} w-20`}
                              />
                            </div>
                            <button
                              onClick={() => removeExtra(extra.id)}
                              className="text-red-500 hover:text-red-300 p-1"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${mutedTextClass}`}>
                      Aucun supplément sélectionné.
                    </p>
                  )}
                </div>

                <div className={`${sectionCardClasses} rounded-2xl p-5 space-y-4`}>
                  <h4 className={`${cardTitleClass} text-base`}>
                    Ajouter depuis la liste d'extras
                  </h4>
                  {unselectedExtras.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h5 className="text-sm font-bold">
                          Accompagnements
                          </h5>
                          <input
                            type="text"
                            value={accompanimentSearch}
                            onChange={(e) => setAccompanimentSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className={`${baseInputClasses} h-10 text-sm`}
                          />
                        </div>
                        <div
                          className={`space-y-2 max-h-48 overflow-y-auto p-3 rounded-xl border ${
                            isDarkMode
                              ? "border-white/15 bg-white/5"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          {filteredGlobalAccompOptions.length > 0 ? (
                            filteredGlobalAccompOptions.map((extra) => (
                              <div
                                key={`accomp-${extra.id}`}
                                className={`flex justify-between items-center p-2 rounded-lg ${
                                  isDarkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                                }`}
                              >
                                <span>{extra.name}</span>
                                <button
                                  onClick={() => addExtra(extra, "accompaniment")}
                                  className="text-xs bg-blue-500 text-white px-3 py-1 rounded-full hover:bg-blue-600"
                                >
                                  Ajouter
                                </button>
                              </div>
                            ))
                          ) : (
                            <p className={`text-sm ${mutedTextClass}`}>
                              Aucun accompagnement trouvé.
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h5 className="text-sm font-bold">
                          Suppléments
                          </h5>
                          <input
                            type="text"
                            value={supplementSearch}
                            onChange={(e) => setSupplementSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className={`${baseInputClasses} h-10 text-sm`}
                          />
                        </div>
                        <div
                          className={`space-y-2 max-h-48 overflow-y-auto p-3 rounded-xl border ${
                            isDarkMode
                              ? "border-white/15 bg-white/5"
                              : "border-gray-200 bg-white"
                          }`}
                        >
                          {filteredGlobalSupplementOptions.length > 0 ? (
                            filteredGlobalSupplementOptions.map((extra) => (
                              <div
                                key={`supp-${extra.id}`}
                                className={`flex justify-between items-center p-2 rounded-lg ${
                                  isDarkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                                }`}
                              >
                                <span>{extra.name}</span>
                                <button
                                  onClick={() => addExtra(extra, "supplement")}
                                  className="text-xs bg-green-500 text-white px-3 py-1 rounded-full hover:bg-green-600"
                                >
                                  Ajouter
                                </button>
                              </div>
                            ))
                          ) : (
                            <p className={`text-sm ${mutedTextClass}`}>
                              Aucun supplément trouvé.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm ${mutedTextClass}`}>
                      Tous les extras ont été ajoutés.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {variations.length === 0 ? (
                  <p className={`text-sm ${mutedTextClass}`}>
                    Ajoutez ou générez des déclinaisons pour configurer leurs extras.
                  </p>
                ) : (
                  variations.map((variation, index) => {
                    const key = getVariationKey(variation, index);
                    const extrasForVariation = variationExtras[key] || [];
                    const variationLabel = Object.entries(variation.attributes || {})
                      .map(([attrName, value]) => `${attrName}: ${value}`)
                      .join(" • ") || `Variation ${index + 1}`;

                    return (
                      <div
                        key={key}
                        className={`${sectionCardClasses} rounded-2xl p-5 space-y-4`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}
                            >
                              Variation
                            </p>
                            <p className="font-semibold">{variationLabel}</p>
                          </div>
                          <label className={`flex items-center gap-2 text-xs ${mutedTextClass}`}>
                            Gratuits / article
                            <input
                              type="number"
                              min="0"
                              placeholder="Global"
                              value={variation.free_accompaniment_quota ?? ""}
                              onChange={(event) =>
                                updateVariation(
                                  index,
                                  "free_accompaniment_quota",
                                  event.target.value === ""
                                    ? null
                                    : Math.max(0, parseInt(event.target.value, 10) || 0),
                                )
                              }
                              className={`${compactInputClasses} w-20`}
                            />
                          </label>
                        </div>

                        {extrasForVariation.length === 0 ? (
                          <p className={`text-sm ${mutedTextClass}`}>
                            Aucun extra configuré pour cette variation.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {extrasForVariation.map((extra) => (
                              <div
                                key={`${key}-${extra.id}`}
                                className={`flex items-center justify-between rounded-xl p-3 ${
                                  isDarkMode
                                    ? "bg-white/5 border border-white/10"
                                    : "bg-gray-50 border border-gray-200"
                                }`}
                              >
                                <div>
                                  <p className="font-semibold">{extra.name}</p>
                                  <p className={`text-xs ${mutedTextClass}`}>
                                    {extra.type === "accompaniment"
                                      ? "Accompagnement"
                                      : "Supplément"}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                  {extra.type === "supplement" && (
                                    <div>
                                      <label className={`text-xs ${mutedTextClass}`}>
                                        Prix (FCFA)
                                      </label>
                                      <input
                                        type="number"
                                        min="0"
                                        value={extra.price}
                                        onChange={(e) =>
                                          updateVariationExtra(
                                            key,
                                            extra.id,
                                            "price",
                                            parseFloat(e.target.value) || 0,
                                          )
                                        }
                                        className={`${compactInputClasses} w-24`}
                                      />
                                    </div>
                                  )}
                                  <div>
                                    <label className={`text-xs ${mutedTextClass}`}>
                                      Qté max
                                    </label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={extra.max_quantity}
                                      onChange={(e) =>
                                        updateVariationExtra(
                                          key,
                                          extra.id,
                                          "max_quantity",
                                          parseInt(e.target.value, 10) || 1,
                                        )
                                      }
                                      className={`${compactInputClasses} w-20`}
                                    />
                                  </div>
                                  <button
                                    onClick={() => removeVariationExtra(key, extra.id)}
                                    className="text-red-500 hover:text-red-300 p-1"
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-sm font-semibold mb-2">
                              Ajouter des accompagnements
                            </h5>
                            <div
                              className={`space-y-2 max-h-40 overflow-y-auto p-3 rounded-xl border ${
                                isDarkMode
                                  ? "border-white/15 bg-white/5"
                                  : "border-gray-200 bg-white"
                              }`}
                            >
                              {availableExtras.map((extra) => (
                                <div
                                  key={`${key}-acc-${extra.id}`}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span>{extra.name}</span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      addVariationExtra(key, extra, "accompaniment")
                                    }
                                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full"
                                  >
                                    Ajouter
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h5 className="text-sm font-semibold mb-2">
                              Ajouter des suppléments
                            </h5>
                            <div
                              className={`space-y-2 max-h-40 overflow-y-auto p-3 rounded-xl border ${
                                isDarkMode
                                  ? "border-white/15 bg-white/5"
                                  : "border-gray-200 bg-white"
                              }`}
                            >
                              {availableExtras.map((extra) => (
                                <div
                                  key={`${key}-sup-${extra.id}`}
                                  className="flex items-center justify-between text-sm"
                                >
                                  <span>{extra.name}</span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      addVariationExtra(key, extra, "supplement")
                                    }
                                    className="text-xs bg-violet-500 text-white px-2 py-1 rounded-full"
                                  >
                                    Ajouter
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div className={`${sectionCardClasses} rounded-2xl p-6 space-y-4`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}>
                    Nom
                  </p>
                  <p className="text-lg font-semibold">{formData.name || "-"}</p>
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}>
                    Catégorie
                  </p>
                  <p className="text-lg font-semibold">
                    {(Array.isArray(categories) ? categories : []).find(
                      (c) => c.id === formData.category_id,
                    )?.name || "-"}
                  </p>
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}>
                    Type
                  </p>
                  <p className="text-lg font-semibold capitalize">{productType}</p>
                </div>
                <div>
                  <p className={`text-xs uppercase tracking-[0.3em] ${mutedTextClass}`}>
                    Prix
                  </p>
                  <p className="text-lg font-semibold">
                    {productType === "simple" ? `${formData.price || 0} CFA` : "Variable"}
                  </p>
                </div>
              </div>

              {productType === "variable" && variations.length > 0 && (
                <div className="pt-2 space-y-2">
                  <h4 className={cardTitleClass}>Déclinaisons</h4>
                  <div className="space-y-2">
                    {variations.map((variation, index) => (
                      <div
                        key={getVariationKey(variation, index)}
                        className={`rounded-xl px-4 py-3 text-sm ${
                          isDarkMode ? "bg-white/5" : "bg-gray-50"
                        }`}
                      >
                        <p className="font-semibold">
                          {Object.values(variation.attributes).join(" / ")}
                        </p>
                        <p className={`text-xs ${mutedTextClass}`}>
                          Prix: {variation.price || 0} CFA
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {extraScope === "perVariation" && productType === "variable" ? (
                <div className="space-y-2">
                  <h4 className={cardTitleClass}>Extras par variation</h4>
                  {variations.map((variation, index) => {
                    const key = getVariationKey(variation, index);
                    const extrasForVariation = variationExtras[key] || [];
                    return (
                      <p key={key} className={`text-sm ${mutedTextClass}`}>
                        {Object.values(variation.attributes).join(" / ")} •{" "}
                        {extrasForVariation.length} extra(s)
                      </p>
                    );
                  })}
                </div>
              ) : (
                selectedExtras.length > 0 && (
                  <div className="space-y-2">
                    <h4 className={cardTitleClass}>Extras globaux</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedExtras.map((e) => (
                        <span
                          key={e.id}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isDarkMode
                              ? "bg-white/10 text-white"
                              : "bg-orange-50 text-orange-700"
                          }`}
                        >
                          {e.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Ne rendre le modal que si showModal est true
  if (!showModal) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 flex items-end justify-center z-50 p-0 sm:p-4 backdrop-blur-sm sm:items-center">
      <div
        ref={modalRef}
        onFocusCapture={(event) => {
          if (modalRef.current?.contains(event.target)) {
            lastFocusedRef.current = event.target;
          }
        }}
        className="shadow-2xl w-full max-w-4xl h-[100dvh] max-h-[100dvh] sm:h-auto sm:max-h-[95vh] rounded-none sm:rounded-2xl flex flex-col overflow-hidden"
        style={modalSurfaceStyle}
      >
        {renderModalHeader()}

        {/* Content */}
        <div className={bodyClasses}>{renderStep()}</div>

        {/* Footer avec boutons modernisés */}
        <div className={`${footerClasses} sticky bottom-0 p-4 sm:p-6`}>
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 ${
                isDarkMode
                  ? "bg-white/10 text-slate-100 hover:bg-white/20"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              ← Précédent
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalSteps }, (_, index) => index + 1).map((step) => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    step === currentStep
                      ? "bg-orange-500 scale-125"
                      : step < currentStep
                        ? "bg-green-500"
                        : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            <div>
              {currentStep < totalSteps && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl font-medium hover:from-orange-600 hover:to-yellow-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                >
                  Suivant →
                </button>
              )}
              {currentStep === totalSteps && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Créer le produit
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <MediaModal
          isOpen={isMediaModalOpen}
          onClose={() => setIsMediaModalOpen(false)}
          onSelectImage={handleSelectFromMedia}
          isEditing={false}
          images={mediaImages}
        />

        {showQuickCategoryModal && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-4"
            role="presentation"
            onClick={() => {
              if (isCreatingQuickCategory) return;
              setShowQuickCategoryModal(false);
            }}
          >
            <div
              className={`${sectionCardClasses} w-full max-w-md rounded-2xl p-5`}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
                Catégories
              </p>
              <h3 className="mt-1 text-lg font-semibold">Ajouter une catégorie</h3>
              <p className={`mt-2 text-sm ${mutedTextClass}`}>
                Créez une catégorie rapidement sans quitter le produit.
              </p>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  value={quickCategoryName}
                  onChange={(e) => setQuickCategoryName(e.target.value)}
                  className={baseInputClasses}
                  placeholder="Ex: Grillades, Cocktails..."
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowQuickCategoryModal(false)}
                    className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                      isDarkMode
                        ? "border border-white/20 text-slate-100 hover:bg-white/10"
                        : "border border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickCreateCategory}
                    disabled={isCreatingQuickCategory}
                    className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
                  >
                    {isCreatingQuickCategory ? "Création..." : "Créer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ProductManagement;
